import { TextSelection, Selection, PluginKey, NodeSelection, Plugin } from 'prosemirror-state';
import { Slice, Fragment } from 'prosemirror-model';
import { InputRule } from 'prosemirror-inputrules';
import { v4 } from 'uuid';
import { NodeExtension } from '../../../core/index.js';
import { selectionCell, TableMap, CellSelection, isInTable as isInTable$1, addColSpan, setAttr, removeColSpan, cellWrapping, cellAround, nextCell, inSameTable, pointsAtCell, drawCellSelection, normalizeSelection } from '../../../citadel/kit/cellSelection/index.js';
import 'prosemirror-commands';
import { keydownHandler } from 'prosemirror-keymap';
import { GapCursor } from '../gap-cursor-extension/index.js';
import { DecorationSet, Decoration } from 'prosemirror-view';
import { GapCursor as GapCursor$1, gapCursorSituation } from '../../../citadel/kit/GapcursorSelection/index.js';
import React, { useState, useRef, useMemo, useEffect, Component } from 'react';
import ReactDom, { render } from 'react-dom';
import throttle from 'lodash.throttle';
import { createInstance } from '@sailor/i18n-web';
import classNames from 'classnames';
import { Transform } from 'prosemirror-transform';

/**
 * 统一样式前缀
 */ var CLASSNAME_PREFIX = "ct";

function getInsertBlockNodes(state, node) {
    var schema = state.schema, selection = state.selection, doc = state.doc;
    var paragraph = schema.nodes.paragraph;
    var $from = selection.$from, $to = selection.$to, to = selection.to;
    var afterNode = $to.nodeAfter;
    var fromNodeGrandParent = $from.node(-1);
    var fromNodeParent = $from.node();
    // 光标在文档尾部
    if (!afterNode) {
        var nodes = [
            node,
            paragraph.create()
        ];
        // 是否在文档尾部后单元格尾部
        var isInTail = to === doc.content.size - 1 || (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;
        if (isInTail) return nodes;
        // 后面一行是否为文本块节点（即有内联内容的块级节点，schema中定义content为inline或者text的节点例如段落、标题, ）
        var nextNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;
        if (nextNode && !nextNode.isTextblock && !nextNode.isText) {
            // 后面一行为非文本块节点
            return nodes;
        }
    }
    return [
        node
    ];
}
var isParagraph = function(node) {
    var _node_type;
    return (node === null || node === void 0 ? void 0 : (_node_type = node.type) === null || _node_type === void 0 ? void 0 : _node_type.name) === "paragraph";
};
// 插入块处理
function insertBlock(state, node) {
    var options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        selection: null,
        cursorMove: true
    };
    if (!node.isBlock) {
        throw new Error("必须传入 block node");
    }
    var tr = state.tr, schema = state.schema;
    var selection = options.selection || state.selection;
    var cursorMove = options.cursorMove;
    var _schema_nodes = schema.nodes, table = _schema_nodes.table, note = _schema_nodes.note;
    var from = selection.from, $from = selection.$from;
    // TODO: 为什么需要清除格式
    tr.setStoredMarks([]); // 清除格式
    var insertingNodes = getInsertBlockNodes(state, node);
    // TODO: selection.replace 并不是常用的函数，是否可以替换成 tr.replaceWith
    selection.replace(tr, new Slice(Fragment.from(insertingNodes), 0, 0));
    // transform 1.1 版本会在 replace 过程中增加多余的空行，此处进行判断并清理
    // transform 升级到 1.3 之后可以恢复此段代码的处理，不需要清理多余的空行
    // https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/20632944
    var step = tr.steps[0];
    if (step) {
        var slice = step === null || step === void 0 ? void 0 : step.slice;
        var content = (slice === null || slice === void 0 ? void 0 : slice.content).content;
        var size = content.length;
        // 末尾两个元素是否为 p 标签
        var removeParagraph = size > 2 && isParagraph(content[size - 1]) && isParagraph(content[size - 2]);
        if (removeParagraph) {
            // 如果替换时最后两个node都是 paragraph，说明有多余的空行，需要删除最后一个空行
            var lastPos = tr.mapping.map(step.to);
            tr.delete(lastPos - 2, lastPos);
        }
    }
    tr.setSelection(new TextSelection(tr.doc.resolve(tr.mapping.map(state.selection.from)), tr.doc.resolve(tr.mapping.map(state.selection.to))));
    // TODO:下面这段代码优化
    // 移动光标位置
    if (cursorMove) {
        var nodeBefore = $from.nodeBefore; // 光标前相邻节点
        var offset = nodeBefore ? 2 : 0;
        if (node.type.isLeaf) {
            offset += 1;
        } else if (node.type === note) {
            offset += 4;
        } else if (node.type === table) {
            offset += 3;
        }
        var newPos = selection.constructor.name === "GapCursor" ? from : from + offset;
        tr.setSelection(TextSelection.create(tr.doc, newPos));
    }
    return {
        transaction: tr
    };
}
var classNamePrefix = function(className) {
    var prefix = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : CLASSNAME_PREFIX;
    return "".concat(prefix, "-").concat(className);
};
// 获取浏览器和系统信息
function getSystemInfo() {
    // browser
    var nVer = navigator.appVersion;
    var nAgt = navigator.userAgent;
    var browser = navigator.appName;
    var version = "".concat(parseFloat(navigator.appVersion));
    var nameOffset;
    var verOffset;
    var ix;
    var isMobile = nAgt.toLowerCase().indexOf("mobile") !== -1;
    // Opera
    // eslint-disable-next-line no-cond-assign
    if ((verOffset = nAgt.indexOf("Opera")) !== -1) {
        browser = "Opera";
        version = nAgt.substring(verOffset + 6);
        // eslint-disable-next-line no-cond-assign
        if ((verOffset = nAgt.indexOf("Version")) !== -1) {
            version = nAgt.substring(verOffset + 8);
        }
    }
    // Opera Next
    // eslint-disable-next-line no-cond-assign
    if ((verOffset = nAgt.indexOf("OPR")) !== -1) {
        browser = "Opera";
        version = nAgt.substring(verOffset + 4);
    // Edge
    // eslint-disable-next-line no-cond-assign
    } else if ((verOffset = nAgt.indexOf("Edge")) !== -1) {
        browser = "Microsoft Edge";
        version = nAgt.substring(verOffset + 5);
    // MSIE
    // eslint-disable-next-line no-cond-assign
    } else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
        browser = "Microsoft Internet Explorer";
        version = nAgt.substring(verOffset + 5);
    // Chrome
    // eslint-disable-next-line no-cond-assign
    } else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
        browser = "Chrome";
        version = nAgt.substring(verOffset + 7);
    // Safari
    // eslint-disable-next-line no-cond-assign
    } else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
        browser = "Safari";
        version = nAgt.substring(verOffset + 7);
        // eslint-disable-next-line no-cond-assign
        if ((verOffset = nAgt.indexOf("Version")) !== -1) {
            version = nAgt.substring(verOffset + 8);
        }
    // Firefox
    // eslint-disable-next-line no-cond-assign
    } else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
        browser = "Firefox";
        version = nAgt.substring(verOffset + 8);
    // MSIE 11+
    } else if (nAgt.indexOf("Trident/") !== -1) {
        browser = "Microsoft Internet Explorer";
        version = nAgt.substring(nAgt.indexOf("rv:") + 3);
    // Other browsers
    } else if (// eslint-disable-next-line no-cond-assign
    (nameOffset = nAgt.lastIndexOf(" ") + 1) < // eslint-disable-next-line no-cond-assign
    (verOffset = nAgt.lastIndexOf("/"))) {
        browser = nAgt.substring(nameOffset, verOffset);
        version = nAgt.substring(verOffset + 1);
        if (browser.toLowerCase() === browser.toUpperCase()) {
            browser = navigator.appName;
        }
    }
    // trim the version string
    // eslint-disable-next-line no-cond-assign
    if ((ix = version.indexOf(";")) !== -1) {
        version = version.substring(0, ix);
    }
    // eslint-disable-next-line no-cond-assign
    if ((ix = version.indexOf(" ")) !== -1) {
        version = version.substring(0, ix);
    }
    // eslint-disable-next-line no-cond-assign
    if ((ix = version.indexOf(")")) !== -1) {
        version = version.substring(0, ix);
    }
    // system
    var os = "unknown";
    var clientStrings = [
        {
            s: "Windows 10",
            r: /(Windows 10.0|Windows NT 10.0)/
        },
        {
            s: "Windows 8.1",
            r: /(Windows 8.1|Windows NT 6.3)/
        },
        {
            s: "Windows 8",
            r: /(Windows 8|Windows NT 6.2)/
        },
        {
            s: "Windows 7",
            r: /(Windows 7|Windows NT 6.1)/
        },
        {
            s: "Windows Vista",
            r: /Windows NT 6.0/
        },
        {
            s: "Windows Server 2003",
            r: /Windows NT 5.2/
        },
        {
            s: "Windows XP",
            r: /(Windows NT 5.1|Windows XP)/
        },
        {
            s: "Windows 2000",
            r: /(Windows NT 5.0|Windows 2000)/
        },
        {
            s: "Windows ME",
            r: /(Win 9x 4.90|Windows ME)/
        },
        {
            s: "Windows 98",
            r: /(Windows 98|Win98)/
        },
        {
            s: "Windows 95",
            r: /(Windows 95|Win95|Windows_95)/
        },
        {
            s: "Windows NT 4.0",
            r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/
        },
        {
            s: "Windows CE",
            r: /Windows CE/
        },
        {
            s: "Windows 3.11",
            r: /Win16/
        },
        {
            s: "Android",
            r: /Android/
        },
        {
            s: "Open BSD",
            r: /OpenBSD/
        },
        {
            s: "Sun OS",
            r: /SunOS/
        },
        {
            s: "Linux",
            r: /(Linux|X11)/
        },
        {
            s: "iOS",
            r: /(iPhone|iPad|iPod)/
        },
        {
            s: "Mac OS X",
            r: /Mac OS X/
        },
        {
            s: "Mac OS",
            r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/
        },
        {
            s: "QNX",
            r: /QNX/
        },
        {
            s: "UNIX",
            r: /UNIX/
        },
        {
            s: "BeOS",
            r: /BeOS/
        },
        {
            s: "OS/2",
            r: /OS\/2/
        },
        {
            s: "Search Bot",
            r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/
        }
    ];
    for(var id in clientStrings){
        var cs = clientStrings[id];
        if (cs.r.test(nAgt)) {
            os = cs.s;
            break;
        }
    }
    var osVersion = "unknown";
    if (/Windows (.*)/.test(os)) {
        osVersion = /Windows (.*)/.exec(os)[1];
        os = "Windows";
    }
    switch(os){
        case "Mac OS X":
            if (/Mac OS X ([._\d]+)/.test(nAgt)) {
                osVersion = /Mac OS X ([._\d]+)/.exec(nAgt)[1];
            }
            break;
        case "Android":
            if (/Android ([._\d]+)/.test(nAgt)) {
                osVersion = /Android ([._\d]+)/.exec(nAgt)[1];
            }
            break;
        case "iOS":
            if (/OS (\d+)_(\d+)_?(\d+)?/.test(nVer)) {
                var splits = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer);
                // eslint-disable-next-line no-bitwise
                osVersion = "".concat(splits[1], ".").concat(splits[2], ".").concat(+splits[3] | 0);
            } else if (/iOS\/(\d+).(\d+).?(\d+)?/.test(nVer)) {
                var splits1 = /iOS\/(\d+).(\d+).?(\d+)?/.exec(nVer);
                // eslint-disable-next-line no-bitwise
                osVersion = "".concat(splits1[1], ".").concat(splits1[2], ".").concat(+splits1[3] | 0);
            }
            break;
    }
    return {
        browser: browser || "unknown",
        browserVersion: version || "unknown",
        os: os || "unknown",
        osVersion: osVersion || "unknown",
        isMobile: isMobile,
        isWindows: ~(os || "unknown").toLowerCase().indexOf("win")
    };
}

// 向上寻找指定DOM节点
var findParentDOM = function(targetDOM, assert) {
    var dom = targetDOM;
    while(true){
        if (!dom || dom.classList && dom.classList.contains("ProseMirror")) {
            return false;
        }
        if (assert(dom)) return dom;
        dom = dom.parentNode;
    }
};
var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
var findParentNode = function(predicate) {
    return function(param) {
        var $from = param.$from;
        return findParentNodeClosestToPos($from, predicate);
    };
};
/**
 * 是否为表格内的位置
 * @param $pos ResolvedPos
 * @returns boolean
 */ var isInTablePos = function($pos) {
    for(var d = $pos.depth; d > 0; d--){
        // 有 tableRole 属性的就是表格的 schema
        if ($pos.node(d).type.spec.tableRole) return true;
    }
    return false;
};
/**
 * 获取在表格中的真正可插入的节点的位置
 * @param $pos ResolvedPos
 * @returns number
 */ var getInsertPosInTable = function($pos) {
    // 不是表格内的 pos 不处理
    if (!isInTablePos($pos)) return null;
    var selectionRight = Selection.near($pos, 1);
    var selectionLeft = Selection.near($pos, -1);
    // 左右可用选区都没有
    if (!selectionRight && !selectionLeft) return null;
    var insertPos = $pos.pos;
    // 只有一边有选区, 那么就用一边的选区的位置
    if (selectionRight && !selectionLeft) {
        insertPos = selectionRight.from;
    } else if (!selectionRight && selectionLeft) {
        insertPos = selectionLeft.from;
    } else {
        // 选取左右最近的能插入的位置, 右侧优先
        insertPos = $pos.pos - selectionLeft.from < selectionRight.from - $pos.pos ? selectionLeft.from : selectionRight.from;
    }
    return insertPos;
};

// from prosemirror-tables v1.1.1
var deleteTable = function() {
    return function(state, dispatch) {
        var $pos = state.selection.$anchor;
        for(var d = $pos.depth; d > 0; d--){
            var node = $pos.node(d);
            if (node.type.spec.tableRole === "table") {
                dispatch(state.tr.delete($pos.before(d), $pos.after(d)).scrollIntoView());
                return true;
            }
        }
        return false;
    };
};
var findTable = function(selection) {
    return findParentNode(function(node) {
        return node.type.spec.tableRole && node.type.spec.tableRole === "table";
    })(selection);
};
var findTableCell = function(selection) {
    return findParentNode(function(node) {
        return node.type.spec.tableRole && [
            "table_cell",
            "table_header"
        ].includes(node.type.name);
    })(selection);
};

var DEFAULT_COL_WIDTH = 68;
var MIN_COL_WIDTH = 50;
// dpi=1.25时，表格宽度需要为 4 的整数倍
var formatWidth = function(width) {
    // width = Math.round(width);
    // width = Math.round(width / 4) * 4;
    return width;
};

function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
// Helper to get the selected rectangle in a table, if any. Adds table
// map, table node, and table start offset to the object for
// convenience.
function selectedRect(state) {
    var sel = state.selection;
    var $pos = selectionCell(state);
    var table = $pos.node(-1);
    var tableStart = $pos.start(-1);
    var map = TableMap.get(table);
    var rect;
    if (_instanceof(sel, CellSelection)) {
        rect = map.rectBetween(sel.$anchorCell.pos - tableStart, sel.$headCell.pos - tableStart);
    } else {
        rect = map.findCell($pos.pos - tableStart);
    }
    rect.tableStart = tableStart;
    rect.map = map;
    rect.table = table;
    return rect;
}
// 是否有表头行
function hasRowHeader(table) {
    var table_header = table.type.schema.nodes.table_header;
    var firstRow = table.firstChild;
    return firstRow.content.content.every(function(p) {
        return p.type === table_header;
    });
}
// 是否有表头列
function hasColHeader(table) {
    var table_header = table.type.schema.nodes.table_header;
    var rows = table.content.content;
    var rowspan = 0;
    if (!rows[0].firstChild) return false;
    if (hasRowHeader(table)) {
        if (rows[0].firstChild.attrs.rowspan === rows.length) return false;
        rowspan = rows[0].firstChild.attrs.rowspan - 1;
        rows = rows.slice(1);
    }
    if (rows.length === 0) return false;
    return rows.every(function(row) {
        if (rowspan > 0) {
            rowspan--;
            return true;
        }
        rowspan = row.firstChild.attrs.rowspan - 1;
        return row.firstChild.type === table_header;
    });
}
// 是否有行号列
function hasNumCol(table) {
    var rows = table.content.content;
    var rowspan = 0;
    if (!rows[0].firstChild) return false;
    if (hasRowHeader(table)) {
        if (rows[0].firstChild.attrs.rowspan === rows.length) return false;
        rowspan = rows[0].firstChild.attrs.rowspan - 1;
        rows = rows.slice(1);
    }
    if (rows.length === 0) return false;
    return rows.every(function(row) {
        if (rowspan > 0) {
            rowspan--;
            return true;
        }
        rowspan = row.firstChild.attrs.rowspan - 1;
        return row.firstChild.attrs.numCell;
    });
}
// 是否是 parent 的下一层子节点，排除掉嵌套表格情况
var isClosestChildren = function(parent, targetNode) {
    var isChildren = false;
    parent.forEach(function(node) {
        if (node === targetNode) {
            isChildren = true;
        }
    });
    return isChildren;
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
var addColumn = function(tr, param, col) {
    var map = param.map, tableStart = param.tableStart, table = param.table, extraAttrs = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
    var _table_type_schema_nodes = table.type.schema.nodes, table_header = _table_type_schema_nodes.table_header, table_cell = _table_type_schema_nodes.table_cell, paragraph = _table_type_schema_nodes.paragraph;
    var tableHasRowHeader = hasRowHeader(table);
    var colWidth = table.attrs.responsive ? MIN_COL_WIDTH : DEFAULT_COL_WIDTH;
    for(var row = 0; row < map.height; row++){
        var index = row * map.width + col;
        // 当前单元格所占列数 > 1
        if (col > 0 && col < map.width && map.map[index - 1] === map.map[index]) {
            var pos = map.map[index];
            var cell = table.nodeAt(pos);
            tr.setNodeMarkup(tr.mapping.map(tableStart + pos), null, addColSpan(cell.attrs, col - map.colCount(pos), [
                colWidth
            ]) // 增加该单元格的 colspan 和 colwidth
            );
            // 如果该单元格占 x 行，则接下来的 x-1 行不做处理
            row += cell.attrs.rowspan - 1;
        } else {
            var type = row === 0 && tableHasRowHeader ? table_header : table_cell;
            var pos1 = map.positionAt(row, col, table);
            tr.insert(tr.mapping.map(tableStart + pos1), type.createAndFill(_objectSpread({
                colwidth: [
                    colWidth
                ]
            }, extraAttrs), paragraph.create()));
        }
    }
    return tr;
};
var canAddColumnBefore = function(state) {
    if (!isInTable$1(state)) return false;
    var rect = selectedRect(state);
    if (rect.left === 0 && (hasColHeader(rect.table) || hasNumCol(rect.table))) {
        return false;
    }
    return true;
};
var addColumnBefore = function() {
    return function(state, dispatch) {
        if (!canAddColumnBefore(state)) return false;
        var rect = selectedRect(state);
        var extraAttrs = {};
        if (!rect.table.attrs.responsive) {
            var colwidth = rect.map.colWidths[rect.left];
            extraAttrs = {
                colwidth: [
                    colwidth
                ]
            };
        }
        var tr = addColumn(state.tr, rect, rect.left, extraAttrs);
        var curCellNode = findTableCell(tr.selection);
        var newPos = curCellNode.pos - 2; // -2是因为</p></cell>
        if (newPos >= 0) tr.setSelection(TextSelection.create(tr.doc, newPos));
        dispatch(tr);
        return true;
    };
};
var addColumnAfter = function() {
    return function(state, dispatch) {
        if (!isInTable$1(state)) return false;
        var rect = selectedRect(state);
        var extraAttrs = {};
        if (!rect.table.attrs.responsive) {
            var colwidth = rect.map.colWidths[rect.right - 1];
            extraAttrs = {
                colwidth: [
                    colwidth
                ]
            };
        }
        var tr = addColumn(state.tr, rect, rect.right, extraAttrs);
        var curCellNode = findTableCell(tr.selection);
        var newPos = curCellNode.pos + curCellNode.node.nodeSize + 2; // +2 是因为<cell><p>
        if (newPos >= 0) tr.setSelection(TextSelection.create(tr.doc, newPos));
        dispatch(tr);
        return true;
    };
};

var addRow = function(tr, param, row) {
    var map = param.map, tableStart = param.tableStart, table = param.table;
    var _table_type_schema_nodes = table.type.schema.nodes, table_row = _table_type_schema_nodes.table_row, table_header = _table_type_schema_nodes.table_header, table_cell = _table_type_schema_nodes.table_cell, paragraph = _table_type_schema_nodes.paragraph;
    var tableHasColHeader = hasColHeader(table);
    var tableHasNumCol = hasNumCol(table);
    var rowPos = tableStart;
    for(var i = 0; i < row; i++)rowPos += table.child(i).nodeSize;
    var cells = [];
    for(var col = 0, index = map.width * row; col < map.width; col++, index++){
        // 当前单元格所占行数 > 1
        if (row > 0 && row < map.height && map.map[index] === map.map[index - map.width]) {
            var pos = map.map[index];
            var attrs = table.nodeAt(pos).attrs;
            tr.setNodeMarkup(tableStart + pos, null, setAttr(attrs, "rowspan", attrs.rowspan + 1));
            // 如果该单元格占 x 列，则接下来的 x-1 列不做处理
            col += attrs.colspan - 1;
            index += attrs.colspan - 1;
        } else {
            var colwidth = [
                map.colWidths[col]
            ];
            var type = col === 0 && tableHasColHeader ? table_header : table_cell;
            var numCell = col === 0 && tableHasNumCol;
            cells.push(type.createAndFill({
                numCell: numCell,
                colwidth: colwidth
            }, paragraph.create()));
        }
    }
    tr.insert(rowPos, table_row.create(null, cells));
    return tr;
};
var canAddRowBefore = function(state) {
    if (!isInTable$1(state)) return false;
    var rect = selectedRect(state);
    if (rect.top === 0 && hasRowHeader(rect.table)) {
        return false;
    }
    return true;
};
var addRowBefore = function() {
    return function(state, dispatch) {
        if (!canAddRowBefore(state)) return false;
        var rect = selectedRect(state);
        var tr = addRow(state.tr, rect, rect.top);
        var _selectedRect = selectedRect(tr), map = _selectedRect.map, tableStart = _selectedRect.tableStart;
        var newPos = map.map[rect.top * map.width + rect.left] + tableStart + 2; // +2 是因为<cell><p>
        if (hasNumCol(rect.table) && rect.left === 0 && map.width > 1) {
            newPos = map.map[rect.top * map.width + rect.left + 1] + tableStart + 2; // +2 是因为<cell><p>
        }
        if (newPos >= 0) tr.setSelection(TextSelection.create(tr.doc, newPos));
        dispatch(tr);
        return true;
    };
};
var addRowAfter = function() {
    return function(state, dispatch) {
        if (!isInTable$1(state)) return false;
        var rect = selectedRect(state);
        var tr = addRow(state.tr, rect, rect.bottom);
        var _selectedRect = selectedRect(tr), map = _selectedRect.map, tableStart = _selectedRect.tableStart;
        var newPos = map.map[rect.bottom * map.width + rect.left] + tableStart + 2; // +2 是因为<cell><p>
        if (hasNumCol(rect.table) && rect.left === 0 && map.width > 1) {
            newPos = map.map[rect.bottom * map.width + rect.left + 1] + tableStart + 2; // +2 是因为<cell><p>
        }
        if (newPos >= 0) tr.setSelection(TextSelection.create(tr.doc, newPos));
        dispatch(tr);
        return true;
    };
};

var removeRow = function(tr, param, row) {
    var map = param.map, table = param.table, tableStart = param.tableStart;
    var rowPos = 0;
    for(var i = 0; i < row; i++)rowPos += table.child(i).nodeSize;
    var nextRow = rowPos + table.child(row).nodeSize;
    var mapFrom = tr.mapping.maps.length;
    tr.delete(rowPos + tableStart, nextRow + tableStart);
    for(var col = 0, index = row * map.width; col < map.width; col++, index++){
        var pos = map.map[index];
        if (row > 0 && pos === map.map[index - map.width]) {
            // If this cell starts in the row above, simply reduce its rowspan
            var attrs = table.nodeAt(pos).attrs;
            tr.setNodeMarkup(tr.mapping.slice(mapFrom).map(pos + tableStart), null, setAttr(attrs, "rowspan", attrs.rowspan - 1));
            col += attrs.colspan - 1;
            index += attrs.colspan - 1;
        } else if (row < map.height && pos === map.map[index + map.width]) {
            // Else, if it continues in the row below, it has to be moved down
            var cell = table.nodeAt(pos);
            var copy = cell.type.create(setAttr(cell.attrs, "rowspan", cell.attrs.rowspan - 1), cell.content);
            var newPos = map.positionAt(row + 1, col, table);
            tr.insert(tr.mapping.slice(mapFrom).map(tableStart + newPos), copy);
            col += cell.attrs.colspan - 1;
            index += cell.attrs.colspan - 1;
        }
    }
};
var deleteRow = function() {
    return function(state, dispatch, view) {
        if (!isInTable$1(state)) return false;
        var tr = state.tr;
        var rect = selectedRect(state);
        // 当前选区包含表格内的所有行时，删除整个表格
        if (rect.top === 0 && rect.bottom === rect.map.height) {
            return deleteTable()(state, dispatch, view);
        }
        var newPos = 0;
        var _rect_map = rect.map, tableMap = _rect_map.map;
        // 如果删除最后一行，光标位于上一行的该列
        if (rect.bottom === rect.map.height) {
            newPos = tableMap[rect.left + (rect.top - 1) * rect.map.width] + rect.tableStart;
        } else {
            // 否则，光标位于下一行的该列
            newPos = tableMap[rect.left + rect.bottom * rect.map.width] + rect.tableStart;
        }
        // 从下往上依次删除行
        for(var i = rect.bottom - 1;; i--){
            removeRow(tr, rect, i);
            if (i === rect.top) break;
            rect.table = rect.tableStart ? tr.doc.nodeAt(rect.tableStart - 1) : tr.doc;
            rect.map = TableMap.get(rect.table);
        }
        // fix: ONES #65006958
        // 分析文档： https://km.sankuai.com/collabpage/1294314574
        tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(newPos + 2))); // +2 是因为<cell><p>
        dispatch(tr.scrollIntoView());
        return true;
    };
};

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _instanceof$1(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var removeColumn = function(tr, param, col) {
    var map = param.map, table = param.table, tableStart = param.tableStart;
    var mapStart = tr.mapping.maps.length;
    for(var row = 0; row < map.height;){
        var index = row * map.width + col;
        var pos = map.map[index];
        var cell = table.nodeAt(pos);
        // If this is part of a col-spanning cell
        if (col > 0 && map.map[index - 1] === pos || col < map.width - 1 && map.map[index + 1] === pos) {
            tr.setNodeMarkup(tr.mapping.slice(mapStart).map(tableStart + pos), null, removeColSpan(cell.attrs, col - map.colCount(pos)));
        } else {
            var start = tr.mapping.slice(mapStart).map(tableStart + pos);
            tr.delete(start, start + cell.nodeSize);
        }
        row += cell.attrs.rowspan;
    }
};
var deleteColumn = function() {
    return function(state, dispatch, view) {
        if (!isInTable$1(state)) return false;
        var rect = selectedRect(state);
        var right = rect.right, width = rect.map.width;
        var tr = state.tr;
        // 当前选区包含表格内的所有列时，删除整个表格
        if (rect.left === 0 && rect.right === rect.map.width) {
            return deleteTable()(state, dispatch, view);
        }
        for(var i = rect.right - 1;; i--){
            removeColumn(tr, rect, i);
            rect.table = rect.tableStart ? tr.doc.nodeAt(rect.tableStart - 1) : tr.doc;
            rect.map = TableMap.get(rect.table);
            if (i === rect.left) break;
        }
        // 特定形状的表格，做【选择行、删除列】的操作时可能出现删出空row(仅row，无cell无p)的情况，导致后续表格计算selectedRect时出错 #65008149
        // 真实的用户意图显然是同时删除空的row，这里补上这部分逻辑
        var rows = _toConsumableArray(rect.table.content.content);
        for(var row = rows.length - 1; row >= 0; row--){
            if (rows[row].type.name === "table_row" && rows[row].nodeSize === 2) {
                rect.table = rect.tableStart ? tr.doc.nodeAt(rect.tableStart - 1) : tr.doc;
                rect.map = TableMap.get(rect.table);
                removeRow(tr, rect, row);
            }
        }
        // 如果删除的最后一列，则需要把光标从下一行的第一个单元格移动至前一行的最后一个单元格
        if (right === width) {
            /**
     * -6 是因为 </p></cell></row><row><cell><p>
     * 理论如果光标处于最后一行时，删除列后光标会在表格外，此时应该 -5（</p></cell></row></table><p>）
     * 但实际上不影响，会自动矫正 */ tr.setSelection(TextSelection.create(tr.doc, tr.selection.from - 6));
        } else if (_instanceof$1(tr.selection, CellSelection)) {
            tr.setSelection(TextSelection.create(tr.doc, tr.selection.$anchorCell.pos + 2) // +2 是因为<cell><p>
            );
        }
        dispatch(tr.scrollIntoView());
        return true;
    };
};

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof$2(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function isEmptyCell(cell) {
    var c = cell.content;
    return c.childCount === 1 && c.firstChild.isTextblock && c.firstChild.childCount === 0;
}
var canMergeCells = function(state) {
    var sel = state.selection;
    if (!_instanceof$2(sel, CellSelection) || sel.$anchorCell.pos === sel.$headCell.pos) {
        return false;
    }
    var hasNumCell = false;
    var allIsNumCell = true;
    sel.ranges.forEach(function(range) {
        var cellNode = findTableCell(range);
        if (cellNode) {
            if (cellNode.node.attrs.numCell) hasNumCell = true;
            else allIsNumCell = false;
        }
    });
    if (hasNumCell && !allIsNumCell) return false;
    return true;
};
var mergeCells = function() {
    return function(state, dispatch, view) {
        if (!canMergeCells(state)) return false;
        var rect = selectedRect(state);
        var map = rect.map;
        var tr = state.tr;
        var seen = [];
        var colwidth = [];
        var content = Fragment.empty;
        var mergedPos;
        var mergedCell;
        for(var row = rect.top; row < rect.bottom; row++){
            for(var col = rect.left; col < rect.right; col++){
                var cellPos = map.map[row * map.width + col];
                var cell = rect.table.nodeAt(cellPos);
                // 该单元格是多列合并后的单元格，且当前列不是合并后的第一列（之前已经加过一次 colwidth）
                var isMulColspan = cell.attrs.colspan > 1 && col > rect.left && cellPos === map.map[row * map.width + col - 1];
                if (row === rect.top && !isMulColspan) {
                    colwidth = colwidth.concat(cell.attrs.colwidth);
                }
                if (seen.indexOf(cellPos) > -1) continue;
                seen.push(cellPos);
                if (mergedPos == null) {
                    mergedPos = cellPos;
                    mergedCell = cell;
                } else {
                    if (!isEmptyCell(cell)) content = content.append(cell.content);
                    var mapped = tr.mapping.map(cellPos + rect.tableStart);
                    tr.delete(mapped, mapped + cell.nodeSize);
                }
            }
        }
        // 继承第一个单元格的attrs
        tr.setNodeMarkup(mergedPos + rect.tableStart, null, _objectSpreadProps(_objectSpread$1({}, mergedCell.attrs), {
            colspan: rect.right - rect.left,
            rowspan: rect.bottom - rect.top,
            colwidth: colwidth
        }));
        if (content.size) {
            var end = mergedPos + 1 + mergedCell.content.size;
            var start = isEmptyCell(mergedCell) ? mergedPos + 1 : end;
            tr.replaceWith(start + rect.tableStart, end + rect.tableStart, content);
        }
        tr.setSelection(new CellSelection(tr.doc.resolve(mergedPos + rect.tableStart)));
        dispatch(tr);
        return true;
    };
};

function _instanceof$3(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var canSplitCell = function(state) {
    var sel = state.selection;
    var cellNode;
    if (!_instanceof$3(sel, CellSelection)) {
        cellNode = cellWrapping(sel.$from);
        if (!cellNode) return false;
    } else {
        if (sel.$anchorCell.pos !== sel.$headCell.pos) return false;
        cellNode = sel.$anchorCell.nodeAfter;
    }
    if (cellNode.attrs.colspan === 1 && cellNode.attrs.rowspan === 1) {
        return false;
    }
    return true;
};
var splitCell = function() {
    return function(state, dispatch, view) {
        if (!canSplitCell(state)) return false;
        var sel = state.selection;
        var cellNode;
        var cellPos;
        if (!_instanceof$3(sel, CellSelection)) {
            cellNode = cellWrapping(sel.$from);
            cellPos = cellAround(sel.$from).pos;
        } else {
            cellNode = sel.$anchorCell.nodeAfter;
            cellPos = sel.$anchorCell.pos;
        }
        var baseAttrs = cellNode.attrs;
        var attrs = [];
        var colwidth = baseAttrs.colwidth;
        if (baseAttrs.rowspan > 1) baseAttrs = setAttr(baseAttrs, "rowspan", 1);
        if (baseAttrs.colspan > 1) baseAttrs = setAttr(baseAttrs, "colspan", 1);
        var rect = selectedRect(state);
        var schema = state.schema, tr = state.tr;
        var _schema_nodes = schema.nodes, table_cell = _schema_nodes.table_cell, table_header = _schema_nodes.table_header;
        for(var i = 0; i < rect.right - rect.left; i++){
            attrs.push(colwidth ? setAttr(baseAttrs, "colwidth", colwidth && colwidth[i] ? [
                colwidth[i]
            ] : null) : baseAttrs);
        }
        var lastCell;
        var tableHasRowHeader = hasRowHeader(rect.table);
        var tableHasColHeader = hasColHeader(rect.table);
        for(var row = rect.top; row < rect.bottom; row++){
            var pos = rect.map.positionAt(row, rect.left, rect.table);
            if (row === rect.top) pos += cellNode.nodeSize;
            for(var col = rect.left, i1 = 0; col < rect.right; col++, i1++){
                if (col === rect.left && row === rect.top) continue;
                var cellType = row === 0 && tableHasRowHeader || col === 0 && tableHasColHeader ? table_header : table_cell;
                tr.insert(lastCell = tr.mapping.map(pos + rect.tableStart, 1), cellType.createAndFill(attrs[i1]));
            }
        }
        tr.setNodeMarkup(cellPos, cellNode.type, attrs[0]);
        if (_instanceof$3(sel, CellSelection)) {
            tr.setSelection(new CellSelection(tr.doc.resolve(sel.$anchorCell.pos), lastCell && tr.doc.resolve(lastCell)));
        }
        dispatch(tr);
        return true;
    };
};

// from prosemirror-tables v1.1.1
var deleteTable$1 = function() {
    return function(state, dispatch) {
        var $pos = state.selection.$anchor;
        for(var d = $pos.depth; d > 0; d--){
            var node = $pos.node(d);
            if (node.type.spec.tableRole === "table") {
                dispatch(state.tr.delete($pos.before(d), $pos.after(d)).scrollIntoView());
                return true;
            }
        }
        return false;
    };
};
var insertTable = function(param) {
    var rowCnt = param.rowCnt, colCnt = param.colCnt, headerData = param.headerData, _param_defaultColWidth = param.defaultColWidth, defaultColWidth = _param_defaultColWidth === void 0 ? 68 : _param_defaultColWidth;
    return function(state, dispatch, view) {
        var schema = state.schema;
        var _schema_nodes = schema.nodes, table = _schema_nodes.table, table_row = _schema_nodes.table_row, table_cell = _schema_nodes.table_cell, table_header = _schema_nodes.table_header;
        var headers = [];
        for(var i = 0; i < colCnt; i++){
            var headerNode = headerData ? table_header.create({
                colwidth: [
                    defaultColWidth
                ]
            }, headerData[i]) : table_header.createAndFill({
                colwidth: [
                    defaultColWidth
                ]
            });
            headers.push(headerNode);
        }
        var rowHeader = table_row.create(null, Fragment.from(headers));
        var rows = [
            rowHeader
        ];
        for(var i1 = 0; i1 < rowCnt - 1; i1++){
            var cells = [];
            for(var j = 0; j < colCnt; j++){
                cells.push(table_cell.createAndFill({
                    colwidth: [
                        defaultColWidth
                    ]
                }));
            }
            var rowNode = table_row.create(null, Fragment.from(cells));
            rows.push(rowNode);
        }
        var tableNode = table.create(null, Fragment.from(rows));
        var transaction = insertBlock(state, tableNode).transaction;
        dispatch(transaction.scrollIntoView());
        view.focus();
        return true;
    };
};

function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// 设置/取消表头行
var toggleRowHeader = function() {
    return function(state, dispatch, view) {
        if (!isInTable$1(state)) return false;
        var tr = state.tr, _state_schema = state.schema, _state_schema_nodes = _state_schema.nodes, table_cell = _state_schema_nodes.table_cell, table_header = _state_schema_nodes.table_header;
        var _selectedRect = selectedRect(state), tableStart = _selectedRect.tableStart, table = _selectedRect.table;
        var transaction = tr;
        if (hasRowHeader(table)) {
            tr.doc.nodesBetween(tableStart, table.nodeSize + tableStart - 1, function(node, pos, parent) {
                if (node.type === table_header && parent === table.firstChild) {
                    var attrs = node.attrs;
                    if (node === parent.firstChild) {
                        var newAttrs = _objectSpreadProps$1(_objectSpread$2({}, attrs), {
                            numCell: hasNumCol(table)
                        });
                        transaction = hasColHeader(table) ? tr.setNodeMarkup(pos, table_header, newAttrs) : tr.setNodeMarkup(pos, table_cell, newAttrs);
                    } else {
                        transaction = tr.setNodeMarkup(pos, table_cell, attrs);
                    }
                }
            });
        } else {
            tr.doc.nodesBetween(tableStart, table.nodeSize + tableStart - 1, function(node, pos, parent) {
                if (parent === table.firstChild) {
                    var attrs = node.attrs;
                    transaction = tr.setNodeMarkup(pos, table_header, _objectSpreadProps$1(_objectSpread$2({}, attrs), {
                        numCell: false
                    }));
                }
            });
        }
        if (!transaction) return false;
        dispatch(transaction);
        view.focus();
        return true;
    };
};
var unsetColHeader = function(tr, state) {
    var _state_schema = state.schema, _state_schema_nodes = _state_schema.nodes, table_cell = _state_schema_nodes.table_cell, table_header = _state_schema_nodes.table_header;
    var _selectedRect = selectedRect(state), tableStart = _selectedRect.tableStart, table = _selectedRect.table;
    var transaction = tr;
    tr.doc.nodesBetween(tableStart, table.nodeSize + tableStart - 1, function(node, pos, parent) {
        if (node.type === table_header && node === parent.firstChild && isClosestChildren(table, parent)) {
            var attrs = node.attrs;
            if (parent === table.firstChild && hasRowHeader(table)) return;
            transaction = tr.setNodeMarkup(pos, table_cell, attrs);
        }
    });
    return transaction;
};
var setColHeader = function(tr, state) {
    var _state_schema = state.schema, _state_schema_nodes = _state_schema.nodes, table_cell = _state_schema_nodes.table_cell, table_header = _state_schema_nodes.table_header;
    var _selectedRect = selectedRect(state), tableStart = _selectedRect.tableStart, table = _selectedRect.table;
    var transaction = tr;
    var rowspan = 0;
    tr.doc.nodesBetween(tableStart, table.nodeSize + tableStart - 1, function(node, pos, parent) {
        if ((node.type === table_cell || node.type === table_header) && node === parent.firstChild && isClosestChildren(table, parent)) {
            if (rowspan === 0) {
                rowspan = node.attrs.rowspan - 1;
                var attrs = node.attrs;
                transaction = tr.setNodeMarkup(pos, table_header, attrs);
            } else {
                rowspan--;
            }
        }
    });
    return transaction;
};
var canToggleColHeader = function(state) {
    if (!isInTable$1(state)) return false;
    var _selectedRect = selectedRect(state), table = _selectedRect.table, map = _selectedRect.map;
    if (hasRowHeader(table) && map.height === 1) return false;
    return true;
};
// 设置/取消表头列
var toggleColHeader = function() {
    return function(state, dispatch, view) {
        if (!canToggleColHeader(state)) return false;
        var tr = state.tr;
        var table = selectedRect(state).table;
        var transaction = tr;
        if (hasColHeader(table)) {
            transaction = unsetColHeader(transaction, state);
        } else {
            transaction = setColHeader(transaction, state);
        }
        if (!transaction) return false;
        dispatch(transaction);
        view.focus();
        return true;
    };
};
var canToggleNumCol = function(state) {
    if (!isInTable$1(state)) return false;
    var _selectedRect = selectedRect(state), table = _selectedRect.table, map = _selectedRect.map;
    if (hasRowHeader(table) && map.height === 1) return false;
    return true;
};
// 设置/取消表头列
var toggleNumCol = function() {
    return function(state, dispatch, view) {
        if (!canToggleNumCol(state)) return false;
        var tr = state.tr, _state_schema = state.schema, table_header = _state_schema.nodes.table_header;
        var _selectedRect = selectedRect(state), tableStart = _selectedRect.tableStart, table = _selectedRect.table, map = _selectedRect.map;
        var transaction = tr;
        var currentHasNumCol = hasNumCol(table);
        var handleColHeader = false;
        if (currentHasNumCol) {
            removeColumn(transaction, {
                map: map,
                table: table,
                tableStart: tableStart
            }, 0);
            // 移除行号列后，如果表格为空，则删除整个表格
            var resTable = transaction.doc.nodeAt(transaction.mapping.map(tableStart - 1));
            var rows = resTable.content.content;
            var isEmptyTable = true;
            rows.forEach(function(row) {
                if (row.childCount > 0) isEmptyTable = false;
            });
            if (isEmptyTable) {
                transaction.delete(tableStart - 1, tableStart - 1 + resTable.nodeSize);
            }
        } else {
            handleColHeader = hasColHeader(table);
            // 如果此时存在表头列，先取消，增加行号列后再重新设置表头列
            if (handleColHeader) {
                transaction = unsetColHeader(transaction, state);
            }
            transaction = addColumn(transaction, {
                map: map,
                table: table,
                tableStart: tableStart
            }, 0, {
                colwidth: [
                    MIN_COL_WIDTH
                ],
                numCell: true
            });
            // 表头行单元格没有行号
            if (hasRowHeader(table)) {
                tr.doc.nodesBetween(tableStart, table.nodeSize + tableStart - 1, function(node, pos) {
                    if (node.type === table_header && node.attrs.numCell) {
                        var attrs = node.attrs;
                        transaction = tr.setNodeMarkup(pos, null, _objectSpreadProps$1(_objectSpread$2({}, attrs), {
                            numCell: false
                        }));
                    }
                });
            }
        }
        if (!transaction) return false;
        dispatch(transaction);
        // 仅设置行号列时（现有逻辑），增加完行号，加回来固定列
        // setColHeader 依赖最新 state，需要 dispatch 之后
        if (!currentHasNumCol && handleColHeader) {
            var tr2 = setColHeader(view.state.tr, view.state);
            dispatch(tr2);
        }
        view.focus();
        return true;
    };
};

function _defineProperty$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof$4(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _objectSpread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$3(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var setCellBgColor = function(color) {
    return function(state, dispatch, view) {
        var selection = state.selection, tr = state.tr;
        var transaction = tr;
        if (_instanceof$4(selection, CellSelection)) {
            selection.ranges.forEach(function(sel) {
                var $from = sel.$from;
                transaction = transaction.setNodeMarkup($from.pos - 1, null, _objectSpreadProps$2(_objectSpread$3({}, $from.parent.attrs), {
                    bgColor: color
                }));
            });
        } else {
            var foundNode = findTableCell(selection);
            if (!foundNode) return false;
            transaction = transaction.setNodeMarkup(foundNode.pos, null, _objectSpreadProps$2(_objectSpread$3({}, foundNode.node.attrs), {
                bgColor: color
            }));
        }
        dispatch(transaction);
        view.focus();
        return true;
    };
};
// 设置表格宽度为自适应
var setResponsive = function() {
    return function(state, dispatch, view) {
        var selection = state.selection, tr = state.tr;
        var foundNode = findTable(selection);
        if (!foundNode) return false;
        var pos = foundNode.pos, tableNode = foundNode.node;
        if (tableNode.attrs.responsive) return false;
        tr.setNodeMarkup(pos, null, _objectSpreadProps$2(_objectSpread$3({}, tableNode.attrs), {
            responsive: true
        }));
        dispatch(tr);
        view.focus();
        return true;
    };
};
var isResponsive = function(state) {
    var selection = state.selection;
    var foundNode = findTable(selection);
    if (!foundNode) return false;
    var tableNode = foundNode.node;
    return tableNode.attrs.responsive;
};
// 设为固定宽度
var setFixed = function() {
    return function(state, dispatch, view) {
        if (!isResponsive(state)) return false;
        var selection = state.selection, tr = state.tr, schema = state.schema, doc = state.doc;
        var _findTable = findTable(selection), tablePos = _findTable.pos, tableNode = _findTable.node;
        var _schema_nodes = schema.nodes, table_header = _schema_nodes.table_header, table_cell = _schema_nodes.table_cell;
        doc.nodesBetween(tablePos, tablePos + tableNode.nodeSize, function(node, pos) {
            if ([
                table_header,
                table_cell
            ].includes(node.type)) {
                var attrs = node.attrs;
                var cellDom = view.domAtPos(pos + 1).node;
                var colspan = parseInt(cellDom.getAttribute("colspan"), 10) || 1;
                var cellWidth = cellDom.getBoundingClientRect().width / colspan;
                var newColwidth = [];
                for(var i = 0; i < colspan; i++)newColwidth.push(cellWidth);
                var newAttrs = _objectSpreadProps$2(_objectSpread$3({}, attrs), {
                    colwidth: newColwidth
                });
                tr.setNodeMarkup(pos, null, newAttrs);
            }
        });
        tr.setNodeMarkup(tablePos, null, _objectSpreadProps$2(_objectSpread$3({}, tableNode.attrs), {
            responsive: false
        }));
        dispatch(tr);
        view.focus();
        return true;
    };
};
// 设置单元格垂直对齐方式
var setCellVerticalAlign = function(align) {
    return function(state, dispatch, view) {
        var alignTypes = [
            "top",
            "middle",
            "bottom"
        ];
        if (!alignTypes.includes(align)) return false;
        var selection = state.selection, tr = state.tr;
        var transaction = tr;
        if (_instanceof$4(selection, CellSelection)) {
            selection.ranges.forEach(function(sel) {
                var $from = sel.$from;
                transaction = transaction.setNodeMarkup($from.pos - 1, null, _objectSpreadProps$2(_objectSpread$3({}, $from.parent.attrs), {
                    verticalAlign: align
                }));
            });
        } else {
            var foundNode = findTableCell(selection);
            if (!foundNode) return false;
            transaction = transaction.setNodeMarkup(foundNode.pos, null, _objectSpreadProps$2(_objectSpread$3({}, foundNode.node.attrs), {
                verticalAlign: align
            }));
        }
        dispatch(transaction);
        view.focus();
        return true;
    };
};
var setTableAttrs = function(attrs) {
    return function(state, dispatch, view) {
        var selection = state.selection, tr = state.tr;
        var foundNode = findTable(selection);
        if (!foundNode) return false;
        tr.setNodeMarkup(foundNode.pos, null, _objectSpread$3({}, foundNode.node.attrs, attrs));
        dispatch(tr);
        view.focus();
        return true;
    };
};
var getTableAttrs = function() {
    return function(state) {
        var selection = state.selection;
        var foundNode = findTable(selection);
        if (!foundNode) return false;
        return foundNode.node.attrs;
    };
};

// 表格外层工具条
var tableLayoutKey = new PluginKey("tableLayout");
var tableEditingKey = new PluginKey("tableEditing");
var columnResizingKey = new PluginKey("tableColumnResizing");
var stickyHeaderKey = new PluginKey("stickyHeader");
var fixTablesKey = new PluginKey("fix-tables");
var scrollBarKey = new PluginKey("tableScrollBar");
// 右键菜单
var tableContextMenuKey = new PluginKey("tableContextMenu");

/* eslint-disable no-cond-assign */ /* eslint-disable @typescript-eslint/no-use-before-define */ /* eslint-disable @typescript-eslint/no-shadow */ // copy from https://github.com/ProseMirror/prosemirror-tables/blob/master/src/input.js
function _instanceof$5(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function domInCell(view, dom) {
    for(; dom && dom !== view.dom; dom = dom.parentNode){
        if (dom.nodeName === "TD" || dom.nodeName === "TH") return dom;
    }
}
function cellUnderMouse(view, event) {
    var mousePos = view.posAtCoords({
        left: event.clientX,
        top: event.clientY
    });
    if (!mousePos) return null;
    return mousePos ? cellAround(view.state.doc.resolve(mousePos.pos)) : null;
}
/** 鼠标右键 */ var RIGHT_CLICK_BTN = 2;
function handleMouseDown(view, startEvent) {
    var setCellSelection = // Create and dispatch a cell selection between the given anchor and
    // the position under the mouse.
    function setCellSelection($anchor, event) {
        var $head = cellUnderMouse(view, event);
        var starting = tableEditingKey.getState(view.state) == null;
        if (!$head || !inSameTable($anchor, $head)) {
            if (starting) $head = $anchor;
            else return;
        }
        var selection = new CellSelection($anchor, $head);
        if (starting || !view.state.selection.eq(selection)) {
            var tr = view.state.tr.setSelection(selection);
            if (starting) tr.setMeta(tableEditingKey, $anchor.pos);
            view.dispatch(tr);
        }
    };
    var move = function move(event) {
        var anchor = tableEditingKey.getState(view.state);
        var $anchor;
        if (anchor != null) {
            // Continuing an existing cross-cell selection
            $anchor = view.state.doc.resolve(anchor);
        } else if (domInCell(view, event.target) && domInCell(view, event.target) !== startDOMCell) {
            // Moving out of the initial cell -- start a new cell selection
            $anchor = cellUnderMouse(view, startEvent);
            if (!$anchor) return stop();
        }
        if ($anchor) setCellSelection($anchor, event);
    };
    if (startEvent.ctrlKey || startEvent.metaKey) return;
    var startDOMCell = domInCell(view, startEvent.target);
    var $anchor;
    if (startEvent.shiftKey && _instanceof$5(view.state.selection, CellSelection)) {
        // Adding to an existing cell selection
        setCellSelection(view.state.selection.$anchorCell, startEvent);
        startEvent.preventDefault();
    } else if (startEvent.shiftKey && startDOMCell && ($anchor = cellAround(view.state.selection.$anchor)) != null && cellUnderMouse(view, startEvent).pos !== $anchor.pos) {
        // Adding to a selection that starts in another cell (causing a
        // cell selection to be created).
        setCellSelection($anchor, startEvent);
        startEvent.preventDefault();
    } else if (!startDOMCell) {
        // Not in a cell, let the default behavior happen.
        return;
    }
    // 忽略右键
    if (startEvent.buttons === RIGHT_CLICK_BTN) {
        return;
    }
    // Stop listening to mouse motion events.
    function stop() {
        view.root.removeEventListener("mouseup", stop);
        view.root.removeEventListener("dragstart", stop);
        view.root.removeEventListener("mousemove", move);
        if (tableEditingKey.getState(view.state) != null) {
            view.dispatch(view.state.tr.setMeta(tableEditingKey, -1));
        }
    }
    view.root.addEventListener("mouseup", stop);
    view.root.addEventListener("dragstart", stop);
    view.root.addEventListener("mousemove", move);
}
function handleTripleClick(view, pos) {
    var doc = view.state.doc;
    var $cell = cellAround(doc.resolve(pos));
    if (!$cell) return false;
    view.dispatch(view.state.tr.setSelection(new CellSelection($cell)));
    return true;
}
var handleKeyDown = keydownHandler({
    ArrowLeft: arrow("horiz", -1),
    ArrowRight: arrow("horiz", 1),
    ArrowUp: arrow("vert", -1),
    ArrowDown: arrow("vert", 1),
    "Shift-ArrowLeft": shiftArrow("horiz", -1),
    "Shift-ArrowRight": shiftArrow("horiz", 1),
    "Shift-ArrowUp": shiftArrow("vert", -1),
    "Shift-ArrowDown": shiftArrow("vert", 1)
});
var handleTab = function(dir) {
    return function(state, dispatch, view) {
        var sel = state.selection;
        if (_instanceof$5(sel, CellSelection)) {
            return maybeSetSelection(state, dispatch, Selection.near(sel.$headCell, dir));
        }
        var nextState = state.apply(state.tr.setSelection(TextSelection.create(state.doc, dir > 0 ? sel.$anchor.end() : sel.$anchor.start())));
        var selection = nextState.selection;
        var end = atEndOfCell(view, "horiz", dir, nextState);
        if (end == null) return false;
        var tr = nextState.tr;
        var $anchor = Selection.near(state.doc.resolve(selection.head + dir), dir).$anchor;
        dispatch(tr.setSelection(TextSelection.between($anchor, tr.doc.resolve($anchor.pos + $anchor.node().nodeSize - 2))));
        return true;
    };
};
function maybeSetSelection(state, dispatch, selection) {
    if (selection.eq(state.selection)) return false;
    if (dispatch) dispatch(state.tr.setSelection(selection).scrollIntoView());
    return true;
}
function arrow(axis, dir) {
    return function(state, dispatch, view) {
        var sel = state.selection;
        if (_instanceof$5(sel, CellSelection)) {
            return maybeSetSelection(state, dispatch, Selection.near(sel.$headCell, dir));
        }
        if (axis !== "horiz" && !sel.empty) return false;
        var end = atEndOfCell(view, axis, dir);
        if (end == null) return false;
        if (axis === "horiz") {
            var nextSel = Selection.near(state.doc.resolve(sel.head + dir), dir);
            if (!isInTable$1({
                selection: nextSel
            })) return false;
            return maybeSetSelection(state, dispatch, nextSel);
        }
        var $cell = state.doc.resolve(end);
        var $next = nextCell($cell, axis, dir);
        var newSel;
        if ($next) {
            newSel = Selection.near($next, 1);
        } else if (dir < 0) {
            newSel = Selection.near(state.doc.resolve($cell.before(-1)), -1);
        } else {
            newSel = Selection.near(state.doc.resolve($cell.after(-1)), 1);
        }
        return maybeSetSelection(state, dispatch, newSel);
    };
}
function shiftArrow(axis, dir) {
    return function(state, dispatch, view) {
        var sel = state.selection;
        if (!_instanceof$5(sel, CellSelection)) {
            var end = atEndOfCell(view, axis, dir);
            if (end == null) return false;
            sel = new CellSelection(state.doc.resolve(end));
        }
        var $head = nextCell(sel.$headCell, axis, dir);
        if (!$head) return false;
        return maybeSetSelection(state, dispatch, new CellSelection(sel.$anchorCell, $head));
    };
}
// Check whether the cursor is at the end of a cell (so that further
// motion would move out of the cell)
function atEndOfCell(view, axis, dir, nextState) {
    if (!_instanceof$5(view.state.selection, TextSelection)) return null;
    var $head = view.state.selection.$head;
    for(var d = $head.depth - 1; d >= 0; d--){
        var parent = $head.node(d);
        var index = dir < 0 ? $head.index(d) : $head.indexAfter(d);
        if (index !== (dir < 0 ? 0 : parent.childCount)) return null;
        if (parent.type.spec.tableRole === "cell" || parent.type.spec.tableRole === "header_cell") {
            var cellPos = $head.before(d);
            var dirStr = void 0;
            if (axis === "vert") {
                dirStr = dir > 0 ? "down" : "up";
            } else {
                dirStr = dir > 0 ? "right" : "left";
            }
            return view.endOfTextblock(dirStr, nextState) ? cellPos : null;
        }
    }
    return null;
}
/**
 * 处理文档内部节点的拖拽到表格中的行为
 * https://km.sankuai.com/collabpage/1309120749
 * https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/65637326
 */ var handleDrop = function(view, event, slice, moved) {
    if (!moved || !slice) return false;
    var eventPos = view.posAtCoords({
        left: event.clientX,
        top: event.clientY
    });
    var $mouse = view.state.doc.resolve(eventPos.pos);
    var insertPos = getInsertPosInTable($mouse);
    // 交给 pm 默认处理
    if (insertPos === null) return false;
    // 删除原来地方的内容
    var tr = view.state.tr;
    tr.deleteSelection();
    // 获得删除之后的位置
    var pos = tr.mapping.map(insertPos);
    var isNode = slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1;
    var beforeInsertDoc = tr.doc;
    if (isNode) {
        tr.replaceRangeWith(pos, pos, slice.content.firstChild);
    } else {
        tr.replaceRange(pos, pos, slice);
    }
    // doc 没变化那就不用继续了
    if (tr.doc.eq(beforeInsertDoc)) return true;
    var $pos = tr.doc.resolve(pos);
    if (isNode && NodeSelection.isSelectable(slice.content.firstChild) && $pos.nodeAfter && $pos.nodeAfter.sameMarkup(slice.content.firstChild)) {
        tr.setSelection(new NodeSelection($pos));
    } else {
        tr.setSelection(TextSelection.between($pos, tr.doc.resolve(tr.mapping.map(insertPos))));
    }
    view.focus();
    view.dispatch(tr.setMeta("uiEvent", "drop"));
    return true;
};

function _instanceof$6(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var handleBackspace = function() {
    return function(state, dispatch, view) {
        var tr = state.tr, sel = state.selection;
        // 已选中整个表格时，按Backspace删除整个表格
        if (_instanceof$6(sel, CellSelection) && sel.isWholeSelection()) {
            deleteTable()(state, dispatch, view);
            return true;
        }
        // 光标在表格后时，按Backspace选中整个表格
        if (_instanceof$6(sel, GapCursor)) {
            var nodeBefore = sel.$from.nodeBefore;
            if (nodeBefore && nodeBefore.type.name === "table") {
                var table = nodeBefore;
                var startPos = sel.from - table.nodeSize + 1;
                var map = TableMap.get(table).map;
                var anchorPos = map[0] + startPos;
                var headPos = map[map.length - 1] + startPos;
                var tableSelection = CellSelection.rowSelection(tr.doc.resolve(anchorPos), tr.doc.resolve(headPos));
                dispatch(tr.setSelection(tableSelection));
                return true;
            }
        }
        return false;
    };
};
var handleDelete = function() {
    return function(state, dispatch, view) {
        var sel = state.selection;
        // 已选中整个表格时，按Delte删除整个表格
        if (_instanceof$6(sel, CellSelection) && sel.isWholeSelection()) {
            deleteTable()(state, dispatch, view);
            return true;
        }
        return false;
    };
};

var convertCommand = function(commandFunction) {
    return function(param) {
        var state = param.state, dispatch = param.dispatch, view = param.view, args = param.args;
        return commandFunction(state, dispatch, view, args);
    };
};

/**
 * keys--不区分大小写
 */ // "META_KEY-0"-正文
// 表格
var regexpTable = /\|(.+)\|\n/;

var AFTER_INSERT = "after_insert";
var TABLE_COLUMN_RESIZE = "table_column_resize";
var TABLE_HEIGHT_CHANGE = "table_height_change";
var HIDE_TABLE_MENU = "hide_table_menu";
var SHOW_TABLE_MENU = "show_table_menu";
var SYNC_PAGE_SCROLL = "sync_page_scroll";

var isResizing = false; // 是否正在调整列宽
function changeResizingStatus(status) {
    isResizing = status;
}
function updateColumns(node, colgroup, table, cellMinWidth, overrideCol, overrideValue) {
    if (isResizing && overrideCol === undefined) return;
    var _node_attrs = node.attrs, indent = _node_attrs.indent, responsive = _node_attrs.responsive, borderStyle = _node_attrs.borderStyle, borderWidth = _node_attrs.borderWidth, borderColor = _node_attrs.borderColor;
    if (!responsive) {
        var _loop = function(i) {
            var _row_child_attrs = row.child(i).attrs, colspan = _row_child_attrs.colspan, colwidth = _row_child_attrs.colwidth;
            var hasWidth = overrideCol === col ? overrideValue : colwidth && colwidth.map(function(p) {
                return parseInt(p, 10);
            }).reduce(function(prev, cur) {
                return prev + cur;
            });
            totalWidth += hasWidth || cellMinWidth;
            for(var j = 0; j < colspan; j++){
                var colWidthValue = overrideCol === col ? overrideValue : colwidth && colwidth[j];
                if (!hasWidth) fixedWidth = false;
                colgroupFragment.appendChild(document.createElement("col")).style.width = colWidthValue ? "".concat(colWidthValue, "px") : "";
                col++;
            }
        };
        var totalWidth = 0;
        var fixedWidth = true;
        var colgroupFragment = document.createDocumentFragment();
        var row = node.content.firstChild;
        var col = 0;
        for(var i = 0; i < row.childCount; i++)_loop(i);
        colgroup.innerHTML = "";
        colgroup.appendChild(colgroupFragment);
        if (fixedWidth) {
            table.style.width = "".concat(totalWidth, "px");
            table.style.minWidth = "";
        } else {
            table.style.width = "";
            table.style.minWidth = "".concat(totalWidth, "px");
        }
    } else {
        colgroup.innerHTML = "";
        table.style.width = "";
        table.style.minWidth = "";
    }
    if (indent >= 0) table.style.marginLeft = "".concat(indent * 20, "px");
    table.className = responsive ? "responsive" : "";
    table.setAttribute("data-borderstyle", borderStyle);
    table.setAttribute("data-borderwidth", borderWidth);
    table.setAttribute("data-bordercolor", borderColor);
}

/* eslint-disable @typescript-eslint/no-this-alias */ /* eslint-disable @typescript-eslint/no-use-before-define */ function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$4(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$3(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function currentColWidth(view, cellPos, param) {
    var colspan = param.colspan, colwidth = param.colwidth;
    var width = colwidth && colwidth[colwidth.length - 1];
    if (width) return width;
    var dom = view.domAtPos(cellPos);
    var node = dom.node.childNodes[dom.offset];
    var domWidth = node.offsetWidth;
    var parts = colspan;
    if (colwidth) {
        for(var i = 0; i < colspan; i++){
            if (colwidth[i]) {
                domWidth -= colwidth[i];
                parts--;
            }
        }
    }
    return domWidth / parts;
}
function domCellAround(target) {
    while(target && target.nodeName !== "TD" && target.nodeName !== "TH"){
        target = target.classList.contains("ProseMirror") ? null : target.parentNode;
    }
    return target;
}
function edgeCell(view, event, side) {
    var left = side === "left" ? event.clientX + 1 : event.clientX; // fix: 鼠标hover在边框左侧的时候，可能找到的单元格不准
    var found = view.posAtCoords({
        left: left,
        top: event.clientY
    });
    if (!found) return -1;
    var pos = found.pos;
    var $cell = cellAround(view.state.doc.resolve(pos));
    if (!$cell) return -1;
    if (side === "right") return $cell.pos;
    var map = TableMap.get($cell.node(-1));
    var start = $cell.start(-1);
    var index = map.map.indexOf($cell.pos - start);
    return index % map.width === 0 ? -1 : start + map.map[index - 1];
}
function draggedWidth(dragging, event, cellMinWidth) {
    var offset = event.clientX - dragging.startX;
    return Math.max(cellMinWidth, dragging.startWidth + offset);
}
function updateHandle(view, value) {
    view.dispatch(view.state.tr.setMeta(columnResizingKey, {
        setHandle: value
    }));
}
function zeroes(n) {
    var result = [];
    for(var i = 0; i < n; i++)result.push(0);
    return result;
}
// 检查表格某列中是否存在附件
function checkColHasAttachment(dom, col) {
    var rowListDom = dom.querySelectorAll("tbody tr");
    var hasAttachment = false;
    rowListDom.forEach(function(row) {
        if (row.children.length > col) {
            var colDom = row.children[col];
            if (colDom.innerHTML.includes("ct-attachment")) {
                hasAttachment = true;
            }
        }
    });
    return hasAttachment;
}
function updateColumnWidth(view, cell, width) {
    var $cell = view.state.doc.resolve(cell);
    var table = $cell.node(-1);
    if (!table) {
        return;
    }
    var map = TableMap.get(table);
    var start = $cell.start(-1);
    var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
    var tr = view.state.tr;
    var dom = view.domAtPos($cell.start(-1)).node;
    // 如果该列中存在附件，最小宽度为 200px
    if (checkColHasAttachment(dom, col)) {
        width = Math.max(200, width);
    }
    for(var row = 0; row < map.height; row++){
        var mapIndex = row * map.width + col;
        // Rowspanning cell that has already been handled
        if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) continue;
        var pos = map.map[mapIndex];
        var attrs = table.nodeAt(pos).attrs;
        var index = attrs.colspan === 1 ? 0 : col - map.colCount(pos);
        if (attrs.colwidth && attrs.colwidth[index] === width) continue;
        var colwidth = attrs.colwidth ? attrs.colwidth.slice() : zeroes(attrs.colspan);
        colwidth[index] = width;
        tr.setNodeMarkup(start + pos, null, setAttr(attrs, "colwidth", colwidth));
    }
    if (tr.docChanged) view.dispatch(tr);
}
function displayColumnWidth(view, cell, width, cellMinWidth) {
    var $cell = view.state.doc.resolve(cell);
    var table = $cell.node(-1);
    var start = $cell.start(-1);
    if (!table || !start) return;
    var col = TableMap.get(table).colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
    var dom = view.domAtPos($cell.start(-1)).node;
    while(dom.nodeName !== "TABLE")dom = dom.parentNode;
    changeResizingStatus(true);
    // 如果该列中存在附件，最小宽度为 200px
    if (checkColHasAttachment(dom, col)) {
        cellMinWidth = 200;
        width = Math.max(cellMinWidth, width);
    }
    updateColumns(table, dom.firstChild, dom, cellMinWidth, col, width);
}
function handleMouseMove(view, event, handleWidth, cellMinWidth, lastColumnResizable) {
    var _event_target, _event_target_classList;
    var pluginState = columnResizingKey.getState(view.state);
    if ((_event_target = event.target) === null || _event_target === void 0 ? void 0 : (_event_target_classList = _event_target.classList) === null || _event_target_classList === void 0 ? void 0 : _event_target_classList.contains("column-resize-handler")) return false;
    if (!pluginState.dragging) {
        var target = domCellAround(event.target);
        var cell = -1;
        if (target) {
            var _target_getBoundingClientRect = target.getBoundingClientRect(), left = _target_getBoundingClientRect.left, right = _target_getBoundingClientRect.right;
            if (event.clientX - left <= handleWidth) {
                cell = edgeCell(view, event, "left");
            } else if (right - event.clientX <= handleWidth) {
                cell = edgeCell(view, event, "right");
            }
        }
        if (cell !== pluginState.activeHandle) {
            if (!lastColumnResizable && cell !== -1) {
                var $cell = view.state.doc.resolve(cell);
                var table = $cell.node(-1);
                var map = TableMap.get(table);
                var start = $cell.start(-1);
                var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
                if (col === map.width - 1) {
                    return false;
                }
            }
            updateHandle(view, cell);
        }
    }
    return false;
}
function handleMouseDown$1(view, event, cellMinWidth, eventEmitter) {
    var move = function move(e) {
        if (!e.which) {
            finish(e);
            return;
        }
        var curPluginState = columnResizingKey.getState(view.state);
        var dragged = draggedWidth(curPluginState.dragging, e, cellMinWidth);
        displayColumnWidth(view, curPluginState.activeHandle, dragged, cellMinWidth);
    };
    var pluginState = columnResizingKey.getState(view.state);
    if (pluginState.activeHandle === -1 || pluginState.dragging) return false;
    var _view_state = view.state, tr = _view_state.tr, doc = _view_state.doc;
    var cell = doc.nodeAt(pluginState.activeHandle);
    var width = currentColWidth(view, pluginState.activeHandle, cell.attrs);
    var _findTable = findTable({
        $from: doc.resolve(pluginState.activeHandle)
    }), tablePos = _findTable.pos, table = _findTable.node;
    // 当前宽度自适应时，设为固定宽度，并同步各单元格的宽度
    if (table.attrs.responsive) {
        doc.nodesBetween(tablePos, tablePos + table.nodeSize, function(node, pos) {
            if ([
                "table_header",
                "table_cell"
            ].includes(node.type.name)) {
                var attrs = node.attrs;
                var cellDom = view.domAtPos(pos + 1).node;
                var colspan = parseInt(cellDom.getAttribute("colspan"), 10) || 1;
                var cellWidth = cellDom.getBoundingClientRect().width / colspan;
                var newColwidth = [];
                for(var i = 0; i < colspan; i++)newColwidth.push(cellWidth);
                var newAttrs = _objectSpreadProps$3(_objectSpread$4({}, attrs), {
                    colwidth: newColwidth
                });
                tr.setNodeMarkup(pos, null, newAttrs);
                if (cell === node) {
                    width = formatWidth(currentColWidth(view, pluginState.activeHandle, newAttrs));
                }
            }
        });
        tr.setNodeMarkup(tablePos, null, _objectSpreadProps$3(_objectSpread$4({}, table.attrs), {
            responsive: false
        }));
    }
    view.dispatch(tr.setMeta(columnResizingKey, {
        setDragging: {
            startX: event.clientX,
            startWidth: width
        }
    }));
    function finish(e) {
        eventEmitter.emit(TABLE_COLUMN_RESIZE);
        window.removeEventListener("mouseup", finish);
        window.removeEventListener("mousemove", move);
        var curPluginState = columnResizingKey.getState(view.state);
        if (curPluginState.dragging) {
            updateColumnWidth(view, curPluginState.activeHandle, draggedWidth(curPluginState.dragging, e, cellMinWidth));
            view.dispatch(view.state.tr.setMeta(columnResizingKey, {
                setDragging: null
            }));
        }
    }
    window.addEventListener("mouseup", finish);
    window.addEventListener("mousemove", move);
    event.preventDefault();
    return true;
}
/**
 * 根据 view pos，生成 decoration dom
 */ var renderDecorationDom = function(view, pos, bWidth, isRowEnd) {
    try {
        var cellDOM = view.domAtPos(pos).node;
        var dom = document.createElement("div");
        var borderWidth = Number.parseInt(bWidth || "1", 10);
        var left = cellDOM.offsetLeft + cellDOM.offsetWidth - borderWidth;
        var tableDOM = cellDOM.closest("table");
        var tableWrapperDOM = cellDOM.closest(".".concat(classNamePrefix("table-wrapper")));
        if (isRowEnd && (tableDOM === null || tableDOM === void 0 ? void 0 : tableDOM.offsetWidth) > (tableWrapperDOM === null || tableWrapperDOM === void 0 ? void 0 : tableWrapperDOM.offsetWidth)) {
            left -= 2;
        }
        dom.style.left = "".concat(left, "px");
        dom.className = "column-resize-handler";
        return dom;
    } catch (error) {
        return null;
    }
};
function handleDecorations(state, cell) {
    var _loop = function(row) {
        var index = col + row * map.width - 1;
        // For positions that are have either a different cell or the end
        // of the table to their right, and either the top of the table or
        // a different cell above them, add a decoration
        if ((col === map.width || map.map[index] !== map.map[index + 1]) && (row === 0 || map.map[index - 1] !== map.map[index - 1 - map.width])) {
            // 在当前行的最后插入Decoration，通过计算left来保证位置正确
            var cellPos = map.map[index];
            var pos = start + cellPos + table.nodeAt(cellPos).nodeSize - 1;
            var rowNode = findParentNodeClosestToPos(tr.doc.resolve(pos), function(node) {
                return node.type === nodes.table_row;
            });
            // 在 Decoration.widget 回调中 view 才是最新的，此时 view.domAtPos 才可靠 #ones 64914273
            decorations.push(Decoration.widget(rowNode.pos + rowNode.node.nodeSize - 1, function(newView) {
                return renderDecorationDom(newView, pos, table.attrs.borderWidth, col === map.width);
            }));
            return "break";
        }
    };
    var decorations = [];
    var $cell = state.doc.resolve(cell);
    var table = $cell.node(-1);
    if (!table) {
        return null;
    }
    var map = TableMap.get(table);
    var start = $cell.start(-1);
    var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan;
    var nodes = state.schema.nodes, tr = state.tr;
    for(var row = 0; row < map.height; row++){
        var _ret = _loop(row);
        if (_ret === "break") break;
    }
    return DecorationSet.create(state.doc, decorations);
}
var ResizeState = /*#__PURE__*/ function() {
    function ResizeState(activeHandle, dragging) {
        _classCallCheck(this, ResizeState);
        this.activeHandle = activeHandle;
        this.dragging = dragging;
    }
    var _proto = ResizeState.prototype;
    _proto.apply = function apply(tr) {
        var state = this;
        var action = tr.getMeta(columnResizingKey);
        if (action && action.setHandle != null) {
            return new ResizeState(action.setHandle, null);
        }
        if (action && action.setDragging !== undefined) {
            return new ResizeState(state.activeHandle, action.setDragging);
        }
        if (state.activeHandle > -1 && tr.docChanged) {
            var handle = tr.mapping.map(state.activeHandle, -1);
            if (!pointsAtCell(tr.doc.resolve(handle))) handle = null;
            state = new ResizeState(handle, state.dragging);
        }
        return state;
    };
    return ResizeState;
}();
function columnResizing(provider, getView) {
    var _ref = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {}, _ref_handleWidth = _ref.handleWidth, handleWidth = _ref_handleWidth === void 0 ? 5 : _ref_handleWidth, _ref_cellMinWidth = _ref.cellMinWidth, cellMinWidth = _ref_cellMinWidth === void 0 ? MIN_COL_WIDTH : _ref_cellMinWidth, _ref_lastColumnResizable = _ref.lastColumnResizable, lastColumnResizable = _ref_lastColumnResizable === void 0 ? true : _ref_lastColumnResizable, _ref_editorEventEmitter = _ref.editorEventEmitter, editorEventEmitter = _ref_editorEventEmitter === void 0 ? null : _ref_editorEventEmitter;
    return new Plugin({
        key: columnResizingKey,
        state: {
            init: function init() {
                return new ResizeState(-1, false);
            },
            apply: function apply(tr, prev) {
                return prev.apply(tr);
            }
        },
        props: {
            handleDOMEvents: {
                mousemove: function mousemove(view, event) {
                    return handleMouseMove(view, event, handleWidth, cellMinWidth, lastColumnResizable);
                },
                mousedown: function mousedown(view, event) {
                    return handleMouseDown$1(view, event, cellMinWidth, editorEventEmitter);
                }
            },
            decorations: function decorations(state) {
                var pluginState = columnResizingKey.getState(state);
                if (pluginState.activeHandle > -1) {
                    return handleDecorations(state, pluginState.activeHandle);
                }
                changeResizingStatus(false);
                return DecorationSet.empty;
            },
            nodeViews: {}
        }
    });
}

function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$5(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$5(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$4(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$4(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$4(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var TableView = /*#__PURE__*/ function() {
    function TableView(node, view, getPos, provider, cellMinWidth, eventEmitter) {
        var _this = this;
        _classCallCheck$1(this, TableView);
        this.handleAfterInsert = function(param) {
            var braceWidth = param.braceWidth, pos = param.pos;
            if (!braceWidth) return;
            var _this_view_state = _this.view.state, selection = _this_view_state.selection, schema = _this_view_state.schema, doc = _this_view_state.doc;
            var _schema_nodes = schema.nodes, table_cell = _schema_nodes.table_cell, table_header = _schema_nodes.table_header;
            var foundCellNode = findParentNode(function(node) {
                return [
                    table_cell,
                    table_header
                ].includes(node.type);
            })(pos ? {
                $from: doc.resolve(pos)
            } : selection);
            // 是否在表格内
            if (foundCellNode) {
                var attrs = foundCellNode.node.attrs;
                var width = attrs.colwidth.reduce(function(a, b) {
                    return a + b;
                });
                // 单元格宽度是否小于撑开宽度
                if (width < braceWidth) {
                    updateColumnWidth(_this.view, foundCellNode.pos, braceWidth);
                }
            }
        };
        // 处理异常宽度表格 固定表格宽度
        this.fixedErrorColumns = function(getPos) {
            if (_this.node.attrs.responsive) return;
            var _this_view = _this.view, state = _this_view.state, dispatch = _this_view.dispatch;
            var tr = state.tr, schema = state.schema;
            var _schema_nodes = schema.nodes, table_header = _schema_nodes.table_header, table_cell = _schema_nodes.table_cell;
            var cellTypes = [
                table_header,
                table_cell
            ];
            var tablePos = getPos();
            tr.doc.nodesBetween(tablePos, tablePos + _this.node.nodeSize, function(node, pos) {
                if (cellTypes.includes(node.type)) {
                    var attrs = node.attrs;
                    var colwidth = attrs.colwidth;
                    if (colwidth.includes(0) || colwidth.includes(NaN) || colwidth.includes(Infinity)) {
                        var dom = _this.view.domAtPos(pos + 1).node;
                        var width = dom.clientWidth + 3;
                        var len = colwidth.length;
                        var newColwidth = new Array(len);
                        var divWidth = Number(width / len);
                        newColwidth.fill(divWidth > _this.cellMinWidth ? divWidth : _this.cellMinWidth);
                        try {
                            tr.setNodeMarkup(pos, null, _objectSpreadProps$4(_objectSpread$5({}, attrs), {
                                colwidth: newColwidth
                            }));
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }
            });
            dispatch(tr.setMeta("addToHistory", false));
        };
        this.view = view;
        this.node = node;
        this.provider = provider;
        this.cellMinWidth = cellMinWidth;
        this.eventEmitter = eventEmitter;
        this.dom = document.createElement("div");
        this.dom.className = "".concat(classNamePrefix("table-wrapper"), " ").concat(classNamePrefix("node-view-dom"));
        var dataDiffId = node.attrs.dataDiffId;
        if (dataDiffId) {
            this.dom.setAttribute("data-diff-id", dataDiffId);
        }
        this.table = this.dom.appendChild(document.createElement("table"));
        this.colgroup = this.table.appendChild(document.createElement("colgroup"));
        changeResizingStatus(false);
        updateColumns(node, this.colgroup, this.table, cellMinWidth);
        this.contentDOM = this.table.appendChild(document.createElement("tbody"));
        this.timer = setTimeout(function() {
            _this.fixedErrorColumns(getPos);
        }, 100);
        // 下面的逻辑是把表格工具条（比如 selectbar）从文档内 fixed 布局改为表格内相对布局进行的 layout 调整
        this.adaptForPortalLayoutNode();
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.on(AFTER_INSERT, this.handleAfterInsert);
        eventEmitter.on(AFTER_INSERT, this.handleAfterInsert);
    }
    var _proto = TableView.prototype;
    _proto.adaptForPortalLayoutNode = function adaptForPortalLayoutNode() {
        var _this = this;
        var addPortalLayoutNode = function() {
            var $mountedContainer = document.createElement("div");
            $mountedContainer.classList.add("mounted-container");
            // 表格列选择条
            var colPortal = document.createElement("div");
            colPortal.className = classNamePrefix("table-col-selectbar-layout");
            $mountedContainer.appendChild(colPortal);
            // 表格行选择条
            var rowPortal = document.createElement("div");
            rowPortal.className = classNamePrefix("table-row-selectbar-layout");
            $mountedContainer.appendChild(rowPortal);
            var allPortal = document.createElement("div");
            allPortal.className = classNamePrefix("table-all-selectbar-layout");
            $mountedContainer.appendChild(allPortal);
            _this.mountedContainer = $mountedContainer;
            _this.dom.appendChild($mountedContainer);
        };
        var wrapTableContainer = function() {
            var $tableWrapper = _this.dom;
            var $tableContainer = document.createElement("div");
            $tableContainer.classList.add("table-container");
            _this.dom = $tableContainer;
            _this.dom.appendChild($tableWrapper);
            _this.tableWrapper = $tableWrapper;
        };
        wrapTableContainer();
        addPortalLayoutNode();
    };
    _proto.destroy = function destroy() {
        var _this_eventEmitter, _this_eventEmitter1;
        (_this_eventEmitter = this.eventEmitter) === null || _this_eventEmitter === void 0 ? void 0 : _this_eventEmitter.on(AFTER_INSERT, this.handleAfterInsert);
        (_this_eventEmitter1 = this.eventEmitter) === null || _this_eventEmitter1 === void 0 ? void 0 : _this_eventEmitter1.off(AFTER_INSERT, this.handleAfterInsert);
        clearTimeout(this.timer);
    };
    _proto.update = function update(node) {
        if (node.type !== this.node.type) return false;
        this.node = node;
        updateColumns(node, this.colgroup, this.table, this.cellMinWidth);
        return true;
    };
    _proto.stopEvent = function stopEvent(e) {
        // 如果事件来源是.mounted-container，也一并ignore
        if (this.mountedContainer.contains(e.target)) {
            return true;
        }
        return false;
    };
    _proto.ignoreMutation = function ignoreMutation(record) {
        return record.type === "attributes" && (record.target === this.table || this.colgroup.contains(record.target) || record.target === this.dom || record.target === this.tableWrapper) || this.mountedContainer.contains(record.target);
    };
    return TableView;
}();

/* eslint-disable no-labels */ /* eslint-disable no-restricted-syntax */ // This file defines helpers for normalizing tables, making sure no
// cells overlap (which can happen, if you have the wrong col- and
// rowspans) and that each row has the same width. Uses the problems
// reported by `TableMap`.
function _defineProperty$6(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$6(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$6(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$5(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$5(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$5(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// Helper for iterating through the nodes in a document that changed
// compared to the given previous document. Useful for avoiding
// duplicate work on each transaction.
function changedDescendants(old, cur, offset, f) {
    var oldSize = old.childCount;
    var curSize = cur.childCount;
    outer: for(var i = 0, j = 0; i < curSize; i++){
        var child = cur.child(i);
        for(var scan = j, e = Math.min(oldSize, i + 3); scan < e; scan++){
            if (old.child(scan) === child) {
                j = scan + 1;
                offset += child.nodeSize;
                continue outer;
            }
        }
        f(child, offset);
        if (j < oldSize && old.child(j).sameMarkup(child)) {
            changedDescendants(old.child(j), child, offset + 1, f);
        } else {
            child.nodesBetween(0, child.content.size, f, offset + 1);
        }
        offset += child.nodeSize;
    }
}
// : (EditorState, Node, number, ?Transaction) → ?Transaction
// Fix the given table, if necessary. Will append to the transaction
// it was given, if non-null, or create a new one if necessary.
function fixTable(state, table, tablePos, tr) {
    var _loop = function(i1) {
        var prob = map.problems[i1];
        if (prob.type === "collision") {
            var cell = table.nodeAt(prob.pos);
            for(var j = 0; j < cell.attrs.rowspan; j++){
                mustAdd[prob.row + j] += prob.n;
            }
            tr.setNodeMarkup(tr.mapping.map(tablePos + 1 + prob.pos), null, removeColSpan(cell.attrs, cell.attrs.colspan - prob.n, prob.n));
        } else if (prob.type === "missing") {
            mustAdd[prob.row] += prob.n;
        } else if (prob.type === "overlong_rowspan") {
            var cell1 = table.nodeAt(prob.pos);
            tr.setNodeMarkup(tr.mapping.map(tablePos + 1 + prob.pos), null, setAttr(cell1.attrs, "rowspan", cell1.attrs.rowspan - prob.n));
        } else if (prob.type === "colwidth mismatch") {
            var cell2 = table.nodeAt(prob.pos);
            tr.setNodeMarkup(tr.mapping.map(tablePos + 1 + prob.pos), null, setAttr(cell2.attrs, "colwidth", prob.colwidth));
        } else if (prob.type === "invalid_cell_for_row_header_table") {
            tr.doc.nodesBetween(tr.mapping.map(tablePos), tr.mapping.map(table.nodeSize + tablePos - 1), function(node, pos, parent) {
                if (parent === table.firstChild) {
                    var attrs = node.attrs;
                    tr.setNodeMarkup(tr.mapping.map(pos), state.schema.nodes.table_header, _objectSpreadProps$5(_objectSpread$6({}, attrs), {
                        numCell: false
                    }));
                }
            });
        }
    };
    var isRowHeader = hasRowHeader(table);
    var isNumCol = hasNumCol(table);
    var map = TableMap.get(table);
    if (!map.problems) return tr;
    if (!tr) tr = state.tr;
    // Track which rows we must add cells to, so that we can adjust that
    // when fixing collisions.
    var mustAdd = [];
    for(var i = 0; i < map.height; i++)mustAdd.push(0);
    for(var i1 = 0; i1 < map.problems.length; i1++)_loop(i1);
    var first;
    var last;
    for(var i2 = 0; i2 < mustAdd.length; i2++){
        if (mustAdd[i2]) {
            if (first == null) first = i2;
            last = i2;
        }
    }
    // Add the necessary cells, using a heuristic for whether to add the
    // cells at the start or end of the rows (if it looks like a 'bite'
    // was taken out of the table, add cells at the start of the row
    // after the bite. Otherwise add them at the end).
    for(var i3 = 0, pos = tablePos + 1; i3 < map.height; i3++){
        var row = table.child(i3);
        var end = pos + row.nodeSize;
        var add = mustAdd[i3];
        var needFixRowHeader = isRowHeader && i3 === 0;
        if (add > 0) {
            var nodes = [];
            for(var j = 0; j < add; j++){
                if (needFixRowHeader) {
                    nodes.push(state.schema.nodes.table_header.createAndFill());
                } else {
                    nodes.push(state.schema.nodes.table_cell.createAndFill());
                }
            }
            // 如果有「序号」，一律从每行最后开始补
            var side = !isNumCol && (i3 === 0 || first === i3 - 1) && last === i3 ? pos + 1 : end - 1;
            tr.insert(tr.mapping.map(side), nodes);
        }
        pos = end;
    }
    return tr.setMeta(fixTablesKey, {
        fixTables: true
    });
}
// :: (EditorState, ?EditorState) → ?Transaction
// Inspect all tables in the given state's document and return a
// transaction that fixes them, if necessary. If `oldState` was
// provided, that is assumed to hold a previous, known-good state,
// which will be used to avoid re-scanning unchanged parts of the
// document.
function fixTables(state, oldState) {
    var tr;
    var check = function(node, pos) {
        if (node.type.spec.tableRole === "table") {
            tr = fixTable(state, node, pos, tr);
        }
    };
    if (!oldState) state.doc.descendants(check);
    else if (oldState.doc !== state.doc) {
        changedDescendants(oldState.doc, state.doc, 0, check);
    }
    return tr;
}

var offsets = {
    table_cell: 2,
    table_row: 1,
    table: 0
};
var FORWARD = 0;
var BACKWARD = 1;
function getAdjustedAnchorIfInTableEnd(view, $anchor, $head, bias) {
    var nextNode = bias === FORWARD ? $anchor.nodeBefore : $anchor.nodeAfter;
    var direction = bias === FORWARD ? 1 : -1;
    if (!nextNode) {
        return null;
    }
    if (![
        "table_header",
        "table_cell",
        "table_row",
        "table"
    ].includes(nextNode.type.name)) {
        return null;
    }
    var $detectAnchor = view.state.doc.resolve($anchor.pos + offsets[nextNode.type.name] * direction);
    var detectNextNode = bias === FORWARD ? $detectAnchor.nodeBefore : $detectAnchor.nodeAfter;
    if (detectNextNode && detectNextNode.type.name === "table") {
        return $detectAnchor;
    }
    return null;
}
function getAdjustedAnchorInTableEdge(view, $anchor, $head) {
    if ($anchor.pos !== $head.pos) {
        return null;
    }
    return getAdjustedAnchorIfInTableEnd(view, $anchor, $head, FORWARD) || getAdjustedAnchorIfInTableEnd(view, $anchor, $head, BACKWARD);
}
function adjustedSelectionWhenInTableEnd(view, $anchor, $head) {
    var $adjustedAnchor = getAdjustedAnchorInTableEdge(view, $anchor, $head);
    return new GapCursor$1($adjustedAnchor, gapCursorSituation.afterNode);
}

function _instanceof$7(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function tableEditing(provider) {
    var _ref = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, _ref_cellMinWidth = _ref.cellMinWidth, cellMinWidth = _ref_cellMinWidth === void 0 ? MIN_COL_WIDTH : _ref_cellMinWidth, _ref_editorEventEmitter = _ref.editorEventEmitter, editorEventEmitter = _ref_editorEventEmitter === void 0 ? null : _ref_editorEventEmitter;
    return new Plugin({
        key: tableEditingKey,
        state: {
            init: function init(_, state) {
                this.spec.props.nodeViews.table = function(node, view, getPos) {
                    return new TableView(node, view, getPos, provider, cellMinWidth, editorEventEmitter);
                };
                return null;
            },
            apply: function apply(tr, cur) {
                var set = tr.getMeta(tableEditingKey);
                if (set != null) return set === -1 ? null : set;
                if (cur == null || !tr.docChanged) return cur;
                var _tr_mapping_mapResult = tr.mapping.mapResult(cur), deleted = _tr_mapping_mapResult.deleted, pos = _tr_mapping_mapResult.pos;
                return deleted ? null : pos;
            }
        },
        props: {
            decorations: drawCellSelection,
            nodeViews: {},
            handleDOMEvents: {
                mousedown: handleMouseDown,
                cut: function(view) {
                    var _view_state = view.state, sel = _view_state.selection;
                    if (_instanceof$7(sel, CellSelection) && sel.isWholeSelection()) {
                        var tablePos = sel.$anchorCell.before(-1);
                        setTimeout(function() {
                            var dispatch = view.dispatch, _view_state = view.state, doc = _view_state.doc, tr = _view_state.tr;
                            var tableSize = doc.nodeAt(tablePos).nodeSize;
                            var selection = TextSelection.create(doc, tablePos, tablePos + tableSize);
                            dispatch(tr.setSelection(selection).deleteSelection());
                        }, 0);
                    }
                    return false;
                }
            },
            // fix: 框选单元格选区的时候阻止浏览器默认的设置textSelection的动作
            createSelectionBetween: function createSelectionBetween(view, $anchor, $head) {
                if (tableEditingKey.getState(view.state) != null) {
                    return view.state.selection;
                }
                // 如果是单光标 && 在表格右侧
                if (getAdjustedAnchorInTableEdge(view, $anchor, $head)) {
                    return adjustedSelectionWhenInTableEnd(view, $anchor, $head);
                }
            },
            handleClick: function(view, pos) {
                var _view_state = view.state, tr = _view_state.tr, doc = _view_state.doc, selection = _view_state.selection;
                if (_instanceof$7(selection, CellSelection)) {
                    view.dispatch(tr.setSelection(TextSelection.create(doc, pos)));
                }
                return false;
            },
            handleTripleClick: handleTripleClick,
            handleKeyDown: handleKeyDown,
            handleDrop: handleDrop
        },
        appendTransaction: function appendTransaction(_, oldState, state) {
            return normalizeSelection(state, fixTables(state, oldState), oldState, false);
        }
    });
}

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = '.selectbar-addtip-wrapper.left,.selectbar-addtip-wrapper.right{float:left;width:50%;height:100%}.selectbar-addtip-wrapper.bottom,.selectbar-addtip-wrapper.top{width:100%;height:50%;position:relative}.selectbar-addtip{position:absolute;display:flex;justify-content:center;align-items:center;background:#111;border-radius:3px;line-height:26px;z-index:99;cursor:pointer}.selectbar-addtip svg path{fill:#fff}.selectbar-addtip.left,.selectbar-addtip.right{bottom:18px;width:20px;height:20px;margin-bottom:4px}.selectbar-addtip.left:after,.selectbar-addtip.right:after{content:"";position:absolute;width:0;bottom:-4px;right:50%;transform:translate(50%);display:block;border-color:#111 transparent;border-style:solid;border-width:5px 5px 0;z-index:1}.selectbar-addtip.top{top:-10px}.selectbar-addtip.bottom{bottom:-10px}.selectbar-addtip.bottom,.selectbar-addtip.top{left:-16px;width:20px;height:20px;margin-right:4px}.selectbar-addtip.bottom:after,.selectbar-addtip.top:after{content:"";position:absolute;width:0;right:-9px;bottom:50%;transform:translateY(50%);display:block;border:5px solid transparent;border-left-color:#111;z-index:1}';
styleInject(css_248z);

function _arrayLikeToArray$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterableToArrayLimit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray(arr, i) {
    return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest();
}
function _unsupportedIterableToArray$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
var AddTip = function(props) {
    var dir = props.dir, offset = props.offset, children = props.children, onClick = props.onClick, _props_visible = props.visible, visible = _props_visible === void 0 ? function() {
        return true;
    } : _props_visible;
    var _useState = _slicedToArray(useState(false), 2), showAddTip = _useState[0], setShowAddTip = _useState[1];
    return /*#__PURE__*/ React.createElement("div", {
        onMouseEnter: function() {
            return setShowAddTip(true);
        },
        onMouseLeave: function() {
            return setShowAddTip(false);
        },
        className: "selectbar-addtip-wrapper ".concat(dir)
    }, children, visible() && showAddTip && /*#__PURE__*/ React.createElement("div", {
        className: "selectbar-addtip ".concat(dir),
        style: offset ? {
            left: offset
        } : {},
        onClick: onClick
    }, /*#__PURE__*/ React.createElement("svg", {
        width: "16px",
        height: "16px",
        viewBox: "0 0 16 16"
    }, /*#__PURE__*/ React.createElement("g", {
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
    }, /*#__PURE__*/ React.createElement("g", {
        transform: "translate(-273.000000, -427.000000)",
        fill: "#BDC2C7"
    }, /*#__PURE__*/ React.createElement("g", {
        transform: "translate(276.000000, 430.000000)"
    }, /*#__PURE__*/ React.createElement("path", {
        d: "M5.66497803,4.33502197 L5.66497803,0.664978027 C5.66497803,0.297720804 5.36725722,0 5,0 C4.63274278,0 4.33502197,0.297720804 4.33502197,0.664978027 L4.33502197,4.33502197 L0.664978027,4.33502197 C0.297720804,4.33502197 0,4.63274278 0,5 C0,5.36725722 0.297720804,5.66497803 0.664978027,5.66497803 L4.33502197,5.66497803 L4.33502197,9.33502197 C4.33502197,9.7022792 4.63274278,10 5,10 C5.36725722,10 5.66497803,9.7022792 5.66497803,9.33502197 L5.66497803,5.66497803 L9.33502197,5.66497803 C9.7022792,5.66497803 10,5.36725722 10,5 C10,4.63274278 9.7022792,4.33502197 9.33502197,4.33502197 L5.66497803,4.33502197 Z",
        id: "Combined-Shape"
    })))))));
};

var compareActiveItemRange = function(range1, range2) {
    range1 = range1 || {};
    range2 = range2 || {};
    return range1.top === range2.top && range1.bottom === range2.bottom;
};
/** 比较表格信息是否发生变化，若无变化返回 true，否则返回 false */ var compareTableInfo = function(info1, info2) {
    if (!info1 || !info2) {
        return false;
    }
    return info1.left === info2.left && info1.top === info2.top && info1.width === info2.width && info1.wrapperWidth === info2.wrapperWidth && info1.rowSelected === info2.rowSelected && info1.colSelected === info2.colSelected && (info1.colWidths || []).join(",") === (info2.colWidths || []).join(",") && (info1.rowHeights || []).join(",") === (info2.rowHeights || []).join(",") && compareActiveItemRange(info1.activeItemRange, info2.activeItemRange);
};

function _arrayLikeToArray$2(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$1(arr) {
    if (Array.isArray(arr)) return arr;
}
function _instanceof$8(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArrayLimit$1(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$1() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray$1(arr, i) {
    return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$2(arr, i) || _nonIterableRest$1();
}
function _unsupportedIterableToArray$2(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$2(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen);
}
/**
 * 计算真正应该插入行的位置, 因为合并单元格的时候位置是不对的
 * https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/65128414
 * 思路是: 一列列按线性关系找到能用于准确定位插入行的单元格的位置
 * 注: 传入的 index 为行的序号
 */ var adjustAnchor = function(param) {
    var colCount = param.colCount, direction = param.direction, index = param.index, map = param.map;
    if (direction === "before") {
        for(var i = 0; i < colCount; i++){
            // 上一行的 idx
            var prevIdx = (index - 1) * colCount + i;
            var currIdx = index * colCount + i;
            // 向上查找,如果是合并单元格那个上面一行的 map 位置是和当前位置是一样的
            if (map[prevIdx] !== map[currIdx]) {
                return map[currIdx];
            }
        }
    } else {
        for(var i1 = 0; i1 < colCount; i1++){
            // 下一行的 idx
            var nextIdx = (index + 1) * colCount + i1;
            var currIdx1 = index * colCount + i1;
            // 向下查找,如果是合并单元格那个下面一行的 map 位置是和当前位置是一样的
            if (map[nextIdx] !== map[currIdx1]) {
                return map[currIdx1];
            }
        }
    }
    return map[index];
};
var RowSelectBar = function(param) {
    var pos = param.pos, view = param.view;
    var containerRef = useRef(null);
    var tableInfoRef = useRef(null);
    var tableWrapper = useMemo(function() {
        return view.nodeDOM(pos);
    }, [
        pos,
        view
    ]);
    var getEveryRowHeight = function() {
        var tableBody = tableWrapper.querySelector("table > tbody");
        return Array.from(tableBody.children).filter(function(el) {
            return [
                "TR"
            ].includes(el.tagName);
        }).map(function(tr) {
            return tr.offsetHeight;
        });
    };
    var getActiveItemRange = function() {
        var _view_state = view.state, sel = _view_state.selection;
        if (_instanceof$8(sel, CellSelection)) {
            return sel.getRect();
        }
        return {
            top: -1,
            bottom: -1
        };
    };
    var getTableInfo = function() {
        var _tableWrapper_querySelector, _tableWrapper_querySelector_getBoundingClientRect, _tableWrapper_querySelector_getBoundingClientRect_call;
        if (!tableWrapper || !tableWrapper.closest(".ProseMirror")) {
            return {
                left: 0,
                top: 0,
                width: 0
            };
        }
        var tableRect = tableWrapper.getBoundingClientRect();
        var editorRect = tableWrapper.closest(".ProseMirror").getBoundingClientRect();
        var selection = view.state.selection;
        return {
            left: tableRect.left - editorRect.left,
            top: tableRect.top - editorRect.top,
            width: (_tableWrapper_querySelector = tableWrapper.querySelector("table")) === null || _tableWrapper_querySelector === void 0 ? void 0 : (_tableWrapper_querySelector_getBoundingClientRect = _tableWrapper_querySelector.getBoundingClientRect) === null || _tableWrapper_querySelector_getBoundingClientRect === void 0 ? void 0 : (_tableWrapper_querySelector_getBoundingClientRect_call = _tableWrapper_querySelector_getBoundingClientRect.call(_tableWrapper_querySelector)) === null || _tableWrapper_querySelector_getBoundingClientRect_call === void 0 ? void 0 : _tableWrapper_querySelector_getBoundingClientRect_call.width,
            rowHeights: getEveryRowHeight(),
            wrapperWidth: tableRect.width,
            activeItemRange: getActiveItemRange(),
            rowSelected: _instanceof$8(selection, CellSelection) && selection.isRowSelection()
        };
    };
    var _useState = _slicedToArray$1(useState(function() {
        return getTableInfo();
    }), 2), tableInfo = _useState[0], setTableInfo = _useState[1];
    var updatePos = function() {
        var newTableInfo = getTableInfo();
        if (!compareTableInfo(tableInfoRef.current, newTableInfo)) {
            setTableInfo(newTableInfo);
            tableInfoRef.current = newTableInfo;
        }
    };
    useEffect(function() {
        var updatePosTimer = setInterval(updatePos, 100);
        return function() {
            clearInterval(updatePosTimer);
            updatePosTimer = null;
        };
    }, [
        tableWrapper
    ]);
    var preventShiftMouseDown = function(e) {
        if (e.shiftKey) e.preventDefault();
    };
    var onSelectRow = function(e, index) {
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, dispatch = view.dispatch;
        if (_instanceof$8(selection, CellSelection) && e.shiftKey && selection.isRowSelection()) {
            var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
            if (!table) return null;
            var _TableMap_get = TableMap.get(table), map = _TableMap_get.map, width = _TableMap_get.width;
            var selectAnchorPos = map[width * index] + start;
            var tableSelection = CellSelection.rowSelection(tr.doc.resolve(selection.$anchorCell.pos), tr.doc.resolve(selectAnchorPos));
            dispatch(tr.setSelection(tableSelection));
            view.focus();
        } else {
            var _ref1 = findTable(selection) || {}, table1 = _ref1.node, start1 = _ref1.start;
            if (!table1) return null;
            var _TableMap_get1 = TableMap.get(table1), map1 = _TableMap_get1.map, width1 = _TableMap_get1.width;
            var anchorPos = map1[width1 * index] + start1;
            var tableSelection1 = CellSelection.rowSelection(tr.doc.resolve(anchorPos));
            dispatch(tr.setSelection(tableSelection1));
            view.focus();
        }
    };
    var clickAddTip = function(e, idx, dir) {
        e.stopPropagation();
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, doc = _view_state.doc, dispatch = view.dispatch;
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        if (!table) return null;
        var _TableMap_get = TableMap.get(table), map = _TableMap_get.map, colCount = _TableMap_get.width;
        var anchorPos = adjustAnchor({
            colCount: colCount,
            direction: dir,
            index: idx,
            map: map
        }) + start;
        dispatch(tr.setSelection(TextSelection.create(doc, anchorPos + 2))); // +2是因为<cell><p></p>
        if (dir === "before") {
            addRowBefore()(view.state, view.dispatch, view);
        } else {
            addRowAfter()(view.state, view.dispatch, view);
        }
        view.focus();
    };
    var showAddRowBeforeTip = function(idx) {
        var _view_state = view.state, selection = _view_state.selection, doc = _view_state.doc;
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        if (!table) return null;
        var _TableMap_get = TableMap.get(table), map = _TableMap_get.map, width = _TableMap_get.width;
        var anchorPos = map[width * idx] + start;
        if (Number.isNaN(anchorPos) || anchorPos === undefined) {
            console.warn("Position Nan out of range - rowSelectBar");
            return false;
        }
        return canAddRowBefore({
            selection: TextSelection.create(doc, anchorPos + 2)
        });
    };
    var rowSelectItems = function() {
        var items = [];
        var selection = view.state.selection;
        var node = (findTable(selection) || {}).node;
        if (!node) return null;
        var rowHeights = getEveryRowHeight();
        var activeRange = getActiveItemRange();
        rowHeights.forEach(function(h, idx) {
            var selected = idx >= activeRange.top && idx < activeRange.bottom;
            items.push(/*#__PURE__*/ React.createElement("div", {
                key: idx,
                style: {
                    height: "".concat(h, "px")
                },
                className: "".concat(classNamePrefix("table-row-selectbar-item")).concat(selected ? " selected" : ""),
                onClick: function(e) {
                    return onSelectRow(e, idx);
                },
                onMouseDown: preventShiftMouseDown
            }, /*#__PURE__*/ React.createElement(AddTip, {
                dir: "top",
                onClick: function(e) {
                    return clickAddTip(e, idx, "before");
                },
                visible: function() {
                    return showAddRowBeforeTip(idx);
                }
            }, /*#__PURE__*/ React.createElement("div", {
                className: "visible-area top"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "btn"
            }))), /*#__PURE__*/ React.createElement(AddTip, {
                dir: "bottom",
                onClick: function(e) {
                    return clickAddTip(e, idx, "after");
                }
            }, /*#__PURE__*/ React.createElement("div", {
                className: "visible-area bottom"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "btn"
            })))));
        });
        return items;
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-row-selectbar-container"))
    }, /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-row-selectbar")),
        ref: containerRef
    }, rowSelectItems()));
};

function _arrayLikeToArray$3(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$2(arr) {
    if (Array.isArray(arr)) return arr;
}
function _arrayWithoutHoles$1(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray$3(arr);
}
function _instanceof$9(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArray$1(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterableToArrayLimit$2(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$2() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableSpread$1() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray$2(arr, i) {
    return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$3(arr, i) || _nonIterableRest$2();
}
function _toConsumableArray$1(arr) {
    return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$3(arr) || _nonIterableSpread$1();
}
function _unsupportedIterableToArray$3(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$3(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen);
}
/**
 * 计算真正应该插入列的位置, 因为合并单元格的时候位置是不对的
 * https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/65128414
 * 思路是: 一行行按线性关系找到能用于准确定位插入列的单元格的位置
 */ var adjustAnchor$1 = function(param) {
    var rowCount = param.rowCount, colCount = param.colCount, direction = param.direction, index = param.index, map = param.map;
    if (direction === "before") {
        for(var i = 0; i < rowCount; i++){
            var idx = i * colCount + index;
            // 向前查找,如果是合并单元格那个前面一个 map 位置是和当前位置是一样的
            if (map[idx - 1] !== map[idx]) {
                return map[idx];
            }
        }
    } else {
        for(var i1 = 0; i1 < rowCount; i1++){
            var idx1 = i1 * colCount + index;
            // 向后查找,如果是合并单元格那个后面一个 map 位置是和当前位置是一样的
            if (map[idx1 + 1] !== map[idx1]) {
                return map[idx1];
            }
        }
    }
    return map[index];
};
var ColSelectBar = function(param) {
    var pos = param.pos, view = param.view, eventEmitter = param.eventEmitter;
    var colBarRef = useRef(null);
    var tableInfoRef = useRef(null);
    var tableWrapper = useMemo(function() {
        var _view_nodeDOM;
        return (_view_nodeDOM = view.nodeDOM(pos)) === null || _view_nodeDOM === void 0 ? void 0 : _view_nodeDOM.querySelector(".ct-table-wrapper");
    }, [
        pos,
        view
    ]);
    var _useState = _slicedToArray$2(useState(0), 2), scrollLeft = _useState[0], setScrollLeft = _useState[1];
    var getEveryColWidth = function() {
        var tableBody = tableWrapper.querySelector("table > tbody");
        var isTableResponsive = isResponsive(view.state);
        return Array.from(tableBody.firstElementChild.children).filter(function(p) {
            return [
                "TH",
                "TD"
            ].includes(p.tagName);
        }).reduce(function(prev, cur) {
            var colspan = Number.parseInt(cur.getAttribute("colspan"), 10) || 1;
            // 单列，没有合并单元格
            if (colspan === 1) {
                return _toConsumableArray$1(prev).concat([
                    cur.offsetWidth
                ]);
            }
            // 非自适应，非单列表格
            if (!isTableResponsive && cur.getAttribute("data-colwidth")) {
                var colwidths = cur.getAttribute("data-colwidth").split(",").map(function(p) {
                    return Number.parseInt(p, 10);
                });
                return _toConsumableArray$1(prev).concat(_toConsumableArray$1(colwidths));
            }
            // 可以处理外部的一些没有data-colwidth属性的表格
            prev = _toConsumableArray$1(prev).concat(_toConsumableArray$1(Array.from({
                length: colspan
            }).map(function() {
                return Math.ceil(cur.offsetWidth / colspan);
            })));
            return prev;
        }, []);
    };
    var getActiveItemRange = function() {
        var _view_state = view.state, sel = _view_state.selection;
        if (_instanceof$9(sel, CellSelection)) {
            return sel.getRect();
        }
        return {
            left: -1,
            right: -1
        };
    };
    var getTableInfo = function() {
        if (!tableWrapper || !tableWrapper.closest(".ProseMirror")) {
            return {
                left: 0,
                top: 0,
                width: 0,
                colWidths: []
            };
        }
        var tableRect = tableWrapper.getBoundingClientRect();
        var editorContainer = tableWrapper.closest(".ct-editor-container");
        if (!editorContainer) return {
            left: 0,
            top: 0,
            width: 0,
            colWidths: []
        };
        // 增加editor容器本身在屏幕中的的偏移计算
        var editorRect = editorContainer.getBoundingClientRect();
        var selection = view.state.selection;
        return {
            left: tableRect.left - editorRect.left,
            // 表格的top + editorContainer的内部内容滚动偏移 - editor容器在屏幕内的偏移
            top: tableRect.top + editorContainer.scrollTop - editorRect.y,
            width: tableRect.width,
            height: tableRect.height,
            colWidths: getEveryColWidth(),
            activeItemRange: getActiveItemRange(),
            colSelected: _instanceof$9(selection, CellSelection) && selection.isColSelection()
        };
    };
    var _useState1 = _slicedToArray$2(useState(function() {
        return getTableInfo();
    }), 2), tableInfo = _useState1[0], setTableInfo = _useState1[1];
    var updatePos = function() {
        if (colBarRef.current) {
            colBarRef.current.scrollLeft = tableWrapper.scrollLeft;
        }
        tableWrapper && setScrollLeft(tableWrapper.scrollLeft);
        var newTableInfo = getTableInfo();
        if (!compareTableInfo(tableInfoRef.current, newTableInfo)) {
            setTableInfo(newTableInfo);
            tableInfoRef.current = newTableInfo;
        }
    };
    useEffect(function() {
        eventEmitter.on(SYNC_PAGE_SCROLL, updatePos);
        var updatePosTimer = setInterval(updatePos, 100);
        return function() {
            eventEmitter.off(SYNC_PAGE_SCROLL, updatePos);
            clearInterval(updatePosTimer);
            updatePosTimer = null;
        };
    }, [
        tableWrapper
    ]);
    var getTotalColWidth = function() {
        var tableBody = tableWrapper.querySelector("table > tbody");
        if (tableBody) {
            return tableBody.offsetWidth;
        }
        return 0;
    };
    var preventShiftMouseDown = function(e) {
        if (e.shiftKey) e.preventDefault();
    };
    var onSelectCol = function(e, index) {
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, dispatch = view.dispatch;
        if (_instanceof$9(selection, CellSelection) && e.shiftKey && selection.isColSelection()) {
            var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
            if (!table) return null;
            var map = TableMap.get(table).map;
            var selectAnchorPos = map[index] + start;
            var tableSelection = CellSelection.colSelection(tr.doc.resolve(selection.$anchorCell.pos), tr.doc.resolve(selectAnchorPos));
            dispatch(tr.setSelection(tableSelection));
            view.focus();
        } else {
            var _ref1 = findTable(selection) || {}, table1 = _ref1.node, start1 = _ref1.start;
            if (!table1) return null;
            var map1 = TableMap.get(table1).map;
            var anchorPos = map1[index] + start1;
            var tableSelection1 = CellSelection.colSelection(tr.doc.resolve(anchorPos));
            dispatch(tr.setSelection(tableSelection1));
            view.focus();
        }
    };
    var clickAddTip = function(e, idx, dir) {
        e.stopPropagation();
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, doc = _view_state.doc, dispatch = view.dispatch;
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        if (!table) return null;
        var _TableMap_get = TableMap.get(table), map = _TableMap_get.map, colCount = _TableMap_get.width, rowCount = _TableMap_get.height;
        var anchorPos = adjustAnchor$1({
            rowCount: rowCount,
            colCount: colCount,
            direction: dir,
            index: idx,
            map: map
        }) + start;
        dispatch(tr.setSelection(TextSelection.create(doc, anchorPos + 2))); // +2是因为<cell><p>
        if (dir === "before") {
            addColumnBefore()(view.state, view.dispatch, view);
        } else {
            addColumnAfter()(view.state, view.dispatch, view);
        }
        view.focus();
    };
    var showAddColBeforeTip = function(idx) {
        var _view_state = view.state, selection = _view_state.selection, doc = _view_state.doc;
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        if (!table) return null;
        var map = TableMap.get(table).map;
        var anchorPos = map[idx] + start;
        return canAddColumnBefore({
            selection: TextSelection.create(doc, anchorPos + 2)
        });
    };
    var colSelectItems = function() {
        var items = [];
        var selection = view.state.selection;
        var node = (findTable(selection) || {}).node;
        if (!node) return null;
        var colWidths = getEveryColWidth();
        var prevTotalWidth = 0;
        var activeRange = getActiveItemRange();
        colWidths.forEach(function(w, idx) {
            var selected = idx >= activeRange.left && idx < activeRange.right;
            items.push(/*#__PURE__*/ React.createElement("div", {
                key: idx,
                style: {
                    width: "".concat(w, "px")
                },
                className: "".concat(classNamePrefix("table-col-selectbar-item")).concat(selected ? " selected" : ""),
                onClick: function(e) {
                    return onSelectCol(e, idx);
                },
                onMouseDown: preventShiftMouseDown
            }, /*#__PURE__*/ React.createElement(AddTip, {
                dir: "left",
                offset: Math.max(prevTotalWidth, scrollLeft) - scrollLeft - 10,
                onClick: function(e) {
                    return clickAddTip(e, idx, "before");
                },
                visible: function() {
                    return showAddColBeforeTip(idx);
                }
            }, /*#__PURE__*/ React.createElement("div", {
                className: "visible-area left"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "btn"
            }))), /*#__PURE__*/ React.createElement(AddTip, {
                dir: "right",
                offset: Math.min(prevTotalWidth + w - scrollLeft, tableInfo.width) - 10,
                onClick: function(e) {
                    return clickAddTip(e, idx, "after");
                }
            }, /*#__PURE__*/ React.createElement("div", {
                className: "visible-area right"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "btn"
            })))));
            prevTotalWidth += w;
        });
        return items;
    };
    return /*#__PURE__*/ React.createElement("div", {
        style: {
            width: "".concat(tableInfo.width, "px"),
            pointerEvents: "none"
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-col-selectbar-container")),
        ref: colBarRef,
        style: {
            width: "".concat(tableInfo.width, "px")
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-col-selectbar")),
        style: {
            width: getTotalColWidth()
        }
    }, colSelectItems())));
};

function _arrayLikeToArray$4(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$3(arr) {
    if (Array.isArray(arr)) return arr;
}
function _instanceof$a(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArrayLimit$3(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$3() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray$3(arr, i) {
    return _arrayWithHoles$3(arr) || _iterableToArrayLimit$3(arr, i) || _unsupportedIterableToArray$4(arr, i) || _nonIterableRest$3();
}
function _unsupportedIterableToArray$4(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$4(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen);
}
var os = getSystemInfo().os;
var dragCopyModifier = /MAC/.test(os.toUpperCase()) ? "altKey" : "ctrlKey";
var AllSelectBar = function(param) {
    var pos = param.pos, view = param.view;
    var tableWrapper = useMemo(function() {
        return view.nodeDOM(pos);
    }, [
        pos,
        view
    ]);
    var getTableInfo = function() {
        if (!tableWrapper || !tableWrapper.closest(".ct-editor")) {
            return {
                left: 0,
                top: 0,
                width: 0
            };
        }
        var tableRect = tableWrapper.getBoundingClientRect();
        var editorRect = tableWrapper.closest(".ct-editor").getBoundingClientRect();
        return {
            left: tableRect.left - editorRect.left,
            top: tableRect.top - editorRect.top,
            width: tableRect.width
        };
    };
    var _useState = _slicedToArray$3(useState(function() {
        return getTableInfo();
    }), 2), tableInfo = _useState[0], setTableInfo = _useState[1];
    useEffect(function() {
        setTableInfo(getTableInfo());
    }, [
        tableWrapper
    ]);
    var handleClick = function() {
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, dispatch = view.dispatch;
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        if (!table) return null;
        var map = TableMap.get(table).map;
        var anchorPos = map[0] + start;
        var headPos = map[map.length - 1] + start;
        var tableSelection = CellSelection.rowSelection(tr.doc.resolve(anchorPos), tr.doc.resolve(headPos));
        dispatch(tr.setSelection(tableSelection));
        view.focus();
    };
    var handleDragStart = function(e) {
        var _view_state = view.state, selection = _view_state.selection, tr = _view_state.tr, dispatch = view.dispatch;
        tableWrapper.setAttribute("style", "opacity: 0.9;");
        e.dataTransfer.setDragImage(tableWrapper, 0, 0);
        var mouseDown = view.mouseDown;
        mouseDown === null || mouseDown === void 0 ? void 0 : mouseDown.done();
        var _ref = findTable(selection) || {}, table = _ref.node, start = _ref.start;
        var newSelection = NodeSelection.create(view.state.doc, start - 1);
        var slice = newSelection.content();
        var map = TableMap.get(table).map;
        var anchorPos = map[0] + start;
        var headPos = map[map.length - 1] + start;
        var tableSelection = CellSelection.rowSelection(tr.doc.resolve(anchorPos), tr.doc.resolve(headPos));
        view.dragging = {
            slice: slice,
            move: !e[dragCopyModifier],
            tableSelection: tableSelection
        };
        dispatch(tr.setSelection(newSelection));
    };
    var _view_state = view.state, sel = _view_state.selection;
    var selected = _instanceof$a(sel, CellSelection) && sel.isWholeSelection();
    return /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-all-selectbar-container")),
        draggable: true,
        onDragStart: handleDragStart,
        onClick: handleClick
    }, /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-all-selectbar"))
    }, /*#__PURE__*/ React.createElement("div", {
        className: "btn".concat(selected ? " selected" : "")
    })));
};

function _classCallCheck$2(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var lastTableNode = null;
var TableLayoutView = /*#__PURE__*/ function() {
    function TableLayoutView(editorView, provider, eventEmitter) {
        var _this = this;
        _classCallCheck$2(this, TableLayoutView);
        /**
   * 此函数处理pluginview update不能覆盖的一个特殊场景，即比如图片加载完成导致的表格高度变化此时的选择条更新
   * 详见https://ones.sankuai.com/ones/product/1859/workItem/task/detail/21026880
   */ this.updateSelectBarWhenTableHeightChange = function() {
            var selection = _this.editorView.state.selection;
            var curCursorInTable = findTable(selection);
            if (curCursorInTable) {
                _this.displaySelectBar(curCursorInTable.pos, _this.editorView);
            }
        };
        this.hideSelectBar = function() {
            var colRowDom = document.querySelector(".".concat(classNamePrefix("table-col-selectbar-layout"), ".active"));
            colRowDom === null || colRowDom === void 0 ? void 0 : colRowDom.classList.remove("active");
            var rowColDom = document.querySelector(".".concat(classNamePrefix("table-row-selectbar-layout"), ".active"));
            rowColDom === null || rowColDom === void 0 ? void 0 : rowColDom.classList.remove("active");
            var allDom = document.querySelector(".".concat(classNamePrefix("table-all-selectbar-layout"), ".active"));
            allDom === null || allDom === void 0 ? void 0 : allDom.classList.remove("active");
            colRowDom && render(null, colRowDom);
            rowColDom && render(null, rowColDom);
            allDom && render(null, allDom);
        };
        this.editorView = editorView;
        this.provider = provider;
        this.eventEmitter = eventEmitter;
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.on(TABLE_HEIGHT_CHANGE, this.updateSelectBarWhenTableHeightChange);
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.on(TABLE_COLUMN_RESIZE, this.hideSelectBar);
    }
    var _proto = TableLayoutView.prototype;
    _proto.update = function update(view, prevState) {
        var selection = view.state.selection;
        var curCursorInTable = findTable(selection);
        var prevCursorInTable = findTable(prevState.selection);
        var nodeDom = curCursorInTable && view.nodeDOM(curCursorInTable.pos);
        if (!curCursorInTable) {
            if (this.selectBarDisplayed()) {
                this.hideSelectBar();
            }
            lastTableNode = null;
            return false;
        }
        if (!this.selectBarDisplayed() || !prevCursorInTable || !lastTableNode || // dataDiffId 废弃，值可能为 null，后续直接用 node 比较，看下面那个 if 是用来替代这个的
        curCursorInTable.node.attrs.dataDiffId !== prevCursorInTable.node.attrs.dataDiffId) {
            this.hideSelectBar();
            this.displaySelectBar(curCursorInTable.pos, view);
            lastTableNode = nodeDom;
        } else if (curCursorInTable.node !== prevCursorInTable.node) {
            if (nodeDom !== lastTableNode) {
                // 先隐藏再展示，防止抖动
                this.hideSelectBar();
                this.displaySelectBar(curCursorInTable.pos, view);
                lastTableNode = nodeDom;
            }
        }
    };
    _proto.selectBarDisplayed = function selectBarDisplayed() {
        var rowColDom = document.querySelector(".".concat(classNamePrefix("table-col-selectbar-layout"), ".active"));
        var colRowDom = document.querySelector(".".concat(classNamePrefix("table-row-selectbar-layout"), ".active"));
        var allDom = document.querySelector(".".concat(classNamePrefix("table-all-selectbar")));
        return rowColDom || colRowDom || allDom;
    };
    _proto.displaySelectBar = function displaySelectBar(pos, view) {
        var tableWrapper = view.nodeDOM(pos);
        var activateSelectBarContainer = function activateSelectBarContainer(selectbarType) {
            var dom = tableWrapper.querySelector(":scope > .mounted-container > .".concat(classNamePrefix("table-".concat(selectbarType, "-selectbar-layout"))));
            // query不到dom，直接return
            if (!dom) return null;
            dom.classList.add("active");
            return dom;
        };
        // pos 作为 key，否则 component 仅 update，数据会异常s
        var colSelectBarContainer = activateSelectBarContainer("col");
        var rowSelectBarContainer = activateSelectBarContainer("row");
        var allSelectBarContainer = activateSelectBarContainer("all");
        render(/*#__PURE__*/ React.createElement(ColSelectBar, {
            key: pos,
            pos: pos,
            view: view,
            eventEmitter: this.eventEmitter
        }), colSelectBarContainer);
        render(/*#__PURE__*/ React.createElement(RowSelectBar, {
            key: pos,
            pos: pos,
            view: view
        }), rowSelectBarContainer);
        render(/*#__PURE__*/ React.createElement(AllSelectBar, {
            key: pos,
            pos: pos,
            view: view
        }), allSelectBarContainer);
    };
    _proto.destroy = function destroy() {
        var eventEmitter = this.eventEmitter;
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.off(TABLE_HEIGHT_CHANGE, this.updateSelectBarWhenTableHeightChange);
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.off(TABLE_COLUMN_RESIZE, this.hideSelectBar);
        this.hideSelectBar();
    };
    return TableLayoutView;
}();

var css_248z$1 = ".ct-table-row-selectbar-container{position:absolute;z-index:3}.ct-table-row-selectbar-container .ct-table-row-selectbar{height:100%;width:26px}.ct-table-row-selectbar-container .ct-table-row-selectbar-item{cursor:pointer}.ct-table-row-selectbar-container .ct-table-row-selectbar-item .visible-area{padding-left:8px;width:100%;height:100%}.ct-table-row-selectbar-container .ct-table-row-selectbar-item .visible-area.bottom .btn{border-bottom:1px solid #ddd}.ct-table-row-selectbar-container .ct-table-row-selectbar-item .visible-area .btn{height:100%;width:10px;background-color:#eee}.ct-table-row-selectbar-container .ct-table-row-selectbar-item:last-child .visible-area.bottom .btn{border-bottom:none}.ct-table-row-selectbar-container .ct-table-row-selectbar-item:hover .btn{background-color:#ccc}.ct-table-row-selectbar-container .ct-table-row-selectbar-item.selected .visible-area .btn{background-color:rgba(0,167,205,.2)}.ct-table-all-selectbar-container{position:absolute;z-index:3}.ct-table-all-selectbar-container .ct-table-all-selectbar{height:16px;width:16px;cursor:-webkit-grab;cursor:grab}.ct-table-all-selectbar-container .ct-table-all-selectbar .btn{width:10px;height:10px;border-radius:100%;background-color:#eee}.ct-table-all-selectbar-container .ct-table-all-selectbar .btn.selected{background-color:rgba(0,167,205,.2)}.ct-table-col-selectbar-container{overflow:hidden;z-index:3;height:45px}.ct-table-col-selectbar-container .ct-table-col-selectbar{height:26px;margin-top:19px;display:flex;overflow:hidden}.ct-table-col-selectbar-container .ct-table-col-selectbar-item{float:left;cursor:pointer;pointer-events:auto}.ct-table-col-selectbar-container .ct-table-col-selectbar-item .visible-area{padding-top:8px;width:100%;height:100%}.ct-table-col-selectbar-container .ct-table-col-selectbar-item .visible-area.right .btn{border-right:1px solid #ddd}.ct-table-col-selectbar-container .ct-table-col-selectbar-item .visible-area .btn{height:10px;width:100%;background-color:#eee}.ct-table-col-selectbar-container .ct-table-col-selectbar-item:last-child .visible-area.bottom .btn{border-right:none}.ct-table-col-selectbar-container .ct-table-col-selectbar-item:hover .btn{background-color:#ccc}.ct-table-col-selectbar-container .ct-table-col-selectbar-item.selected .visible-area .btn{background-color:rgba(0,167,205,.2)}";
styleInject(css_248z$1);

function _defineProperty$7(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$7(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$7(target, key, source[key]);
        });
    }
    return target;
}
function tableLayout(provider, param) {
    var editorEventEmitter = param.editorEventEmitter;
    return new Plugin({
        key: tableLayoutKey,
        state: {
            init: function init() {
                return {
                    tableInfo: null,
                    tableLayoutKey: false
                };
            },
            apply: function apply(tr, value) {
                var newValue = tr.getMeta(this);
                return _objectSpread$7({}, value, newValue);
            }
        },
        view: function view(editorView) {
            return new TableLayoutView(editorView, provider, editorEventEmitter);
        }
    });
}

var ExtensionType;
(function(ExtensionType) {
    ExtensionType["Node"] = "node";
    ExtensionType["Mark"] = "mark";
    ExtensionType["NodeEmbed"] = "node-embed";
})(ExtensionType || (ExtensionType = {}));

/**
 * 通用配置
 */ var pluginKey = new PluginKey("image");

var isInTable = function($container) {
    var $outer = $container.parentNode;
    var tagName = $outer.tagName.toUpperCase();
    return tagName === "TD" || tagName === "TH";
};
// 判断选区或者光标是否在目标节点内
function isInTarget(state, targetNode) {
    var $anchor = state.selection.$anchor;
    for(var d = $anchor.depth; d > 0; d--){
        if ($anchor.node(d).eq(targetNode)) return true;
    }
    return false;
}

var css_248z$2 = ".ct-table-headerbar-container .ct-table-headerbar{position:fixed;z-index:500}.ct-table-headerbar-container .ct-table-headerbar .ct-table-wrapper{padding:0!important;margin:0!important;overflow:hidden}.ct-table-headerbar-container .ct-table-headerbar .ct-table-wrapper .column-resize-handler{display:none}.ct-table-headerbar-container * ::-moz-selection{background:rgba(46,170,220,.2)!important}.ct-table-headerbar-container * ::selection{background:rgba(46,170,220,.2)!important}";
styleInject(css_248z$2);

function _arrayLikeToArray$5(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$4(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterableToArrayLimit$4(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$4() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray$4(arr, i) {
    return _arrayWithHoles$4(arr) || _iterableToArrayLimit$4(arr, i) || _unsupportedIterableToArray$5(arr, i) || _nonIterableRest$4();
}
function _unsupportedIterableToArray$5(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$5(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen);
}
var HeaderBar = function(param) {
    var $tableWrapper = param.$tableWrapper, provider = param.provider, view = param.view;
    // 存
    var containerRef = useRef(null);
    var tableDOMRef = useRef(null);
    var tbodyDOMRef = useRef(null);
    var firstRowDOMRef = useRef(null);
    // 初始化相关 DOM
    useEffect(function() {
        var $table = $tableWrapper.querySelector("table");
        tableDOMRef.current = $table;
        var $tbody = $table.querySelector("tbody");
        tbodyDOMRef.current = $tbody;
        var $firstRow = $tbody && $tbody.querySelector("tr");
        firstRowDOMRef.current = $firstRow;
    }, [
        $tableWrapper
    ]);
    // 同步表头位置
    var wrapperDOMRef = useRef(null);
    var syncPosition = function() {
        if (!containerRef.current) {
            return;
        }
        var topBias = provider.getTopBias();
        var left = tableDOMRef.current.getBoundingClientRect().left;
        var scrollLeft = $tableWrapper.scrollLeft;
        containerRef.current.style.left = "".concat(scrollLeft + left, "px");
        containerRef.current.style.top = "".concat(topBias, "px");
        containerRef.current.style.width = "".concat($tableWrapper.clientWidth, "px");
        if (wrapperDOMRef.current) {
            wrapperDOMRef.current.scrollLeft = scrollLeft;
        }
    };
    // 创建表头DOM元素
    var createHeaderDOMs = function() {
        if (!containerRef.current) {
            return;
        }
        var $table = tableDOMRef.current;
        var scrollLeft = $tableWrapper.scrollLeft;
        var headerBarTableDOM = $table.cloneNode();
        headerBarTableDOM.className = "";
        headerBarTableDOM.style.width = "".concat($table.clientWidth, "px");
        var headerColgroupDOM = $table.firstElementChild.cloneNode(true);
        var colDOMs = [];
        var rowDOM = $table.lastElementChild.children[0];
        for(var i = 0; i < rowDOM.childElementCount; i++){
            var cellDOM = rowDOM.children[i];
            var colspan = parseInt(cellDOM.getAttribute("colspan"), 10) || 1;
            var cellWidth = cellDOM.getBoundingClientRect().width / colspan;
            for(var j = 0; j < colspan; j++){
                colDOMs.push({
                    cellWidth: cellWidth
                });
            }
        }
        if (headerColgroupDOM.childNodes.length === 0) {
            colDOMs.forEach(function(col) {
                var cellWidth = col.cellWidth || parseInt(col.style && col.style.width, 10);
                headerColgroupDOM.appendChild(document.createElement("col")).style.width = "".concat(cellWidth, "px");
            });
        }
        var headerTbodyDOM = document.createElement("tbody");
        var wrapperDOM = document.createElement("div");
        wrapperDOM.className = classNamePrefix("table-wrapper");
        var $firstRow = firstRowDOMRef.current;
        var tableHeader = $firstRow.cloneNode(true);
        headerTbodyDOM.appendChild(tableHeader);
        headerBarTableDOM.appendChild(headerColgroupDOM);
        headerBarTableDOM.appendChild(headerTbodyDOM);
        wrapperDOM.appendChild(headerBarTableDOM);
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(wrapperDOM);
        wrapperDOM.scrollLeft = scrollLeft;
        wrapperDOMRef.current = wrapperDOM;
    };
    var _useState = _slicedToArray$4(useState({
        left: 0,
        topBias: 0,
        tableWidth: 0,
        tableWrapperWidth: 0
    }), 2), sizes = _useState[0], setSizes = _useState[1];
    var topBias = sizes.topBias, left = sizes.left, tableWrapperWidth = sizes.tableWrapperWidth, tableWidth = sizes.tableWidth;
    // 监听位置相关参数的变化 执行同步位置
    var isMountedRef = useRef(false);
    useEffect(function() {
        var animationId = null;
        // 更新的时候才执行
        if (isMountedRef.current) {
            animationId = window.requestAnimationFrame(function() {
                syncPosition();
            });
        } else {
            isMountedRef.current = true;
        }
        return function() {
            window.cancelAnimationFrame(animationId);
        };
    }, [
        topBias,
        left,
        tableWrapperWidth
    ]);
    // 通过监听宽度,判断增删列的情况进行重新创建 DOM, 初始化时会执行一次
    useEffect(function() {
        var animationId = window.requestAnimationFrame(function() {
            syncPosition();
            createHeaderDOMs();
        });
        return function() {
            window.cancelAnimationFrame(animationId);
        };
    }, [
        tableWidth
    ]);
    // 监听表格左右滚动 同步位置
    useEffect(function() {
        var animationId = null;
        var handleScroll = function() {
            if (animationId) {
                window.cancelAnimationFrame(animationId);
            }
            animationId = window.requestAnimationFrame(function() {
                syncPosition();
            });
        };
        $tableWrapper.addEventListener("scroll", handleScroll);
        return function() {
            $tableWrapper.removeEventListener("scroll", handleScroll);
        };
    }, []);
    // 同步相关位置参数
    var refresh = function() {
        var _tableDOMRef_current_getBoundingClientRect, _tableDOMRef_current;
        setSizes({
            left: (_tableDOMRef_current_getBoundingClientRect = (_tableDOMRef_current = tableDOMRef.current) === null || _tableDOMRef_current === void 0 ? void 0 : _tableDOMRef_current.getBoundingClientRect()) === null || _tableDOMRef_current_getBoundingClientRect === void 0 ? void 0 : _tableDOMRef_current_getBoundingClientRect.left,
            topBias: provider.getTopBias(),
            tableWidth: tableDOMRef.current.clientWidth,
            tableWrapperWidth: $tableWrapper === null || $tableWrapper === void 0 ? void 0 : $tableWrapper.clientWidth
        });
    };
    // 隔一定时间检测 同步相关位置参数
    useEffect(function() {
        refresh();
        var refreshTimer = setInterval(refresh, 50);
        return function() {
            clearInterval(refreshTimer);
            refreshTimer = null;
        };
    }, []);
    var handleDoubleClick = function(e) {
        var headerDOM = e.target.closest("th");
        if (headerDOM) {
            var state = view.state, dispatch = view.dispatch;
            var tr = state.tr;
            var index = Array.from(headerDOM.parentElement.children).indexOf(headerDOM);
            var trueDOM = tableDOMRef.current.lastElementChild.firstElementChild.cells[index];
            var pos = view.posAtDOM(trueDOM, null);
            if (provider.getTopBias && provider.getScrollWrapper) {
                provider.getScrollWrapper().scrollTop -= provider.getTopBias() - trueDOM.getBoundingClientRect().top;
            }
            dispatch(tr.setSelection(TextSelection.create(state.tr.doc, pos - 1)));
            view.focus();
        }
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-headerbar")),
        ref: containerRef,
        onDoubleClick: handleDoubleClick
    });
};

function _classCallCheck$3(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var HEADER_BAR_UPDATE_DEBOUNCE_TIME = 16;
var StickyHeaderView = /*#__PURE__*/ function() {
    function StickyHeaderView(editorView, provider, eventEmitter) {
        var _this = this;
        _classCallCheck$3(this, StickyHeaderView);
        // 解决表头高度超过一页时无法滚动的问题
        this.handleMouseWheel = function(e) {
            var isInCloneHeader = e.target.closest(".".concat(classNamePrefix("table-headerbar")));
            if (!isInCloneHeader) return;
            var direct = 0;
            if (e.wheelDelta) {
                direct = e.wheelDelta > 0 ? 1 : -1;
            } else if (e.detail) {
                direct = e.detail > 0 ? 1 : -1;
            }
            if (direct !== 0) {
                if (direct === 1) {
                    _this.scrollWrapper.scrollTop -= 7;
                } else {
                    _this.scrollWrapper.scrollTop += 7;
                }
            }
        };
        this.handlePageScroll = function() {
            var _this_eventEmitter;
            (_this_eventEmitter = _this.eventEmitter) === null || _this_eventEmitter === void 0 ? void 0 : _this_eventEmitter.emit(SYNC_PAGE_SCROLL);
            _this.updateHeaderBar();
        };
        this.findTableNode = function() {
            var res = null;
            var _this_view_state = _this.view.state, doc = _this_view_state.doc, _this_view_state_schema = _this_view_state.schema, table = _this_view_state_schema.nodes.table;
            var topBias = _this.provider.getTopBias();
            doc.descendants(function(item, pos) {
                if (item.type === table) {
                    var $tableWrapper = _this.view.nodeDOM(pos);
                    if (!$tableWrapper) return;
                    var tableClientRect = $tableWrapper.getBoundingClientRect();
                    var $firstRow = $tableWrapper.querySelector("tr");
                    if (!$firstRow) return;
                    if (hasRowHeader(item) && tableClientRect.top < topBias && tableClientRect.bottom > topBias + $firstRow.clientHeight) {
                        // 嵌套在表格里的表格只有光标在内部的时候才会有浮动表头
                        if (isInTable($tableWrapper)) {
                            if (isInTarget(_this.view.state, item)) {
                                res = {
                                    node: item,
                                    pos: pos,
                                    $tableWrapper: $tableWrapper
                                };
                            }
                        } else {
                            res = {
                                node: item,
                                pos: pos,
                                $tableWrapper: $tableWrapper
                            };
                        }
                    }
                }
            });
            return res;
        };
        this.showHeaderBar = function(param) {
            var $tableWrapper = param.$tableWrapper;
            render(/*#__PURE__*/ React.createElement(HeaderBar, {
                $tableWrapper: $tableWrapper,
                provider: _this.provider,
                view: _this.view
            }), document.querySelector(".".concat(classNamePrefix("table-headerbar-container"))));
        };
        this.hideHeaderBar = function() {
            var container = document.querySelector(".".concat(classNamePrefix("table-headerbar-container")));
            if (container) {
                render(null, container);
            }
        };
        this.updateHeaderBar = throttle(function() {
            var curTableInfo = _this.findTableNode();
            if (curTableInfo) {
                if (!_this.tableInfo || !_this.tableInfo.node.eq(curTableInfo.node)) {
                    _this.showHeaderBar(curTableInfo);
                }
            }
            if (!curTableInfo && _this.tableInfo) {
                _this.hideHeaderBar();
            }
            _this.tableInfo = curTableInfo;
        }, HEADER_BAR_UPDATE_DEBOUNCE_TIME);
        this.view = editorView;
        this.provider = provider;
        this.eventEmitter = eventEmitter;
        this.headerBar = document.createElement("div");
        this.headerBar.className = classNamePrefix("table-headerbar-container");
        this.view.dom.parentNode.insertBefore(this.headerBar, this.view.dom);
        this.scrollWrapper = this.provider.getScrollWrapper();
        if (this.scrollWrapper) {
            this.scrollWrapper.addEventListener("scroll", this.handlePageScroll);
            this.scrollWrapper.addEventListener("mousewheel", this.handleMouseWheel);
            // 嵌套表格点击切换触发
            document.addEventListener("click", this.updateHeaderBar);
        }
    }
    var _proto = StickyHeaderView.prototype;
    _proto.destroy = function destroy() {
        var _this_updateHeaderBar, _this_updateHeaderBar_cancel;
        this.hideHeaderBar();
        (_this_updateHeaderBar = this.updateHeaderBar) === null || _this_updateHeaderBar === void 0 ? void 0 : (_this_updateHeaderBar_cancel = _this_updateHeaderBar.cancel) === null || _this_updateHeaderBar_cancel === void 0 ? void 0 : _this_updateHeaderBar_cancel.call(_this_updateHeaderBar);
        if (this.scrollWrapper) {
            this.scrollWrapper.removeEventListener("scroll", this.handlePageScroll);
            this.scrollWrapper.removeEventListener("mousewheel", this.handleMouseWheel);
            document.removeEventListener("click", this.updateHeaderBar);
        }
    };
    return StickyHeaderView;
}();

function stickyHeader(provider, param) {
    var editorEventEmitter = param.editorEventEmitter;
    return new Plugin({
        key: stickyHeaderKey,
        props: {},
        view: function view(editorView) {
            return new StickyHeaderView(editorView, provider, editorEventEmitter);
        }
    });
}

var css_248z$3 = ".ct-table-scrollbar-container{position:fixed;z-index:19}.ct-table-scrollbar-container .ct-table-scrollbar{height:15px;overflow-x:auto;overflow-y:hidden}.ct-table-scrollbar-container .ct-table-scrollbar .inner{border:1px solid transparent}";
styleInject(css_248z$3);

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$4(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var ScrollBar = /*#__PURE__*/ function(Component) {
    _inherits(ScrollBar, Component);
    var _super = _createSuper(ScrollBar);
    function ScrollBar() {
        _classCallCheck$4(this, ScrollBar);
        var _this;
        _this = _super.apply(this, arguments);
        _this.handleScroll = function() {
            var $tableWrapper = _this.props.$tableWrapper;
            var scrollLeft = _this.scrollBar.scrollLeft;
            if ($tableWrapper.scrollLeft !== scrollLeft) {
                $tableWrapper.scrollLeft = scrollLeft;
            }
        };
        _this.handleTableScroll = function() {
            var scrollBar = _assertThisInitialized(_this).scrollBar;
            var $tableWrapper = _this.props.$tableWrapper;
            var scrollLeft = $tableWrapper.scrollLeft;
            if (scrollBar.scrollLeft !== scrollLeft) {
                scrollBar.scrollLeft = scrollLeft;
            }
        };
        return _this;
    }
    var _proto = ScrollBar.prototype;
    _proto.componentDidMount = function componentDidMount() {
        var $tableWrapper = this.props.$tableWrapper;
        this.scrollBar.scrollLeft = $tableWrapper.scrollLeft;
        this.scrollBar.onscroll = this.handleScroll;
        $tableWrapper.addEventListener("scroll", this.handleTableScroll);
    };
    _proto.componentWillUnmount = function componentWillUnmount() {
        this.scrollBar.onscroll = null;
        var $tableWrapper = this.props.$tableWrapper;
        $tableWrapper.removeEventListener("scroll", this.handleTableScroll);
    };
    _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
        var $tableWrapper = this.props.$tableWrapper;
        var scrollLeft = $tableWrapper.scrollLeft;
        if (this.scrollBar.scrollLeft !== scrollLeft) {
            this.scrollBar.scrollLeft = scrollLeft;
        }
        if (prevProps.$tableWrapper !== $tableWrapper) {
            var // fix: Cannot read properties of null (reading 'scrollLeft')
            // @see https://km.sankuai.com/collabpage/1353433541
            _prevProps_$tableWrapper;
            (_prevProps_$tableWrapper = prevProps.$tableWrapper) === null || _prevProps_$tableWrapper === void 0 ? void 0 : _prevProps_$tableWrapper.removeEventListener("scroll", this.handleTableScroll);
            $tableWrapper.addEventListener("scroll", this.handleTableScroll);
        }
    };
    _proto.render = function render() {
        var _this = this;
        var $tableWrapper = this.props.$tableWrapper;
        var containerWidth = $tableWrapper.clientWidth;
        var tableWidth = $tableWrapper.querySelector("table").getBoundingClientRect().width;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNamePrefix("table-scrollbar"),
            style: {
                width: "".concat(containerWidth, "px")
            },
            ref: function(refs) {
                _this.scrollBar = refs;
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: "inner",
            style: {
                width: "".concat(tableWidth + 2, "px")
            }
        }));
    };
    return ScrollBar;
}(Component);

function _classCallCheck$5(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var SCROLL_BAR_UPDATE_DEBOUNCE_TIME = 16;
var ScrollBarView = /*#__PURE__*/ function() {
    function ScrollBarView(editorView, provider) {
        var _this = this;
        _classCallCheck$5(this, ScrollBarView);
        this.updateScrollBar = throttle(function() {
            var _this_view_dom, _this_view_dom_parentNode, _this_view_dom_parentNode_querySelectorAll;
            var tableDOMs = Array.from(((_this_view_dom = _this.view.dom) === null || _this_view_dom === void 0 ? void 0 : (_this_view_dom_parentNode = _this_view_dom.parentNode) === null || _this_view_dom_parentNode === void 0 ? void 0 : (_this_view_dom_parentNode_querySelectorAll = _this_view_dom_parentNode.querySelectorAll) === null || _this_view_dom_parentNode_querySelectorAll === void 0 ? void 0 : _this_view_dom_parentNode_querySelectorAll.call(_this_view_dom_parentNode, ".".concat(classNamePrefix("table-wrapper")))) || []).filter(function(tableDOM) {
                return !tableDOM.closest(".".concat(classNamePrefix("collapse"), ":not(.active)"));
            });
            var res = tableDOMs.some(function(tableDOM) {
                return _this.toggleScrollBar(tableDOM);
            });
            if (!res) {
                _this.hideScrollBar();
            }
        }, SCROLL_BAR_UPDATE_DEBOUNCE_TIME);
        this.toggleScrollBar = function($tableWrapper) {
            var _$tableWrapper_querySelector, _$tableWrapper_querySelector_getBoundingClientRect;
            var bottomBias = _this.provider.getBottomBias ? _this.provider.getBottomBias() : -20;
            var isOutTable = $tableWrapper && !$tableWrapper.closest("table");
            if (!isOutTable) return false;
            var tableRect = (_$tableWrapper_querySelector = $tableWrapper.querySelector("table")) === null || _$tableWrapper_querySelector === void 0 ? void 0 : (_$tableWrapper_querySelector_getBoundingClientRect = _$tableWrapper_querySelector.getBoundingClientRect) === null || _$tableWrapper_querySelector_getBoundingClientRect === void 0 ? void 0 : _$tableWrapper_querySelector_getBoundingClientRect.call(_$tableWrapper_querySelector);
            if (!tableRect) {
                return false;
            }
            var tableLeft = tableRect.left + $tableWrapper.scrollLeft;
            var tableBottom = window.innerHeight - tableRect.bottom;
            var containerWidth = $tableWrapper.clientWidth;
            var tableWidth = tableRect.width;
            // 满足滚动条件
            if (containerWidth < tableWidth && tableRect.top + bottomBias + 10 < window.innerHeight && tableBottom < bottomBias + 10) {
                var $container = document.querySelector(".".concat(classNamePrefix("table-scrollbar-container")));
                $container.style.left = "".concat(tableLeft, "px");
                $container.style.bottom = "".concat(bottomBias, "px");
                _this.$tableWrapper = $tableWrapper;
                _this.tableWidth = tableWidth;
                _this.showScrollBar($tableWrapper);
                return true;
            }
            return false;
        };
        this.showScrollBar = function($tableWrapper) {
            render(/*#__PURE__*/ React.createElement(ScrollBar, {
                $tableWrapper: $tableWrapper,
                provider: _this.provider,
                view: _this.view
            }), document.querySelector(".".concat(classNamePrefix("table-scrollbar-container"))));
        };
        this.hideScrollBar = function() {
            _this.$tableWrapper = null;
            _this.tableWidth = 0;
            var container = document.querySelector(".".concat(classNamePrefix("table-scrollbar-container")));
            if (container) {
                render(null, container);
            }
        };
        this.view = editorView;
        this.provider = provider;
        this.scrollBar = document.createElement("div");
        this.scrollBar.className = classNamePrefix("table-scrollbar-container");
        this.view.dom.parentNode.insertBefore(this.scrollBar, this.view.dom);
        this.scrollWrapper = this.provider.getScrollWrapper();
        this.updateScrollBar();
        if (this.scrollWrapper) {
            this.scrollWrapper.addEventListener("scroll", this.updateScrollBar);
            // 嵌套表格点击切换触发
            document.addEventListener("click", this.updateScrollBar);
        }
    }
    var _proto = ScrollBarView.prototype;
    _proto.update = function update() {
        if (this.$tableWrapper && this.$tableWrapper.querySelector("table").getBoundingClientRect().width !== this.tableWidth) {
            this.updateScrollBar();
        }
    };
    _proto.destroy = function destroy() {
        var _this_updateScrollBar, _this_updateScrollBar_cancel;
        this.hideScrollBar();
        (_this_updateScrollBar = this.updateScrollBar) === null || _this_updateScrollBar === void 0 ? void 0 : (_this_updateScrollBar_cancel = _this_updateScrollBar.cancel) === null || _this_updateScrollBar_cancel === void 0 ? void 0 : _this_updateScrollBar_cancel.call(_this_updateScrollBar);
        if (this.scrollWrapper) {
            this.scrollWrapper.removeEventListener("scroll", this.updateScrollBar);
            document.removeEventListener("click", this.updateScrollBar);
        }
    };
    return ScrollBarView;
}();

/**
 * 横向滚动条插件，在 windows 或接入鼠标的情况下，方便将表格横向滚动
 */ function scrollBar(provider) {
    return new Plugin({
        key: scrollBarKey,
        props: {},
        view: function view(editorView) {
            return new ScrollBarView(editorView, provider);
        }
    });
}

var locale = "en";
var version = 16;
var cannot_search_user = "User not found, click space to complete input";
var load_fail_retry = "Failed to load, please try again later";
var pk_avatar = "avatar";
var mention_by_mis_name = "Can @user by name and MIS number";
var searching = "Searching...";
var pk_link_text = "Text";
var insert_blank = "Insert";
var pk_visit_link = "Access the link";
var pk_link_address = "Address";
var pk_link = "Link";
var cancel_blank = "Cancel";
var pk_link_change_tip = "Link text follows the Doc change";
var pk_edit_link = "Edit link";
var pk_link_text_tip = "Input the link text";
var pk_cancel_link = "Cancel link";
var save_blank = "Save";
var pk_link_address_tip = "Input the link address";
var border = "Border";
var pk_insert = "Insert";
var image_link = "Image Link";
var pk_retry = "Retry";
var pk_edit = "Edit";
var width = "Width";
var reupload = "Reupload";
var image_upload_fail1 = "Sorry, upload error!";
var right_address_tip = "Please input right address";
var image_upload_fail3 = "Otherwise, please click button below to retry.";
var image_upload_fail2 = "If the image is moved/pasted from outside, please download and reupload.";
var pk_confirm = "Confirm";
var input_html = "Enter HTML code.";
var formual = "Formual";
var pk_loading = "Loading...";
var click_to_expand = "Click to expand the content";
var pk_cancel = "Cancel";
var input_html_tip = "You can insert HTML code to embed corresponding content on a webpage.";
var input_latex = "Enter Latex code";
var insert_latex_tip = "You can insert Latex code to embed corresponding content within the page.";
var pk_title = "Title";
var wire_frame = "Wireframe";
var fill_in = "Fill";
var input_plantuml = "Enter PlantUML code.";
var border_style = "Border type";
var set_th_line = "Header Column";
var set_th_row = "Header Row";
var table_attr = "Table style";
var cancel_th_line = "Cancel Header Column";
var insert_line_right = "Right Insert Column";
var insert_row = "Delete Rows";
var split_cell = "Split cells";
var delete_line = "Delete Columns";
var pk_no = "No borders";
var input_markdown_tip = "You can insert Markdown code to embed corresponding content within the page.";
var confirm_blank = "Confirm";
var cancel_th_row = "Cancel Header Row";
var insert_row_above = "Insert Row Above";
var delete_table = "Delete";
var input_plantuml_tip = "You can insert PlantUML code to embed corresponding content within the page.";
var border_width = "Border width";
var input_markdown = "Enter Markdown code.";
var merge_cell = "Merge cells";
var set_line_num = "Show Row Number";
var border_color = "Border color";
var insert_line_left = "Left Insert Column";
var insert_row_below = "Insert Row Below";
var cancel_line_num = "Cancel Row Number";
var exit_data_lost_tip = "If you exit, any unsaved data will be lost.";
var if_exit_gantt = "Are you sure to exit the Gantt chart?";
var pk_gantt_loading = "Loading Gantt chart...";
var gantt_load_fail = "Gantt chart loading failed.";
var operate_error = "Invalid operation";
var gantt_keep_work_tip = "Please keep at least one task.";
var save_success = "Save successful";
var gantt_frame_load_fail = "Gantt chart framework failed to load.";
var pk_network_exception = "Network exception";
var save_fail = "Save failed";
var pk_saving = "Saving...";
var gantt_plugin_fail = "Gantt chart editing plugin failed to start.";
var gantt_editing_tip = "Editing Gantt Chart, please wait a moment.";
var pk_discard = "Drop";
var pk_zoom_in = "Zoom in";
var pk_zoom_out = "Zoom out";
var pk_use = "Use";
var draft_edit_tip = "There is a draft that was edited but not saved on {time}. Do you want to continue editing?";
var if_use_local_draft = "Do you use local drafts?";
var pk_load_fail = "Loading failed";
var pk_this_time = "Now";
var choose_time = "Select time";
var pk_hh_mm_ss = "hh:mm:ss A";
var pk_y_d = "MM-DD-YYYY";
var pk_y_d_ts = "MM-DD-YYYY hh:mm:ss A";
var pk_text = "Text";
var h1 = "Heading 1";
var h2 = "Heading 2";
var h3 = "Heading 3";
var h4 = "Heading 4";
var h5 = "Heading 5";
var h6 = "Heading 6";
var pk_catalog = "Catalog";
var page_view_load_fail_click = "Directory tree loading failed. Please click to reload.";
var page_tree_no_sub_doc = "No sub documents found in this directory";
var page_tree_no_perm = "No permission to view the directory tree.";
var pk_load_fail_retry = "Loading failed, please click to reload.";
var loading2 = "Loading";
var page_tree_deleted = "The directory tree has been deleted.";
var pk_pause = "Pause";
var pk_mute = "Mute";
var pk_untitled = "Untitled";
var pk_play = "Play";
var adjust_volume = "Adjust volume";
var download_fail_retry = "Download failed please retry";
var pk_download = "Download";
var get_file_fail = "Failed to get file";
var pk_network_fail = "Network connection failed";
var pk_upload_fail = "Upload failed";
var audio_not_support_tip = "This browser does not support the HTML5 audio tag.";
var pk_volume = "Volume";
var browser_not_support_tip = "This browser does not support the HTML5 video tag.";
var pk_zoom_in2 = "Zoom in";
var video_upload_tip = "Video is being transcoded, if the file is large, it may take a long time. Please wait patiently...";
var pk_video = "Video";
var pk_zoom_out2 = "Zoom out";
var space_update_fail_tip = "Space update acquisition failed. Click to retry.";
var pk_recent_update = "Recent Updates";
var show_more = "Show More";
var no_space_update = "No Updates in Space.";
var pk_restore = "Restore";
var en_parker = {
    locale: locale,
    version: version,
    cannot_search_user: cannot_search_user,
    load_fail_retry: load_fail_retry,
    pk_avatar: pk_avatar,
    mention_by_mis_name: mention_by_mis_name,
    searching: searching,
    pk_link_text: pk_link_text,
    insert_blank: insert_blank,
    pk_visit_link: pk_visit_link,
    pk_link_address: pk_link_address,
    pk_link: pk_link,
    cancel_blank: cancel_blank,
    pk_link_change_tip: pk_link_change_tip,
    pk_edit_link: pk_edit_link,
    pk_link_text_tip: pk_link_text_tip,
    pk_cancel_link: pk_cancel_link,
    save_blank: save_blank,
    pk_link_address_tip: pk_link_address_tip,
    border: border,
    pk_insert: pk_insert,
    image_link: image_link,
    pk_retry: pk_retry,
    pk_edit: pk_edit,
    width: width,
    reupload: reupload,
    image_upload_fail1: image_upload_fail1,
    right_address_tip: right_address_tip,
    image_upload_fail3: image_upload_fail3,
    image_upload_fail2: image_upload_fail2,
    pk_confirm: pk_confirm,
    input_html: input_html,
    formual: formual,
    pk_loading: pk_loading,
    click_to_expand: click_to_expand,
    pk_cancel: pk_cancel,
    input_html_tip: input_html_tip,
    input_latex: input_latex,
    insert_latex_tip: insert_latex_tip,
    pk_title: pk_title,
    wire_frame: wire_frame,
    fill_in: fill_in,
    input_plantuml: input_plantuml,
    border_style: border_style,
    set_th_line: set_th_line,
    set_th_row: set_th_row,
    table_attr: table_attr,
    cancel_th_line: cancel_th_line,
    insert_line_right: insert_line_right,
    insert_row: insert_row,
    split_cell: split_cell,
    delete_line: delete_line,
    pk_no: pk_no,
    input_markdown_tip: input_markdown_tip,
    confirm_blank: confirm_blank,
    cancel_th_row: cancel_th_row,
    insert_row_above: insert_row_above,
    delete_table: delete_table,
    input_plantuml_tip: input_plantuml_tip,
    border_width: border_width,
    input_markdown: input_markdown,
    merge_cell: merge_cell,
    set_line_num: set_line_num,
    border_color: border_color,
    insert_line_left: insert_line_left,
    insert_row_below: insert_row_below,
    cancel_line_num: cancel_line_num,
    exit_data_lost_tip: exit_data_lost_tip,
    if_exit_gantt: if_exit_gantt,
    pk_gantt_loading: pk_gantt_loading,
    gantt_load_fail: gantt_load_fail,
    operate_error: operate_error,
    gantt_keep_work_tip: gantt_keep_work_tip,
    save_success: save_success,
    gantt_frame_load_fail: gantt_frame_load_fail,
    pk_network_exception: pk_network_exception,
    save_fail: save_fail,
    pk_saving: pk_saving,
    gantt_plugin_fail: gantt_plugin_fail,
    gantt_editing_tip: gantt_editing_tip,
    pk_discard: pk_discard,
    pk_zoom_in: pk_zoom_in,
    pk_zoom_out: pk_zoom_out,
    pk_use: pk_use,
    draft_edit_tip: draft_edit_tip,
    if_use_local_draft: if_use_local_draft,
    pk_load_fail: pk_load_fail,
    pk_this_time: pk_this_time,
    choose_time: choose_time,
    pk_hh_mm_ss: pk_hh_mm_ss,
    pk_y_d: pk_y_d,
    pk_y_d_ts: pk_y_d_ts,
    pk_text: pk_text,
    h1: h1,
    h2: h2,
    h3: h3,
    h4: h4,
    h5: h5,
    h6: h6,
    pk_catalog: pk_catalog,
    page_view_load_fail_click: page_view_load_fail_click,
    page_tree_no_sub_doc: page_tree_no_sub_doc,
    page_tree_no_perm: page_tree_no_perm,
    pk_load_fail_retry: pk_load_fail_retry,
    loading2: loading2,
    page_tree_deleted: page_tree_deleted,
    pk_pause: pk_pause,
    pk_mute: pk_mute,
    pk_untitled: pk_untitled,
    pk_play: pk_play,
    adjust_volume: adjust_volume,
    download_fail_retry: download_fail_retry,
    pk_download: pk_download,
    get_file_fail: get_file_fail,
    pk_network_fail: pk_network_fail,
    pk_upload_fail: pk_upload_fail,
    audio_not_support_tip: audio_not_support_tip,
    pk_volume: pk_volume,
    browser_not_support_tip: browser_not_support_tip,
    pk_zoom_in2: pk_zoom_in2,
    video_upload_tip: video_upload_tip,
    pk_video: pk_video,
    pk_zoom_out2: pk_zoom_out2,
    space_update_fail_tip: space_update_fail_tip,
    pk_recent_update: pk_recent_update,
    show_more: show_more,
    no_space_update: no_space_update,
    pk_restore: pk_restore
};

var locale$1 = "zh";
var version$1 = 16;
var cannot_search_user$1 = "未搜索到用户，点击空格完成输入";
var load_fail_retry$1 = "加载失败，请稍后重试";
var pk_avatar$1 = "头像";
var mention_by_mis_name$1 = "可通过姓名及mis号@用户";
var searching$1 = "搜索中...";
var pk_link_text$1 = "链接文字";
var insert_blank$1 = "插 入";
var pk_link$1 = "链接";
var pk_link_address$1 = "链接地址";
var pk_visit_link$1 = "访问链接";
var cancel_blank$1 = "取 消";
var pk_link_change_tip$1 = "链接文字跟随文档标题变更";
var pk_edit_link$1 = "编辑链接";
var pk_cancel_link$1 = "取消链接";
var pk_link_text_tip$1 = "请输入链接文字";
var save_blank$1 = "保 存";
var pk_link_address_tip$1 = "请输入链接地址";
var border$1 = "边框";
var pk_insert$1 = "插入";
var image_link$1 = "图片链接";
var pk_edit$1 = "编辑";
var pk_retry$1 = "重试";
var width$1 = "宽度";
var reupload$1 = "重新上传";
var image_upload_fail1$1 = "抱歉，图片上传失败！";
var image_upload_fail3$1 = "否则，请点击下方按钮进行重试。";
var right_address_tip$1 = "请输入正确的地址";
var image_upload_fail2$1 = "如果是从外部拖动、粘贴过来的图片，请下载原图并重新上传。";
var pk_confirm$1 = "确定";
var input_html$1 = "输入HTML代码";
var formual$1 = "公式";
var pk_loading$1 = "正在加载中...";
var pk_cancel$1 = "取消";
var click_to_expand$1 = "点击展开内容";
var input_html_tip$1 = "可以插入HTML代码，以在页面内嵌入相应内容：";
var insert_latex_tip$1 = "可以插入Latex代码，以在页面内嵌入相应内容：";
var input_latex$1 = "输入Latex代码";
var pk_title$1 = "标题";
var wire_frame$1 = "线框";
var fill_in$1 = "填充";
var input_plantuml$1 = "輸入PlantUML代碼";
var border_style$1 = "边框样式";
var set_th_row$1 = "设置表头行";
var set_th_line$1 = "设置表头列";
var table_attr$1 = "表格属性";
var cancel_th_line$1 = "取消表头列";
var insert_row$1 = "删除行";
var insert_line_right$1 = "右侧插入列";
var split_cell$1 = "拆分单元格";
var delete_line$1 = "删除列";
var confirm_blank$1 = "确 定";
var input_markdown_tip$1 = "可以插入Markdown代码，以在页面内嵌入相应内容：";
var pk_no$1 = "无";
var cancel_th_row$1 = "取消表头行";
var insert_row_above$1 = "上方插入行";
var delete_table$1 = "删除表格";
var input_plantuml_tip$1 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容";
var border_width$1 = "边框宽度";
var input_markdown$1 = "输入Markdown代码";
var merge_cell$1 = "合并单元格";
var set_line_num$1 = "设置行号列";
var border_color$1 = "边框颜色";
var insert_line_left$1 = "左侧插入列";
var insert_row_below$1 = "下方插入行";
var cancel_line_num$1 = "取消行号列";
var if_exit_gantt$1 = "确定退出甘特图?";
var exit_data_lost_tip$1 = "退出后，尚未保存的数据将丢失。";
var gantt_load_fail$1 = "甘特图载入失败";
var pk_gantt_loading$1 = "甘特图载入中...";
var operate_error$1 = "您的操作有误";
var gantt_keep_work_tip$1 = "请至少保留一个任务";
var gantt_frame_load_fail$1 = "甘特图框架加载失败";
var save_success$1 = "保存成功";
var save_fail$1 = "保存失败";
var pk_network_exception$1 = "网络异常";
var pk_saving$1 = "正在保存中...";
var gantt_plugin_fail$1 = "甘特图编辑插件启动失败";
var gantt_editing_tip$1 = "正在编辑甘特图，请稍候再试噢。";
var pk_discard$1 = "废弃";
var pk_zoom_in$1 = "放大查看";
var pk_zoom_out$1 = "缩小查看";
var pk_use$1 = "使用";
var draft_edit_tip$1 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继续编辑";
var pk_load_fail$1 = "加载失败";
var if_use_local_draft$1 = "是否使用本地草稿？";
var pk_this_time$1 = "此刻";
var choose_time$1 = "选择时间";
var pk_hh_mm_ss$1 = "HH:mm:ss";
var pk_y_d$1 = "YYYY-MM-DD";
var pk_y_d_ts$1 = "YYYY-MM-DD HH:mm:ss";
var pk_text$1 = "正文";
var h1$1 = "标题 1";
var h2$1 = "标题 2";
var h3$1 = "标题 3";
var h4$1 = "标题 4";
var h5$1 = "标题 5";
var h6$1 = "标题 6";
var pk_catalog$1 = "目录";
var page_view_load_fail_click$1 = "目录树加载失败，请点击重新加载";
var page_tree_no_sub_doc$1 = "该目录下暂无子文档";
var page_tree_no_perm$1 = "无权查看该目录树";
var pk_load_fail_retry$1 = "加载失败，请点击重新加载";
var loading2$1 = "正在加载";
var page_tree_deleted$1 = "该目录树已被删除";
var pk_pause$1 = "暂停";
var pk_mute$1 = "静音";
var pk_untitled$1 = "未命名";
var pk_play$1 = "播放";
var adjust_volume$1 = "音量调节";
var download_fail_retry$1 = "下载失败，请重试";
var get_file_fail$1 = "文件获取失败";
var pk_download$1 = "下载";
var pk_network_fail$1 = "网络连接失败";
var pk_upload_fail$1 = "上传失败";
var audio_not_support_tip$1 = "本浏览器不支持 HTML5 audio 标签";
var browser_not_support_tip$1 = "本浏览器不支持 HTML5 video 标签";
var pk_volume$1 = "音量";
var pk_zoom_in2$1 = "放大";
var pk_video$1 = "视频";
var video_upload_tip$1 = "视频正在转码中，如文件较大可能时间较长，请耐心等待…";
var pk_zoom_out2$1 = "缩小";
var space_update_fail_tip$1 = "空间更新获取失败，点击重试";
var pk_recent_update$1 = "最近更新";
var show_more$1 = "显示更多";
var no_space_update$1 = "空间暂时没有动态～";
var pk_restore$1 = "恢复默认";
var zh_parker = {
    locale: locale$1,
    version: version$1,
    cannot_search_user: cannot_search_user$1,
    load_fail_retry: load_fail_retry$1,
    pk_avatar: pk_avatar$1,
    mention_by_mis_name: mention_by_mis_name$1,
    searching: searching$1,
    pk_link_text: pk_link_text$1,
    insert_blank: insert_blank$1,
    pk_link: pk_link$1,
    pk_link_address: pk_link_address$1,
    pk_visit_link: pk_visit_link$1,
    cancel_blank: cancel_blank$1,
    pk_link_change_tip: pk_link_change_tip$1,
    pk_edit_link: pk_edit_link$1,
    pk_cancel_link: pk_cancel_link$1,
    pk_link_text_tip: pk_link_text_tip$1,
    save_blank: save_blank$1,
    pk_link_address_tip: pk_link_address_tip$1,
    border: border$1,
    pk_insert: pk_insert$1,
    image_link: image_link$1,
    pk_edit: pk_edit$1,
    pk_retry: pk_retry$1,
    width: width$1,
    reupload: reupload$1,
    image_upload_fail1: image_upload_fail1$1,
    image_upload_fail3: image_upload_fail3$1,
    right_address_tip: right_address_tip$1,
    image_upload_fail2: image_upload_fail2$1,
    pk_confirm: pk_confirm$1,
    input_html: input_html$1,
    formual: formual$1,
    pk_loading: pk_loading$1,
    pk_cancel: pk_cancel$1,
    click_to_expand: click_to_expand$1,
    input_html_tip: input_html_tip$1,
    insert_latex_tip: insert_latex_tip$1,
    input_latex: input_latex$1,
    pk_title: pk_title$1,
    wire_frame: wire_frame$1,
    fill_in: fill_in$1,
    input_plantuml: input_plantuml$1,
    border_style: border_style$1,
    set_th_row: set_th_row$1,
    set_th_line: set_th_line$1,
    table_attr: table_attr$1,
    cancel_th_line: cancel_th_line$1,
    insert_row: insert_row$1,
    insert_line_right: insert_line_right$1,
    split_cell: split_cell$1,
    delete_line: delete_line$1,
    confirm_blank: confirm_blank$1,
    input_markdown_tip: input_markdown_tip$1,
    pk_no: pk_no$1,
    cancel_th_row: cancel_th_row$1,
    insert_row_above: insert_row_above$1,
    delete_table: delete_table$1,
    input_plantuml_tip: input_plantuml_tip$1,
    border_width: border_width$1,
    input_markdown: input_markdown$1,
    merge_cell: merge_cell$1,
    set_line_num: set_line_num$1,
    border_color: border_color$1,
    insert_line_left: insert_line_left$1,
    insert_row_below: insert_row_below$1,
    cancel_line_num: cancel_line_num$1,
    if_exit_gantt: if_exit_gantt$1,
    exit_data_lost_tip: exit_data_lost_tip$1,
    gantt_load_fail: gantt_load_fail$1,
    pk_gantt_loading: pk_gantt_loading$1,
    operate_error: operate_error$1,
    gantt_keep_work_tip: gantt_keep_work_tip$1,
    gantt_frame_load_fail: gantt_frame_load_fail$1,
    save_success: save_success$1,
    save_fail: save_fail$1,
    pk_network_exception: pk_network_exception$1,
    pk_saving: pk_saving$1,
    gantt_plugin_fail: gantt_plugin_fail$1,
    gantt_editing_tip: gantt_editing_tip$1,
    pk_discard: pk_discard$1,
    pk_zoom_in: pk_zoom_in$1,
    pk_zoom_out: pk_zoom_out$1,
    pk_use: pk_use$1,
    draft_edit_tip: draft_edit_tip$1,
    pk_load_fail: pk_load_fail$1,
    if_use_local_draft: if_use_local_draft$1,
    pk_this_time: pk_this_time$1,
    choose_time: choose_time$1,
    pk_hh_mm_ss: pk_hh_mm_ss$1,
    pk_y_d: pk_y_d$1,
    pk_y_d_ts: pk_y_d_ts$1,
    pk_text: pk_text$1,
    h1: h1$1,
    h2: h2$1,
    h3: h3$1,
    h4: h4$1,
    h5: h5$1,
    h6: h6$1,
    pk_catalog: pk_catalog$1,
    page_view_load_fail_click: page_view_load_fail_click$1,
    page_tree_no_sub_doc: page_tree_no_sub_doc$1,
    page_tree_no_perm: page_tree_no_perm$1,
    pk_load_fail_retry: pk_load_fail_retry$1,
    loading2: loading2$1,
    page_tree_deleted: page_tree_deleted$1,
    pk_pause: pk_pause$1,
    pk_mute: pk_mute$1,
    pk_untitled: pk_untitled$1,
    pk_play: pk_play$1,
    adjust_volume: adjust_volume$1,
    download_fail_retry: download_fail_retry$1,
    get_file_fail: get_file_fail$1,
    pk_download: pk_download$1,
    pk_network_fail: pk_network_fail$1,
    pk_upload_fail: pk_upload_fail$1,
    audio_not_support_tip: audio_not_support_tip$1,
    browser_not_support_tip: browser_not_support_tip$1,
    pk_volume: pk_volume$1,
    pk_zoom_in2: pk_zoom_in2$1,
    pk_video: pk_video$1,
    video_upload_tip: video_upload_tip$1,
    pk_zoom_out2: pk_zoom_out2$1,
    space_update_fail_tip: space_update_fail_tip$1,
    pk_recent_update: pk_recent_update$1,
    show_more: show_more$1,
    no_space_update: no_space_update$1,
    pk_restore: pk_restore$1
};

var locale$2 = "zh-HK";
var version$2 = 16;
var cannot_search_user$2 = "未能找到用户，請按空格鍵完成輸入";
var load_fail_retry$2 = "載入失敗，請稍後重試。";
var pk_avatar$2 = "頭像";
var mention_by_mis_name$2 = "可通過姓名及MIS號碼@用戶";
var searching$2 = "搜尋中...";
var pk_link_text$2 = "鏈接文字";
var insert_blank$2 = "插 入";
var pk_visit_link$2 = "访问鏈接";
var pk_link_address$2 = "鏈接地址";
var pk_link$2 = "鏈接";
var cancel_blank$2 = "取 消";
var pk_link_change_tip$2 = "鏈接文字跟隨文件標題變更";
var pk_edit_link$2 = "編輯鏈接";
var pk_link_text_tip$2 = "請輸入鏈接文字";
var pk_cancel_link$2 = "取消鏈接";
var save_blank$2 = "保 存";
var pk_link_address_tip$2 = "請輸入鏈接地址";
var border$2 = "邊框";
var pk_insert$2 = "插入";
var image_link$2 = "圖片鏈接";
var pk_retry$2 = "重試";
var pk_edit$2 = "編輯";
var width$2 = "寬度";
var reupload$2 = "重新上傳";
var image_upload_fail1$2 = "抱歉，圖片上傳失敗！";
var right_address_tip$2 = "請輸入正確的地址";
var image_upload_fail3$2 = "否則，請按下方按鈕進行重試。";
var image_upload_fail2$2 = "如果您從外部拖動、粘貼過來的圖片，請下載原圖並重新上傳。";
var pk_confirm$2 = "確定";
var input_html$2 = "輸入HTML代碼";
var formual$2 = "公式";
var pk_loading$2 = "正在載入中...";
var pk_cancel$2 = "取消";
var click_to_expand$2 = "點選展開內容";
var input_html_tip$2 = "可以插入HTML代碼，以在頁面內嵌入相應內容：";
var insert_latex_tip$2 = "可以插入Latex代碼，以在頁面內嵌入相應內容：";
var input_latex$2 = "输入LaTeX代码";
var pk_title$2 = "標題";
var wire_frame$2 = "線框";
var fill_in$2 = "填充";
var input_plantuml$2 = "輸入PlantUML程式碼";
var border_style$2 = "邊框樣式";
var set_th_row$2 = "設置表頭行";
var set_th_line$2 = "設置表頭列";
var table_attr$2 = "表格屬性";
var cancel_th_line$2 = "取消表頭列";
var insert_row$2 = "删除行";
var insert_line_right$2 = "右側插入列";
var split_cell$2 = "拆分儲存格";
var delete_line$2 = "删除列";
var pk_no$2 = "無";
var confirm_blank$2 = "確 定";
var input_markdown_tip$2 = "可以插入Markdown代碼，以在頁面內嵌入相應內容：";
var cancel_th_row$2 = "取消表頭行";
var insert_row_above$2 = "上方插入行";
var delete_table$2 = "刪除表格";
var input_plantuml_tip$2 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容：";
var border_width$2 = "邊框寬度";
var input_markdown$2 = "輸入Markdown代碼";
var merge_cell$2 = "合併儲存格";
var set_line_num$2 = "設置行号列";
var border_color$2 = "邊框顏色";
var insert_line_left$2 = "左側插入列";
var insert_row_below$2 = "下方插入行";
var cancel_line_num$2 = "取消行号列";
var if_exit_gantt$2 = "確定退出甘特圖?";
var exit_data_lost_tip$2 = "退出後，尚未儲存的數據將會遺失。";
var pk_gantt_loading$2 = "甘特圖載入中...";
var gantt_load_fail$2 = "甘特圖載入失敗";
var operate_error$2 = "您的操作有誤";
var gantt_keep_work_tip$2 = "請至少保留一個任務";
var save_success$2 = "保存成功";
var gantt_frame_load_fail$2 = "甘特圖框架載入失敗";
var save_fail$2 = "保存失敗";
var pk_network_exception$2 = "網絡異常";
var pk_saving$2 = "正在儲存中...";
var gantt_plugin_fail$2 = "甘特圖編輯插件啟動失敗。";
var gantt_editing_tip$2 = "正在編輯甘特圖，請稍後再試唷。";
var pk_discard$2 = "廢棄";
var pk_zoom_in$2 = "放大查看";
var pk_zoom_out$2 = "縮小查看";
var pk_use$2 = "使用";
var draft_edit_tip$2 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继續編輯";
var pk_load_fail$2 = "加載失敗";
var if_use_local_draft$2 = "是否使用本地草稿？";
var pk_this_time$2 = "此刻";
var choose_time$2 = "選擇時間";
var pk_hh_mm_ss$2 = "HH:mm:ss";
var pk_y_d$2 = "YYYY-MM-DD";
var pk_y_d_ts$2 = "YYYY-MM-DD HH:mm:ss";
var pk_text$2 = "正文";
var h1$2 = "標題 1";
var h2$2 = "標題 2";
var h3$2 = "標題 3";
var h4$2 = "標題 4";
var h5$2 = "標題 5";
var h6$2 = "標題 6";
var pk_catalog$2 = "目錄";
var page_view_load_fail_click$2 = "目錄樹加載失敗，請點擊重新加載。";
var page_tree_no_sub_doc$2 = "該目錄下暫無子文檔。";
var page_tree_no_perm$2 = "無權查看該目錄樹";
var pk_load_fail_retry$2 = "加載失敗，請點擊重新加載。";
var loading2$2 = "正在載入";
var page_tree_deleted$2 = "該目錄樹已被刪除";
var pk_pause$2 = "暫停";
var pk_mute$2 = "靜音";
var pk_untitled$2 = "未命名";
var pk_play$2 = "播放";
var adjust_volume$2 = "音量調節";
var download_fail_retry$2 = "下載失敗，請重試";
var pk_download$2 = "下載";
var get_file_fail$2 = "文件獲取失敗";
var pk_network_fail$2 = "網絡連接失敗";
var pk_upload_fail$2 = "上載失敗";
var audio_not_support_tip$2 = "本瀏覽器不支援 HTML5 audio 標籤";
var pk_volume$2 = "音量";
var browser_not_support_tip$2 = "本瀏覽器不支援 HTML5 video 標籤";
var pk_zoom_in2$2 = "放大";
var video_upload_tip$2 = "視頻正在轉碼中，如果文件較大可能需要較長的時間，請耐心等待...";
var pk_video$2 = "視頻";
var pk_zoom_out2$2 = "縮小";
var space_update_fail_tip$2 = "空間更新獲取失敗，點擊重試";
var pk_recent_update$2 = "最近更新";
var show_more$2 = "顯示更多";
var no_space_update$2 = "空間暫時沒有動態～";
var pk_restore$2 = "恢復預設";
var zhHK_parker = {
    locale: locale$2,
    version: version$2,
    cannot_search_user: cannot_search_user$2,
    load_fail_retry: load_fail_retry$2,
    pk_avatar: pk_avatar$2,
    mention_by_mis_name: mention_by_mis_name$2,
    searching: searching$2,
    pk_link_text: pk_link_text$2,
    insert_blank: insert_blank$2,
    pk_visit_link: pk_visit_link$2,
    pk_link_address: pk_link_address$2,
    pk_link: pk_link$2,
    cancel_blank: cancel_blank$2,
    pk_link_change_tip: pk_link_change_tip$2,
    pk_edit_link: pk_edit_link$2,
    pk_link_text_tip: pk_link_text_tip$2,
    pk_cancel_link: pk_cancel_link$2,
    save_blank: save_blank$2,
    pk_link_address_tip: pk_link_address_tip$2,
    border: border$2,
    pk_insert: pk_insert$2,
    image_link: image_link$2,
    pk_retry: pk_retry$2,
    pk_edit: pk_edit$2,
    width: width$2,
    reupload: reupload$2,
    image_upload_fail1: image_upload_fail1$2,
    right_address_tip: right_address_tip$2,
    image_upload_fail3: image_upload_fail3$2,
    image_upload_fail2: image_upload_fail2$2,
    pk_confirm: pk_confirm$2,
    input_html: input_html$2,
    formual: formual$2,
    pk_loading: pk_loading$2,
    pk_cancel: pk_cancel$2,
    click_to_expand: click_to_expand$2,
    input_html_tip: input_html_tip$2,
    insert_latex_tip: insert_latex_tip$2,
    input_latex: input_latex$2,
    pk_title: pk_title$2,
    wire_frame: wire_frame$2,
    fill_in: fill_in$2,
    input_plantuml: input_plantuml$2,
    border_style: border_style$2,
    set_th_row: set_th_row$2,
    set_th_line: set_th_line$2,
    table_attr: table_attr$2,
    cancel_th_line: cancel_th_line$2,
    insert_row: insert_row$2,
    insert_line_right: insert_line_right$2,
    split_cell: split_cell$2,
    delete_line: delete_line$2,
    pk_no: pk_no$2,
    confirm_blank: confirm_blank$2,
    input_markdown_tip: input_markdown_tip$2,
    cancel_th_row: cancel_th_row$2,
    insert_row_above: insert_row_above$2,
    delete_table: delete_table$2,
    input_plantuml_tip: input_plantuml_tip$2,
    border_width: border_width$2,
    input_markdown: input_markdown$2,
    merge_cell: merge_cell$2,
    set_line_num: set_line_num$2,
    border_color: border_color$2,
    insert_line_left: insert_line_left$2,
    insert_row_below: insert_row_below$2,
    cancel_line_num: cancel_line_num$2,
    if_exit_gantt: if_exit_gantt$2,
    exit_data_lost_tip: exit_data_lost_tip$2,
    pk_gantt_loading: pk_gantt_loading$2,
    gantt_load_fail: gantt_load_fail$2,
    operate_error: operate_error$2,
    gantt_keep_work_tip: gantt_keep_work_tip$2,
    save_success: save_success$2,
    gantt_frame_load_fail: gantt_frame_load_fail$2,
    save_fail: save_fail$2,
    pk_network_exception: pk_network_exception$2,
    pk_saving: pk_saving$2,
    gantt_plugin_fail: gantt_plugin_fail$2,
    gantt_editing_tip: gantt_editing_tip$2,
    pk_discard: pk_discard$2,
    pk_zoom_in: pk_zoom_in$2,
    pk_zoom_out: pk_zoom_out$2,
    pk_use: pk_use$2,
    draft_edit_tip: draft_edit_tip$2,
    pk_load_fail: pk_load_fail$2,
    if_use_local_draft: if_use_local_draft$2,
    pk_this_time: pk_this_time$2,
    choose_time: choose_time$2,
    pk_hh_mm_ss: pk_hh_mm_ss$2,
    pk_y_d: pk_y_d$2,
    pk_y_d_ts: pk_y_d_ts$2,
    pk_text: pk_text$2,
    h1: h1$2,
    h2: h2$2,
    h3: h3$2,
    h4: h4$2,
    h5: h5$2,
    h6: h6$2,
    pk_catalog: pk_catalog$2,
    page_view_load_fail_click: page_view_load_fail_click$2,
    page_tree_no_sub_doc: page_tree_no_sub_doc$2,
    page_tree_no_perm: page_tree_no_perm$2,
    pk_load_fail_retry: pk_load_fail_retry$2,
    loading2: loading2$2,
    page_tree_deleted: page_tree_deleted$2,
    pk_pause: pk_pause$2,
    pk_mute: pk_mute$2,
    pk_untitled: pk_untitled$2,
    pk_play: pk_play$2,
    adjust_volume: adjust_volume$2,
    download_fail_retry: download_fail_retry$2,
    pk_download: pk_download$2,
    get_file_fail: get_file_fail$2,
    pk_network_fail: pk_network_fail$2,
    pk_upload_fail: pk_upload_fail$2,
    audio_not_support_tip: audio_not_support_tip$2,
    pk_volume: pk_volume$2,
    browser_not_support_tip: browser_not_support_tip$2,
    pk_zoom_in2: pk_zoom_in2$2,
    video_upload_tip: video_upload_tip$2,
    pk_video: pk_video$2,
    pk_zoom_out2: pk_zoom_out2$2,
    space_update_fail_tip: space_update_fail_tip$2,
    pk_recent_update: pk_recent_update$2,
    show_more: show_more$2,
    no_space_update: no_space_update$2,
    pk_restore: pk_restore$2
};

// This file was auto generated by resource-sync tools.
var resources = {
    "en": {
        parker: en_parker
    },
    "zh": {
        parker: zh_parker
    },
    "zh-HK": {
        parker: zhHK_parker
    }
};

function _classCallCheck$6(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var I18nManager = /*#__PURE__*/ function() {
    function I18nManager() {
        _classCallCheck$6(this, I18nManager);
    }
    I18nManager.getInstance = function getInstance() {
        if (this.instance === null) {
            this.instance = createInstance();
            this.instance.init({
                // lng: 'zh',
                fallbackLng: "zh",
                ns: [
                    "parker"
                ],
                defaultNS: "parker",
                resources: resources,
                interpolation: {
                    escapeValue: false
                },
                react: {
                    useSuspense: false
                }
            });
            window.parkerI18nClient = this.instance;
        }
        return this.instance;
    };
    return I18nManager;
}();
I18nManager.instance = null;
var i18nClient = I18nManager.getInstance();

var css_248z$4 = ".ct-button{height:32px;border:1px solid rgba(0,0,0,.13);border-radius:4px;box-sizing:border-box;min-width:80px;font-size:14px;font-family:PingFangSC-Regular;color:rgba(0,0,0,.87);margin-left:12px;cursor:pointer;transition:all .3s}.ct-button:hover{border:1px solid #1c6cdc;color:#1c6cdc}.ct-button:active{color:#1253b5;border:1px solid #1253b5}.ct-button[disabled]{cursor:not-allowed;opacity:.45}.ct-button[disabled]:active,.ct-button[disabled]:hover{border:1px solid rgba(0,0,0,.13);color:rgba(0,0,0,.87)}.ct-button-primary.ct-button{border:1px solid #1c6cdc;background:#1c6cdc;color:#fff}.ct-button-primary.ct-button[disabled]{cursor:not-allowed;opacity:.45}.ct-button-primary.ct-button[disabled]:active,.ct-button-primary.ct-button[disabled]:hover{background:#1c6cdc;color:#fff}.ct-button-primary.ct-button:hover{border:1px solid #3d91f2;background:#3d91f2;color:#fff}.ct-button-primary.ct-button:active{border:1px solid #1253b5;background:#1253b5;color:#fff}.ct-button-danger{background:#e75252;color:#fff}.ct-button-disabled{opacity:.45;cursor:not-allowed}";
styleInject(css_248z$4);

function _assertThisInitialized$1(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$7(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$8(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _getPrototypeOf$1(o) {
    _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$1(o);
}
function _inherits$1(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$1(subClass, superClass);
}
function _possibleConstructorReturn$1(self, call) {
    if (call && (_typeof$1(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$1(self);
}
function _setPrototypeOf$1(o, p) {
    _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$1(o, p);
}
var _typeof$1 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$1() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$1(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$1();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$1(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$1(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$1(this, result);
    };
}
var Button = /*#__PURE__*/ function(Component) {
    _inherits$1(Button, Component);
    var _super = _createSuper$1(Button);
    function Button() {
        _classCallCheck$7(this, Button);
        return _super.apply(this, arguments);
    }
    var _proto = Button.prototype;
    _proto.render = function render() {
        var _this_props = this.props, children = _this_props.children, type = _this_props.type, _this_props_prefix = _this_props.prefix, prefix = _this_props_prefix === void 0 ? "button" : _this_props_prefix, disabled = _this_props.disabled, onClick = _this_props.onClick;
        var customPrefix = classNamePrefix(prefix);
        var classes = _defineProperty$8({}, "".concat(customPrefix, "-disabled"), disabled);
        if (type) {
            classes["".concat(customPrefix, "-").concat(type)] = true;
        }
        return /*#__PURE__*/ React.createElement("button", {
            disabled: disabled,
            className: classNames(customPrefix, classes),
            onClick: onClick
        }, children);
    };
    return Button;
}(Component);

var css_248z$5 = '.ct-base-modal{position:fixed;top:0;left:0;right:0;bottom:0;background-color:rgba(0,0,0,.5);z-index:1002}.ct-base-modal .mask{position:absolute;top:0;left:0;right:0;bottom:0}.ct-base-modal-header{height:58px;padding:16px 24px;border-radius:4px 4px 0 0;background-color:#fff;line-height:26px;color:rgba(0,0,0,.87)}.ct-base-modal-header-title{font-size:18px;font-family:PingFangSC-Medium}.ct-base-modal-header-close{position:absolute;width:28px;height:28px;top:15px;right:20px;background:#fff;transition:all .3s;display:flex;align-items:center;justify-content:center;border-radius:50%;cursor:pointer}.ct-base-modal-header-close:hover{background:rgba(0,0,0,.07)}.ct-base-modal-header-close-icon{cursor:pointer;width:16px;height:16px;border:none;outline:none;background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 standalone%3D%22no%22%3F%3E%3C!DOCTYPE svg PUBLIC %22-%2F%2FW3C%2F%2FDTD SVG 1.1%2F%2FEN%22 %22http%3A%2F%2Fwww.w3.org%2FGraphics%2FSVG%2F1.1%2FDTD%2Fsvg11.dtd%22%3E%3Csvg t%3D%221526118302462%22 class%3D%22icon%22 style%3D%22%22 viewBox%3D%220 0 1024 1024%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 p-id%3D%223872%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22 width%3D%2216%22 height%3D%2216%22%3E%3Cdefs%3E%3Cstyle type%3D%22text%2Fcss%22%3E%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath d%3D%22M512 444.032L209.984 142.08A48 48 0 1 0 142.08 209.92L444.032 512 142.08 814.016A48 48 0 1 0 209.92 881.92L512 579.968l302.016 301.952a48 48 0 1 0 67.904-67.904L579.968 512l301.952-302.016A48 48 0 1 0 814.08 142.08L512 444.032z%22 fill%3D%22%23bbbbbb%22 p-id%3D%223873%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E") no-repeat 50%}.ct-base-modal-body{padding:0 24px}.ct-base-modal-content{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:560px;border-radius:10px;overflow:hidden;background-color:#fff;box-shadow:0 1px 4px 0 rgba(111,130,166,.08),0 16px 30px 0 rgba(111,130,166,.14)}.ct-base-modal-footer{display:flex;justify-content:flex-end;height:64px;padding:16px 24px;border-radius:0 0 4px 4px}';
styleInject(css_248z$5);

/* eslint-disable jsx-a11y/control-has-associated-label */ function _assertThisInitialized$2(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$8(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf$2(o) {
    _getPrototypeOf$2 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$2(o);
}
function _inherits$2(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$2(subClass, superClass);
}
function _possibleConstructorReturn$2(self, call) {
    if (call && (_typeof$2(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$2(self);
}
function _setPrototypeOf$2(o, p) {
    _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$2(o, p);
}
var _typeof$2 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$2() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$2(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$2();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$2(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$2(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$2(this, result);
    };
}
var defaultModalWidth = {
    small: 400,
    middle: 600,
    large: 800,
    super: 960
};
var Modal = /*#__PURE__*/ function(Component) {
    _inherits$2(Modal, Component);
    var _super = _createSuper$2(Modal);
    function Modal() {
        _classCallCheck$8(this, Modal);
        var _this;
        _this = _super.apply(this, arguments);
        _this.onKeyDown = function(e) {
            var _this_props = _this.props, onOk = _this_props.onOk, canEnter = _this_props.canEnter;
            if (!e.isComposing && (e.which === 13 || e.keyCode === 13) && canEnter) {
                onOk();
                e.stopPropagation();
                e.preventDefault();
                return false;
            }
            return undefined;
        };
        return _this;
    }
    var _proto = Modal.prototype;
    _proto.render = function render() {
        var _this_props = this.props, children = _this_props.children, className = _this_props.className, title = _this_props.title, contentClassName = _this_props.contentClassName, _this_props_okText = _this_props.okText, okText = _this_props_okText === void 0 ? i18nClient.t("pk_confirm", "确定") : _this_props_okText, _this_props_cancelText = _this_props.cancelText, cancelText = _this_props_cancelText === void 0 ? i18nClient.t("pk_cancel", "取消") : _this_props_cancelText, onOk = _this_props.onOk, disabled = _this_props.disabled, onCancel = _this_props.onCancel, _this_props_showFooter = _this_props.showFooter, showFooter = _this_props_showFooter === void 0 ? true : _this_props_showFooter, wrapper = _this_props.wrapper, userPrefix = _this_props.prefix, size = _this_props.size;
        var prefix = classNamePrefix(userPrefix);
        return /*#__PURE__*/ ReactDom.createPortal(/*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, " ").concat(className || "")
        }, /*#__PURE__*/ React.createElement("div", {
            className: "mask"
        }), /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-content ").concat(contentClassName || ""),
            style: {
                width: defaultModalWidth[size]
            },
            onKeyDown: this.onKeyDown
        }, /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-header")
        }, /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-header-title")
        }, title), /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-header-close"),
            onClick: onCancel
        }, /*#__PURE__*/ React.createElement("button", {
            className: "".concat(prefix, "-header-close-icon")
        }))), /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-body")
        }, children), showFooter && /*#__PURE__*/ React.createElement("div", {
            className: "".concat(prefix, "-footer")
        }, /*#__PURE__*/ React.createElement(Button, {
            onClick: onCancel
        }, cancelText), /*#__PURE__*/ React.createElement(Button, {
            type: "primary",
            className: "ok",
            onClick: onOk,
            disabled: disabled
        }, okText)))), wrapper || document.body);
    };
    return Modal;
}(Component);
Modal.defaultProps = {
    prefix: "base-modal",
    size: "middle",
    canEnter: false
};

var colors = [
    "#fff",
    "#333",
    "#f5222d",
    "#fa541c",
    "#faad14",
    "#fadb14",
    "#52c41a",
    "#13c2c2",
    "#1890ff",
    "#2f54eb",
    "#722ed1",
    "#eb2f96",
    "#f6f6f6",
    "#989898",
    "#ffe8e7",
    "#ffece0",
    "#fff7cb",
    "#fcfcca",
    "#e5f7d3",
    "#cffff7",
    "#d4eefc",
    "#dfe8fc",
    "#efe1fa",
    "#fbe1eb",
    "#e9e9e9",
    "#666",
    "#ffa39e",
    "#ffbb96",
    "#ffe58f",
    "#fffb8f",
    "#b7eb8f",
    "#87e8de",
    "#91d5ff",
    "#adc6ff",
    "#d3adf7",
    "#ffadd2",
    "#ddd",
    "#4b4b4b",
    "#ff4d4f",
    "#ff7a45",
    "#ffc53d",
    "#ffec3d",
    "#73d13d",
    "#36cfc9",
    "#40a8ff",
    "#597ef7",
    "#9254de",
    "#f759ab",
    "#ccc",
    "#262626",
    "#cf1322",
    "#d4380d",
    "#d48806",
    "#d4b106",
    "#389e0d",
    "#08979c",
    "#096dd9",
    "#1d39c4",
    "#531dab",
    "#c41d7f",
    "#aaa",
    "#000",
    "#820014",
    "#871400",
    "#874d00",
    "#876800",
    "#135200",
    "#00474f",
    "#003a8c",
    "#061178",
    "#22075e",
    "#780650"
];

function _assertThisInitialized$3(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$9(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf$3(o) {
    _getPrototypeOf$3 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$3(o);
}
function _inherits$3(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$3(subClass, superClass);
}
function _possibleConstructorReturn$3(self, call) {
    if (call && (_typeof$3(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$3(self);
}
function _setPrototypeOf$3(o, p) {
    _setPrototypeOf$3 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$3(o, p);
}
var _typeof$3 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$3() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$3(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$3();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$3(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$3(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$3(this, result);
    };
}
var PopItem = /*#__PURE__*/ function(Component) {
    _inherits$3(PopItem, Component);
    var _super = _createSuper$3(PopItem);
    function PopItem() {
        _classCallCheck$9(this, PopItem);
        return _super.apply(this, arguments);
    }
    var _proto = PopItem.prototype;
    _proto.render = function render() {
        var _this_props = this.props, type = _this_props.type, defaultColor = _this_props.defaultColor, onSelect = _this_props.onSelect, selectedColor = _this_props.selectedColor;
        var colorGrids = colors.map(function(color, index) {
            return /*#__PURE__*/ React.createElement("div", {
                key: color,
                onClick: function() {
                    return onSelect(color);
                },
                className: classNames(classNamePrefix("table-color-grid"), {
                    selected: selectedColor === color,
                    "has-margin": index >= 10 && index < 20
                }),
                style: {
                    background: color
                }
            });
        });
        return /*#__PURE__*/ React.createElement("div", {
            "data-action": "".concat(type, "-color"),
            className: "".concat(classNamePrefix("table-color-popover"))
        }, /*#__PURE__*/ React.createElement("div", {
            onClick: function() {
                return onSelect(defaultColor);
            },
            className: "default"
        }, /*#__PURE__*/ React.createElement("div", {
            style: {
                background: defaultColor,
                borderColor: defaultColor
            },
            className: "color-grid"
        }), /*#__PURE__*/ React.createElement("div", {
            className: "text"
        }, i18nClient.t("pk_restore", "恢复默认"))), colorGrids);
    };
    return PopItem;
}(Component);

var css_248z$6 = '.ct-table-color-popover-wrapper{display:flex;justify-content:center;align-items:center;position:relative;height:28px;width:246px;font-size:12px;cursor:pointer;transition:all .2s ease}.ct-table-color-popover-wrapper .color-block{display:flex;justify-content:flex-start;align-items:baseline;width:246px;height:24px;padding:6px 8px;border:1px solid #ddd;border-radius:2px}.ct-table-color-popover-wrapper .color-block .left{width:100%;height:100%;margin-right:10px}.ct-table-color-popover-wrapper .color-block .right{width:14px;height:100%;background:url("data:image/svg+xml,%3Csvg width%3D%227px%22 height%3D%224px%22 viewBox%3D%220 0 7 4%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 45.1 (43504) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%2220%E3%80%81%E5%AF%8C%E6%96%87%E6%9C%AC%E7%BC%96%E8%BE%91%22 transform%3D%22translate(-521.000000%2C -82.000000)%22 fill%3D%22%23AEAEAE%22%3E            %3Cg id%3D%22Group%22 transform%3D%22translate(0.000000%2C 56.000000)%22%3E                %3Cg id%3D%22Group-8%22 transform%3D%22translate(258.500000%2C 15.000000)%22%3E                    %3Cg id%3D%22%E5%AD%97%E5%8F%B7%22 transform%3D%22translate(223.000000%2C 0.000000)%22%3E                        %3Cpolygon id%3D%22%E5%BD%A2%E7%8A%B6_32%22 points%3D%2240 11.5 46 11.5 43 14.5%22%3E%3C%2Fpolygon%3E                    %3C%2Fg%3E                %3C%2Fg%3E            %3C%2Fg%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 50%}.ct-table-color-popover{display:flex;justify-content:flex-start;align-items:flex-start;flex-wrap:wrap;position:absolute;width:258px;height:150px;bottom:-152px;left:0;padding:8px;border-radius:2px;background:#fff;border:1px solid #eee;box-shadow:0 1px 4px 0 rgba(111,130,166,.08),0 16px 30px 0 rgba(111,130,166,.14);z-index:2}.ct-table-color-popover .default{position:relative;width:calc(100% - 2px);margin:0 1px 8px;height:24px;border:1px solid #ddd;border-radius:2px;cursor:pointer}.ct-table-color-popover .color-grid{position:absolute;top:5px;left:10px;width:13px;height:13px;border:1px solid}.ct-table-color-popover .text{height:24px;line-height:24px;text-align:center;font-size:12px;color:#333}.ct-table-color-grid{margin:0 2px;width:16px;height:16px;cursor:pointer}.ct-table-color-grid:nth-child(2),.ct-table-color-grid:nth-child(12){border:1px solid #eee}.ct-table-color-grid.selected{z-index:2;border-radius:1px;border:1px solid #fff;box-shadow:0 0 0 2px #999}.ct-table-color-grid:hover{z-index:3;border-radius:1px;border:1px solid #fff}.ct-table-color-grid:hover:nth-child(2),.ct-table-color-grid:hover:nth-child(14){box-shadow:0 0 0 2px #aaa}.ct-table-color-grid:hover:nth-child(2){box-shadow:0 0 0 2px #fff}.ct-table-color-grid:hover:nth-child(3){box-shadow:0 0 0 2px #333}.ct-table-color-grid:hover:nth-child(4){box-shadow:0 0 0 2px #f5222d}.ct-table-color-grid:hover:nth-child(5){box-shadow:0 0 0 2px #fa541c}.ct-table-color-grid:hover:nth-child(6){box-shadow:0 0 0 2px #faad14}.ct-table-color-grid:hover:nth-child(7){box-shadow:0 0 0 2px #fadb14}.ct-table-color-grid:hover:nth-child(8){box-shadow:0 0 0 2px #52c41a}.ct-table-color-grid:hover:nth-child(9){box-shadow:0 0 0 2px #13c2c2}.ct-table-color-grid:hover:nth-child(10){box-shadow:0 0 0 2px #1890ff}.ct-table-color-grid:hover:nth-child(11){box-shadow:0 0 0 2px #2f54eb}.ct-table-color-grid:hover:nth-child(12){box-shadow:0 0 0 2px #722ed1}.ct-table-color-grid:hover:nth-child(13){box-shadow:0 0 0 2px #eb2f96}.ct-table-color-grid:hover:nth-child(14){box-shadow:0 0 0 2px #f6f6f6}.ct-table-color-grid:hover:nth-child(15){box-shadow:0 0 0 2px #989898}.ct-table-color-grid:hover:nth-child(16){box-shadow:0 0 0 2px #ffe8e7}.ct-table-color-grid:hover:nth-child(17){box-shadow:0 0 0 2px #ffece0}.ct-table-color-grid:hover:nth-child(18){box-shadow:0 0 0 2px #fff7cb}.ct-table-color-grid:hover:nth-child(19){box-shadow:0 0 0 2px #fcfcca}.ct-table-color-grid:hover:nth-child(20){box-shadow:0 0 0 2px #e5f7d3}.ct-table-color-grid:hover:nth-child(21){box-shadow:0 0 0 2px #cffff7}.ct-table-color-grid:hover:nth-child(22){box-shadow:0 0 0 2px #d4eefc}.ct-table-color-grid:hover:nth-child(23){box-shadow:0 0 0 2px #dfe8fc}.ct-table-color-grid:hover:nth-child(24){box-shadow:0 0 0 2px #efe1fa}.ct-table-color-grid:hover:nth-child(25){box-shadow:0 0 0 2px #fbe1eb}.ct-table-color-grid:hover:nth-child(26){box-shadow:0 0 0 2px #e9e9e9}.ct-table-color-grid:hover:nth-child(27){box-shadow:0 0 0 2px #666}.ct-table-color-grid:hover:nth-child(28){box-shadow:0 0 0 2px #ffa39e}.ct-table-color-grid:hover:nth-child(29){box-shadow:0 0 0 2px #ffbb96}.ct-table-color-grid:hover:nth-child(30){box-shadow:0 0 0 2px #ffe58f}.ct-table-color-grid:hover:nth-child(31){box-shadow:0 0 0 2px #fffb8f}.ct-table-color-grid:hover:nth-child(32){box-shadow:0 0 0 2px #b7eb8f}.ct-table-color-grid:hover:nth-child(33){box-shadow:0 0 0 2px #87e8de}.ct-table-color-grid:hover:nth-child(34){box-shadow:0 0 0 2px #91d5ff}.ct-table-color-grid:hover:nth-child(35){box-shadow:0 0 0 2px #adc6ff}.ct-table-color-grid:hover:nth-child(36){box-shadow:0 0 0 2px #d3adf7}.ct-table-color-grid:hover:nth-child(37){box-shadow:0 0 0 2px #ffadd2}.ct-table-color-grid:hover:nth-child(38){box-shadow:0 0 0 2px #ddd}.ct-table-color-grid:hover:nth-child(39){box-shadow:0 0 0 2px #4b4b4b}.ct-table-color-grid:hover:nth-child(40){box-shadow:0 0 0 2px #ff4d4f}.ct-table-color-grid:hover:nth-child(41){box-shadow:0 0 0 2px #ff7a45}.ct-table-color-grid:hover:nth-child(42){box-shadow:0 0 0 2px #ffc53d}.ct-table-color-grid:hover:nth-child(43){box-shadow:0 0 0 2px #ffec3d}.ct-table-color-grid:hover:nth-child(44){box-shadow:0 0 0 2px #73d13d}.ct-table-color-grid:hover:nth-child(45){box-shadow:0 0 0 2px #36cfc9}.ct-table-color-grid:hover:nth-child(46){box-shadow:0 0 0 2px #40a8ff}.ct-table-color-grid:hover:nth-child(47){box-shadow:0 0 0 2px #597ef7}.ct-table-color-grid:hover:nth-child(48){box-shadow:0 0 0 2px #9254de}.ct-table-color-grid:hover:nth-child(49){box-shadow:0 0 0 2px #f759ab}.ct-table-color-grid:hover:nth-child(50){box-shadow:0 0 0 2px #ccc}.ct-table-color-grid:hover:nth-child(51){box-shadow:0 0 0 2px #262626}.ct-table-color-grid:hover:nth-child(52){box-shadow:0 0 0 2px #cf1322}.ct-table-color-grid:hover:nth-child(53){box-shadow:0 0 0 2px #d4380d}.ct-table-color-grid:hover:nth-child(54){box-shadow:0 0 0 2px #d48806}.ct-table-color-grid:hover:nth-child(55){box-shadow:0 0 0 2px #d4b106}.ct-table-color-grid:hover:nth-child(56){box-shadow:0 0 0 2px #389e0d}.ct-table-color-grid:hover:nth-child(57){box-shadow:0 0 0 2px #08979c}.ct-table-color-grid:hover:nth-child(58){box-shadow:0 0 0 2px #096dd9}.ct-table-color-grid:hover:nth-child(59){box-shadow:0 0 0 2px #1d39c4}.ct-table-color-grid:hover:nth-child(60){box-shadow:0 0 0 2px #531dab}.ct-table-color-grid:hover:nth-child(61){box-shadow:0 0 0 2px #c41d7f}.ct-table-color-grid:hover:nth-child(62){box-shadow:0 0 0 2px #aaa}.ct-table-color-grid:hover:nth-child(63){box-shadow:0 0 0 2px #000}.ct-table-color-grid:hover:nth-child(64){box-shadow:0 0 0 2px #820014}.ct-table-color-grid:hover:nth-child(65){box-shadow:0 0 0 2px #871400}.ct-table-color-grid:hover:nth-child(66){box-shadow:0 0 0 2px #874d00}.ct-table-color-grid:hover:nth-child(67){box-shadow:0 0 0 2px #876800}.ct-table-color-grid:hover:nth-child(68){box-shadow:0 0 0 2px #135200}.ct-table-color-grid:hover:nth-child(69){box-shadow:0 0 0 2px #00474f}.ct-table-color-grid:hover:nth-child(70){box-shadow:0 0 0 2px #003a8c}.ct-table-color-grid:hover:nth-child(71){box-shadow:0 0 0 2px #061178}.ct-table-color-grid:hover:nth-child(72){box-shadow:0 0 0 2px #22075e}.ct-table-color-grid:hover:nth-child(73){box-shadow:0 0 0 2px #780650}';
styleInject(css_248z$6);

/* eslint-disable react/destructuring-assignment */ function _assertThisInitialized$4(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$a(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf$4(o) {
    _getPrototypeOf$4 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$4(o);
}
function _inherits$4(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$4(subClass, superClass);
}
function _possibleConstructorReturn$4(self, call) {
    if (call && (_typeof$4(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$4(self);
}
function _setPrototypeOf$4(o, p) {
    _setPrototypeOf$4 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$4(o, p);
}
var _typeof$4 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$4() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$4(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$4();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$4(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$4(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$4(this, result);
    };
}
var ColorPopover = /*#__PURE__*/ function(Component) {
    _inherits$4(ColorPopover, Component);
    var _super = _createSuper$4(ColorPopover);
    function ColorPopover(props) {
        _classCallCheck$a(this, ColorPopover);
        var _this;
        _this = _super.call(this, props);
        // 接收来自 Popover 的选中事件后，调用父级 transform 方法
        _this.onSelect = function(color) {
            var setColor = _this.props.setColor;
            setColor(color);
            // HACK 异步 setState 以确保 tooltip 关闭
            window.requestAnimationFrame(function() {
                _this.setState({
                    hasTooltip: false,
                    isOpen: false
                });
            });
        };
        _this.handleMouseEnter = function() {
            var isOpen = _this.state.isOpen;
            if (isOpen) return;
            _this.setState({
                hasTooltip: true
            });
        };
        _this.state = {
            hasTooltip: false,
            isOpen: false
        };
        return _this;
    }
    var _proto = ColorPopover.prototype;
    _proto.render = function render() {
        var _this = this;
        var _this_props = this.props, type = _this_props.type, tips = _this_props.tips, defaultColor = _this_props.defaultColor, selectedColor = _this_props.selectedColor;
        var _this_state = this.state, hasTooltip = _this_state.hasTooltip, isOpen = _this_state.isOpen;
        return /*#__PURE__*/ React.createElement("div", {
            onMouseEnter: this.handleMouseEnter,
            onMouseLeave: function() {
                return _this.setState({
                    hasTooltip: false
                });
            },
            onClick: function() {
                return _this.setState({
                    isOpen: !isOpen
                });
            },
            className: "".concat(classNamePrefix("table-color-popover-wrapper"), " ").concat(type, "-color")
        }, /*#__PURE__*/ React.createElement("div", {
            className: "color-block"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "left",
            style: {
                backgroundColor: selectedColor
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: "right"
        })), isOpen && /*#__PURE__*/ React.createElement(PopItem, {
            type: type,
            defaultColor: defaultColor,
            selectedColor: selectedColor,
            onSelect: this.onSelect
        }), hasTooltip && /*#__PURE__*/ React.createElement("div", {
            className: "ct-toolbar-tooltip"
        }, tips));
    };
    return ColorPopover;
}(Component);

var css_248z$7 = '.table-modal-content{display:flex;justify-content:flex-start;align-items:baseline;padding:24px}.table-modal-content .left{display:flex;flex-direction:column;justify-content:space-between;width:246px;height:176px;margin-right:30px}.table-modal-content .right{width:300px}.table-modal-content .title{margin-bottom:8px;font-size:14px;color:#333;line-height:22px}.table-modal-content .style-wrapper{display:flex;justify-content:flex-start;align-items:baseline}.table-modal-content .style-item{width:76px;height:70px;text-align:center}.table-modal-content .dashed{background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2248px%22 height%3D%2242px%22 viewBox%3D%220 0 48 42%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E8%99%9A%E7%BA%BF%E6%A1%86%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22%E8%A1%A8%E6%A0%BC%E5%B1%9E%E6%80%A7%22%3E            %3Cpath d%3D%22M45%2C16.5 L45%2C10.5 L48%2C10.5 L48%2C16.5 L45%2C16.5 Z M45%2C25.5 L48%2C25.5 L48%2C31.5 L45%2C31.5 L45%2C25.5 Z M45%2C34.5 L48%2C34.5 L48%2C36 C48%2C39.3137085 45.3137085%2C42 42%2C42 L37.4854431%2C42 L37.4854431%2C39 L42%2C39 C43.6568542%2C39 45%2C37.6568542 45%2C36 L45%2C34.5 Z M34.4854431%2C39 L34.4854431%2C42 L28.4854431%2C42 L28.4854431%2C39 L34.4854431%2C39 Z M19.4854431%2C39 L19.4854431%2C42 L13.4854431%2C42 L13.4854431%2C39 L19.4854431%2C39 Z M10.4854431%2C39 L10.4854431%2C42 L6%2C42 C2.6862915%2C42 0%2C39.3137085 0%2C36 L0%2C34.5 L3%2C34.5 L3%2C36 C3%2C37.6568542 4.34314575%2C39 6%2C39 L10.4854431%2C39 Z M3%2C31.5 L0%2C31.5 L0%2C25.5 L3%2C25.5 L3%2C31.5 Z M3%2C16.5 L0%2C16.5 L0%2C10.5 L3%2C10.5 L3%2C16.5 Z M3%2C7.5 L0%2C7.5 L0%2C6 C0%2C2.6862915 2.6862915%2C0 6%2C0 L10.4854431%2C0 L10.4854431%2C3 L6%2C3 C4.34314575%2C3 3%2C4.34314575 3%2C6 L3%2C7.5 Z M13.4854431%2C3 L13.4854431%2C0 L19.4854431%2C0 L19.4854431%2C3 L13.4854431%2C3 Z M28.4854431%2C3 L28.4854431%2C0 L34.4854431%2C0 L34.4854431%2C3 L28.4854431%2C3 Z M37.4854431%2C3 L37.4854431%2C0 L42%2C0 C45.3137085%2C0 48%2C2.6862915 48%2C6 L48%2C7.5 L45%2C7.5 L45%2C6 C45%2C4.34314575 43.6568542%2C3 42%2C3 L37.4854431%2C3 Z%22 fill%3D%22%236B6B6B%22 fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E            %3Cpath d%3D%22M25.4854431%2C22.5 L25.4854431%2C31.538842 L22.4854431%2C31.538842 L22.4854431%2C22.5 L10.2857143%2C22.5 L10.2857143%2C19.5 L22.4854431%2C19.5 L22.4854431%2C10.461158 L25.4854431%2C10.461158 L25.4854431%2C19.5 L37.7142857%2C19.5 L37.7142857%2C22.5 L25.4854431%2C22.5 Z M-2.27373675e-13%2C22.5 L-2.27373675e-13%2C19.5 L6.85714286%2C19.5 L6.85714286%2C22.5 L-2.27373675e-13%2C22.5 Z M41.1428571%2C22.5 L41.1428571%2C19.5 L48%2C19.5 L48%2C22.5 L41.1428571%2C22.5 Z M22.4854431%2C1.42108547e-14 L25.4854431%2C1.42108547e-14 L25.4854431%2C6.97410534 L22.4854431%2C6.97410534 L22.4854431%2C1.42108547e-14 Z M22.4854431%2C35.0258947 L25.4854431%2C35.0258947 L25.4854431%2C42 L22.4854431%2C42 L22.4854431%2C35.0258947 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23CCCCCC%22%3E%3C%2Fpath%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 50%}.table-modal-content .solid{margin-left:18px;background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2248px%22 height%3D%2242px%22 viewBox%3D%220 0 48 42%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E5%AE%9E%E7%BA%BF%E6%A1%86%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22Group-9%22%3E            %3Cpath d%3D%22M25.4854431%2C22.5 L25.4854431%2C31.538842 L22.4854431%2C31.538842 L22.4854431%2C22.5 L17.1428571%2C22.5 L10.2857143%2C22.5 L10.2857143%2C19.5 L17.1428571%2C19.5 L22.4854431%2C19.5 L22.4854431%2C10.461158 L25.4854431%2C10.461158 L25.4854431%2C19.5 L33.7302246%2C19.5 L37.7142857%2C19.5 L37.7142857%2C22.5 L33.7302246%2C22.5 L25.4854431%2C22.5 Z M0%2C22.5 L0%2C19.5 L6.85714286%2C19.5 L6.85714286%2C22.5 L0%2C22.5 Z M41.1428571%2C22.5 L41.1428571%2C19.5 L48%2C19.5 L48%2C22.5 L41.1428571%2C22.5 Z M22.4854431%2C0 L25.4854431%2C0 L25.4854431%2C6.97410534 L22.4854431%2C6.97410534 L22.4854431%2C0 Z M22.4854431%2C35.0258947 L25.4854431%2C35.0258947 L25.4854431%2C42 L22.4854431%2C42 L22.4854431%2C35.0258947 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23CCCCCC%22%3E%3C%2Fpath%3E            %3Cpath d%3D%22M6%2C3 C4.34314575%2C3 3%2C4.34314575 3%2C6 L3%2C36 C3%2C37.6568542 4.34314575%2C39 6%2C39 L42%2C39 C43.6568542%2C39 45%2C37.6568542 45%2C36 L45%2C6 C45%2C4.34314575 43.6568542%2C3 42%2C3 L6%2C3 Z M6%2C0 L42%2C0 C45.3137085%2C0 48%2C2.6862915 48%2C6 L48%2C36 C48%2C39.3137085 45.3137085%2C42 42%2C42 L6%2C42 C2.6862915%2C42 0%2C39.3137085 0%2C36 L0%2C6 C0%2C2.6862915 2.6862915%2C0 6%2C0 Z%22 id%3D%22Rectangle-15%22 fill%3D%22%236B6B6B%22 fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 50%}.table-modal-content .selected{background-color:rgba(0,167,205,.1)}.width-wrapper{padding:0 20px;display:flex;justify-content:space-around;align-items:center;flex-wrap:wrap;height:220px;border:1px solid #ddd;border-radius:2px;font-size:12px}.width-wrapper .width-item{cursor:pointer;width:126px;height:19px;line-height:19px;border-radius:2px;text-align:center;display:flex;align-items:center;justify-content:center}.width-wrapper .width-item .inner{display:inline-block;vertical-align:middle;width:110px;background:#1c6cdc}.width-wrapper .width-item:nth-child(2) .inner{height:4px}.width-wrapper .width-item:nth-child(3) .inner{height:1px}.width-wrapper .width-item:nth-child(4) .inner{height:5px}.width-wrapper .width-item:nth-child(5) .inner{height:2px}.width-wrapper .width-item:nth-child(6) .inner{height:6px}.width-wrapper .width-item:nth-child(7) .inner{height:3px}.width-wrapper .width-item:nth-child(8) .inner{height:7px}';
styleInject(css_248z$7);

function _assertThisInitialized$5(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$b(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$9(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _getPrototypeOf$5(o) {
    _getPrototypeOf$5 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$5(o);
}
function _inherits$5(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$5(subClass, superClass);
}
function _objectSpread$8(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$9(target, key, source[key]);
        });
    }
    return target;
}
function _possibleConstructorReturn$5(self, call) {
    if (call && (_typeof$5(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$5(self);
}
function _setPrototypeOf$5(o, p) {
    _setPrototypeOf$5 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$5(o, p);
}
var _typeof$5 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$5() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$5(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$5();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$5(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$5(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$5(this, result);
    };
}
var TableModal = /*#__PURE__*/ function(Component) {
    _inherits$5(TableModal, Component);
    var _super = _createSuper$5(TableModal);
    function TableModal(props) {
        _classCallCheck$b(this, TableModal);
        var _this;
        _this = _super.call(this, props);
        _this.handleOk = function() {
            var _this_props = _this.props, setTableAttrs = _this_props.setTableAttrs, hideModal = _this_props.hideModal;
            setTableAttrs(_objectSpread$8({}, _this.state));
            hideModal();
        };
        _this.handleCancel = function() {
            var hideModal = _this.props.hideModal;
            hideModal();
        };
        var getTableAttrs = _this.props.getTableAttrs;
        var attrs = getTableAttrs();
        _this.state = {
            borderStyle: attrs.borderStyle || "solid",
            borderColor: attrs.borderColor || "#cccccc",
            borderWidth: attrs.borderWidth === null ? 1 : attrs.borderWidth
        };
        return _this;
    }
    var _proto = TableModal.prototype;
    _proto.render = function render() {
        var _this = this;
        var _this_state = this.state, borderStyle = _this_state.borderStyle, borderColor = _this_state.borderColor, borderWidth = _this_state.borderWidth;
        return /*#__PURE__*/ React.createElement(Modal, {
            className: "",
            contentClassName: "table-modal-wrapper",
            title: i18nClient.t("table_attr", "表格属性"),
            disabled: false,
            okText: i18nClient.t("pk_confirm", "确定"),
            onOk: this.handleOk,
            onCancel: function() {
                _this.handleCancel();
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: "table-modal-content"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "left"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "border-style"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "title"
        }, i18nClient.t("border_style", "边框样式"), "："), /*#__PURE__*/ React.createElement("div", {
            className: "style-wrapper"
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames("style-item dashed", {
                selected: borderStyle === "dashed"
            }),
            onClick: function() {
                return _this.setState({
                    borderStyle: "dashed"
                });
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames("style-item solid", {
                selected: borderStyle === "solid"
            }),
            onClick: function() {
                return _this.setState({
                    borderStyle: "solid"
                });
            }
        }))), /*#__PURE__*/ React.createElement("div", {
            className: "border-color"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "title"
        }, i18nClient.t("border_color", "边框颜色"), "："), /*#__PURE__*/ React.createElement(ColorPopover, {
            type: "table-border",
            tips: i18nClient.t("border_color", "边框颜色"),
            defaultColor: "#ccc",
            selectedColor: borderColor,
            setColor: function(color) {
                return _this.setState({
                    borderColor: color
                });
            }
        }))), /*#__PURE__*/ React.createElement("div", {
            className: "right"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "border-width"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "title"
        }, i18nClient.t("border_width", "边框宽度"), "："), /*#__PURE__*/ React.createElement("div", {
            className: "width-wrapper"
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames("width-item", {
                selected: borderWidth === 0
            }),
            onClick: function() {
                return _this.setState({
                    borderWidth: 0
                });
            }
        }, i18nClient.t("pk_no", "无")), [
            4,
            1,
            5,
            2,
            6,
            3,
            7
        ].map(function(_) {
            return /*#__PURE__*/ React.createElement("div", {
                key: _,
                className: classNames("width-item", {
                    selected: borderWidth === _
                }),
                onClick: function() {
                    return _this.setState({
                        borderWidth: _
                    });
                }
            }, /*#__PURE__*/ React.createElement("div", {
                className: "inner",
                style: {
                    backgroundColor: borderColor
                }
            }));
        }))))));
    };
    return TableModal;
}(Component);

var MenuItem = function(param) {
    var name = param.name, split = param.split, disabled = param.disabled, command = param.command;
    return /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-context-item")).concat(split ? " split" : "").concat(disabled ? " disabled" : ""),
        onClick: command
    }, name);
};

var css_248z$8 = ".ct-table-context-menu{position:fixed;background:#fff;box-shadow:0 2px 10px #ccc;color:#333;z-index:999}.ct-table-context-menu .ct-table-context-item{padding:8px 30px 8px 15px;min-width:100px;cursor:default;font-size:12px}.ct-table-context-menu .ct-table-context-item:hover{background:#f6f6f6}.ct-table-context-menu .ct-table-context-item:active{background:#e9e9e9}.ct-table-context-menu .ct-table-context-item.disabled{color:#999;cursor:not-allowed}.ct-table-context-menu .ct-table-context-item.split{border-bottom:1px solid #ddd}";
styleInject(css_248z$8);

function _arrayLikeToArray$6(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$5(arr) {
    if (Array.isArray(arr)) return arr;
}
function _defineProperty$a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterableToArrayLimit$5(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$5() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _objectSpread$9(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$a(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$6(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$6(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$6(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _slicedToArray$5(arr, i) {
    return _arrayWithHoles$5(arr) || _iterableToArrayLimit$5(arr, i) || _unsupportedIterableToArray$6(arr, i) || _nonIterableRest$5();
}
function _unsupportedIterableToArray$6(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$6(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$6(o, minLen);
}
// 硬编码 ContextMenu 宽高与页面底栏边距
var menuW = 130;
var bottomBarH = 60;
var paddingRight = 10;
var paddingBottom = 70;
var Menu = function(param) {
    var pos = param.pos, view = param.view, commands = param.commands;
    var calculatePos = function() {
        var top = "auto", right = "auto", bottom = "auto", left = "auto";
        var menuH = 370;
        var screenW = window.innerWidth;
        var screenH = window.innerHeight;
        if (pos.x + menuW > screenW) right = "".concat(paddingRight, "px");
        else left = "".concat(pos.x, "px");
        if (pos.y + menuH > screenH - bottomBarH) bottom = "".concat(paddingBottom, "px");
        else top = "".concat(pos.y, "px");
        return {
            top: top,
            right: right,
            bottom: bottom,
            left: left
        };
    };
    var _useState = _slicedToArray$5(useState({
        left: "auto",
        right: "auto",
        top: "auto",
        bottom: "auto"
    }), 2), menuPos = _useState[0], setMenuPos = _useState[1];
    var _useState1 = _slicedToArray$5(useState(true), 2), displayMenu = _useState1[0], setDisplayMenu = _useState1[1];
    var _useState2 = _slicedToArray$5(useState(false), 2), showModal = _useState2[0], setShowModal = _useState2[1];
    useEffect(function() {
        setMenuPos(calculatePos());
    }, [
        pos,
        view
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "".concat(classNamePrefix("table-context-menu")),
        style: _objectSpreadProps$6(_objectSpread$9({}, menuPos), {
            display: displayMenu ? "block" : "none"
        })
    }, /*#__PURE__*/ React.createElement(MenuItem, {
        name: "".concat(commands.hasRowHeader() ? i18nClient.t("cancel_th_row", "取消表头行") : i18nClient.t("set_th_row", "设置表头行")),
        command: function() {
            commands.toggleRowHeader();
            setDisplayMenu(false);
            view.focus();
        }
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: "".concat(commands.hasColHeader() ? i18nClient.t("cancel_th_line", "取消表头列") : i18nClient.t("set_th_line", "设置表头列")),
        command: function() {
            commands.toggleColHeader();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canToggleColHeader()
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: "".concat(commands.hasNumCol() ? i18nClient.t("cancel_line_num", "取消行号列") : i18nClient.t("set_line_num", "设置行号列")),
        command: function() {
            commands.toggleNumCol();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canToggleNumCol(),
        split: true
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("insert_row_above", "上方插入行"),
        command: function() {
            commands.addRowBefore();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canAddRowBefore()
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("insert_row_below", "下方插入行"),
        command: function() {
            commands.addRowAfter();
            setDisplayMenu(false);
            view.focus();
        }
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("insert_row", "删除行"),
        command: function() {
            commands.deleteRow();
            setDisplayMenu(false);
            view.focus();
        },
        split: true
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("insert_line_left", "左侧插入列"),
        command: function() {
            commands.addColumnBefore();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canAddColumnBefore()
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("insert_line_right", "右侧插入列"),
        command: function() {
            commands.addColumnAfter();
            setDisplayMenu(false);
            view.focus();
        }
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("delete_line", "删除列"),
        command: function() {
            commands.deleteColumn();
            setDisplayMenu(false);
            view.focus();
        },
        split: true
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("merge_cell", "合并单元格"),
        command: function() {
            commands.mergeCells();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canMergeCells()
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("split_cell", "拆分单元格"),
        command: function() {
            commands.splitCell();
            setDisplayMenu(false);
            view.focus();
        },
        disabled: !commands.canSplitCell(),
        split: true
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("table_attr", "表格属性"),
        command: function() {
            setShowModal(true);
            setDisplayMenu(false);
        },
        split: true
    }), /*#__PURE__*/ React.createElement(MenuItem, {
        name: i18nClient.t("delete_table", "删除表格"),
        command: function() {
            commands.deleteTable();
            setDisplayMenu(false);
            view.focus();
        }
    })), showModal && /*#__PURE__*/ React.createElement(TableModal, {
        hideModal: function() {
            return setShowModal(false);
        },
        setTableAttrs: commands.setTableAttrs,
        getTableAttrs: commands.getTableAttrs
    }));
};

function _classCallCheck$c(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var ContextMenuView = /*#__PURE__*/ function() {
    function ContextMenuView(editorView, commands, eventEmitter) {
        var _this = this;
        _classCallCheck$c(this, ContextMenuView);
        this.showMenu = function(pos) {
            render(null, _this.portal);
            render(/*#__PURE__*/ React.createElement(Menu, {
                view: _this.editorView,
                pos: pos,
                commands: _this.commands
            }), _this.portal);
        };
        this.hideMenu = function() {
            render(null, _this.portal);
        };
        this.commands = commands;
        this.editorView = editorView;
        this.editorView = editorView;
        this.addPortalLayoutNode();
        this.eventEmitter = eventEmitter;
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.on(SHOW_TABLE_MENU, this.showMenu);
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.on(HIDE_TABLE_MENU, this.hideMenu);
    }
    var _proto = ContextMenuView.prototype;
    _proto.addPortalLayoutNode = function addPortalLayoutNode() {
        this.portal = document.createElement("div");
        this.portal.className = classNamePrefix("table-context-menu-container");
        this.editorView.dom.parentNode.appendChild(this.portal);
    };
    _proto.destroy = function destroy() {
        var eventEmitter = this.eventEmitter;
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.off(SHOW_TABLE_MENU, this.showMenu);
        eventEmitter === null || eventEmitter === void 0 ? void 0 : eventEmitter.off(HIDE_TABLE_MENU, this.hideMenu);
    };
    return ContextMenuView;
}();

var css_248z$9 = ".ct-contextmenu-popover{display:none;position:fixed;background:#fff;box-shadow:0 2px 10px #ccc;color:#333;z-index:999;width:105px}";
styleInject(css_248z$9);

function contextMenu(commands, param) {
    var editorEventEmitter = param.editorEventEmitter;
    return new Plugin({
        key: tableContextMenuKey,
        props: {
            handleDOMEvents: {
                mousedown: function() {
                    editorEventEmitter.emit(HIDE_TABLE_MENU);
                    return false;
                },
                contextmenu: function(view, e) {
                    var schema = view.state.schema;
                    // 图片等宏不要弹出自定义的 contextmenu
                    var _schema_nodes = schema.nodes, image = _schema_nodes.image, minder = _schema_nodes.minder, drawio = _schema_nodes.drawio;
                    var ignoreList = [
                        image,
                        minder,
                        drawio
                    ].filter(Boolean);
                    // 用 event.clientX 不用 posAtDOM 是为了防止命中段末
                    var eventPos = view.posAtCoords({
                        left: e.clientX,
                        top: e.clientY
                    });
                    var $target = view.state.doc.resolve(eventPos.pos).nodeAfter;
                    var targetType = $target === null || $target === void 0 ? void 0 : $target.type;
                    var ignoreTarget = ignoreList.includes(targetType);
                    if (ignoreTarget) {
                        return false;
                    }
                    var dom = findParentDOM(e.target, function(n) {
                        return n.tagName.toUpperCase() === "TABLE";
                    });
                    if (dom) {
                        editorEventEmitter.emit(SHOW_TABLE_MENU, {
                            x: e.clientX,
                            y: e.clientY
                        });
                        e.preventDefault();
                        view.focus();
                    } else {
                        editorEventEmitter.emit(HIDE_TABLE_MENU);
                        return false;
                    }
                    return undefined;
                }
            }
        },
        view: function view(editorView) {
            return new ContextMenuView(editorView, commands, editorEventEmitter);
        }
    });
}

// Utilities used for copy/paste handling.
//
// This module handles pasting cell content into tables, or pasting
// anything into a cell selection, as replacing a block of cells with
// the content of the selection. When pasting cells into a cell, that
// involves placing the block of pasted content so that its top left
// aligns with the selection cell, optionally extending the table to
// the right or bottom to make sure it is large enough. Pasting into a
// cell selection is different, here the cells in the selection are
// clipped to the selection's rectangle, optionally repeating the
// pasted cells when they are smaller than the selection.
function _defineProperty$b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$a(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$b(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$7(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$7(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$7(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// Utilities to help with copying and pasting table cells
function fitSlice(nodeType, slice) {
    var node = nodeType.createAndFill();
    var tr = new Transform(node).replace(0, node.content.size, slice);
    return tr.doc;
}
// : (Schema, [Fragment]) → {width: number, height: number, rows: [Fragment]}
// Compute the width and height of a set of cells, and make sure each
// row has the same number of cells.
function ensureRectangular(schema, rows) {
    var table_cell = schema.nodes.table_cell;
    var widths = [];
    for(var i = 0; i < rows.length; i++){
        var row = rows[i];
        for(var j = row.childCount - 1; j >= 0; j--){
            var _row_child_attrs = row.child(j).attrs, rowspan = _row_child_attrs.rowspan, colspan = _row_child_attrs.colspan;
            for(var r = i; r < i + rowspan; r++){
                widths[r] = (widths[r] || 0) + colspan;
            }
        }
    }
    var width = 0;
    for(var r1 = 0; r1 < widths.length; r1++)width = Math.max(width, widths[r1]);
    for(var r2 = 0; r2 < widths.length; r2++){
        if (r2 >= rows.length) rows.push(Fragment.empty);
        if (widths[r2] < width) {
            var empty = table_cell.createAndFill();
            var cells = [];
            for(var i1 = widths[r2]; i1 < width; i1++)cells.push(empty);
            rows[r2] = rows[r2].append(Fragment.from(cells));
        }
    }
    return {
        height: rows.length,
        width: width,
        rows: rows
    };
}
// : (Slice) → ?{width: number, height: number, rows: [Fragment]}
// Get a rectangular area of cells from a slice, or null if the outer
// nodes of the slice aren't table cells or rows.
function pastedCells(slice) {
    if (!slice.size) return null;
    var content = slice.content, openStart = slice.openStart, openEnd = slice.openEnd;
    while(content.childCount === 1 && (openStart > 0 && openEnd > 0 || content.firstChild.type.spec.tableRole === "table")){
        openStart--;
        openEnd--;
        content = content.firstChild.content;
    }
    var first = content.firstChild;
    var role = first.type.spec.tableRole;
    var schema = first.type.schema;
    var table_row = schema.nodes.table_row;
    var rows = [];
    if (role === "row") {
        for(var i = 0; i < content.childCount; i++){
            var cells = content.child(i).content;
            var left = i ? 0 : Math.max(0, openStart - 1);
            var right = i < content.childCount - 1 ? 0 : Math.max(0, openEnd - 1);
            if (left || right) {
                cells = fitSlice(table_row, new Slice(cells, left, right)).content;
            }
            rows.push(cells);
        }
    } else if (role === "cell" || role === "header_cell") {
        rows.push(openStart || openEnd ? fitSlice(table_row, new Slice(content, openStart, openEnd)).content : content);
    } else {
        return null;
    }
    return ensureRectangular(schema, rows);
}
// : ({width: number, height: number, rows: [Fragment]}, number, number)
// → {width: number, height: number, rows: [Fragment]}
// Clip or extend (repeat) the given set of cells to cover the given
// width and height. Will clip rowspan/colspan cells at the edges when
// they stick out.
function clipCells(param, newWidth, newHeight) {
    var width = param.width, height = param.height, rows = param.rows;
    if (width !== newWidth) {
        var added = [];
        var newRows = [];
        for(var row = 0; row < rows.length; row++){
            var frag = rows[row];
            var cells = [];
            for(var col = added[row] || 0, i = 0; col < newWidth; i++){
                var cell = frag.child(i % frag.childCount);
                if (col + cell.attrs.colspan > newWidth) {
                    cell = cell.type.create(removeColSpan(cell.attrs, cell.attrs.colspan, col + cell.attrs.colspan - newWidth), cell.content);
                }
                cells.push(cell);
                col += cell.attrs.colspan;
                for(var j = 1; j < cell.attrs.rowspan; j++){
                    added[row + j] = (added[row + j] || 0) + cell.attrs.colspan;
                }
            }
            newRows.push(Fragment.from(cells));
        }
        rows = newRows;
        width = newWidth;
    }
    if (height !== newHeight) {
        var newRows1 = [];
        for(var row1 = 0, i1 = 0; row1 < newHeight; row1++, i1++){
            var cells1 = [];
            var source = rows[i1 % height];
            for(var j1 = 0; j1 < source.childCount; j1++){
                var cell1 = source.child(j1);
                if (row1 + cell1.attrs.rowspan > newHeight) {
                    cell1 = cell1.type.create(setAttr(cell1.attrs, "rowspan", Math.max(1, newHeight - cell1.attrs.rowspan)), cell1.content);
                }
                cells1.push(cell1);
            }
            newRows1.push(Fragment.from(cells1));
        }
        rows = newRows1;
        height = newHeight;
    }
    return {
        width: width,
        height: height,
        rows: rows
    };
}
function createEmptyRow(table, schema, map, width) {
    var cells = [];
    var _schema_nodes = schema.nodes, table_cell = _schema_nodes.table_cell, table_header = _schema_nodes.table_header, table_row = _schema_nodes.table_row;
    for(var i = 0, start = (map.height - 1) * map.width; i < Math.max(map.width, width); i++){
        var cell = void 0;
        if (i >= map.width) {
            cell = table_cell.createAndFill();
        } else {
            var mapCell = table.nodeAt(map.map[start + i]);
            var header = i >= map.width ? false : mapCell.type === table_header;
            cell = header ? table_header.createAndFill({
                numCell: mapCell.attrs.numCell
            }) : table_cell.createAndFill({
                numCell: mapCell.attrs.numCell
            });
        }
        cells.push(cell);
    }
    return table_row.create(null, Fragment.from(cells));
}
// Make sure a table has at least the given width and height. Return
// true if something was changed.
function growTable(tr, map, table, start, width, height, mapFrom) {
    var schema = tr.doc.type.schema;
    var _schema_nodes = schema.nodes, table_cell = _schema_nodes.table_cell, table_header = _schema_nodes.table_header;
    var inserted = false;
    if (width > map.width) {
        inserted = true;
        for(var row = 0, rowEnd = 0; row < map.height; row++){
            var rowNode = table.child(row);
            rowEnd += rowNode.nodeSize;
            var cells = [];
            for(var i = map.width; i < width; i++){
                var add = null;
                if (rowNode.lastChild === null || rowNode.lastChild.type === table_cell) {
                    add = table_cell.createAndFill();
                } else {
                    add = table_header.createAndFill();
                }
                cells.push(add);
            }
            tr.insert(tr.mapping.slice(mapFrom).map(rowEnd - 1 + start), cells);
        }
    }
    if (height > map.height) {
        inserted = true;
        var rows = [];
        for(var i1 = map.height; i1 < height; i1++){
            rows.push(createEmptyRow(table, schema, map, width));
        }
        tr.insert(tr.mapping.slice(mapFrom).map(start + table.nodeSize - 2), rows);
    }
    return inserted;
}
// Make sure the given line (left, top) to (right, top) doesn't cross
// any rowspan cells by splitting cells that cross it. Return true if
// something changed.
function isolateHorizontal(tr, map, table, start, left, right, top, mapFrom) {
    if (top === 0 || top === map.height) return false;
    var found = false;
    for(var col = left; col < right; col++){
        var index = top * map.width + col;
        var pos = map.map[index];
        if (map.map[index - map.width] === pos) {
            found = true;
            var cell = table.nodeAt(pos);
            var _map_findCell = map.findCell(pos), cellTop = _map_findCell.top, cellLeft = _map_findCell.left;
            tr.setNodeMarkup(tr.mapping.slice(mapFrom).map(pos + start), null, setAttr(cell.attrs, "rowspan", top - cellTop));
            tr.insert(tr.mapping.slice(mapFrom).map(map.positionAt(top, cellLeft, table)) + start, cell.type.createAndFill(setAttr(cell.attrs, "rowspan", cellTop + cell.attrs.rowspan - top)));
            col += cell.attrs.colspan - 1;
        }
    }
    return found;
}
// Make sure the given line (left, top) to (left, bottom) doesn't
// cross any colspan cells by splitting cells that cross it. Return
// true if something changed.
function isolateVertical(tr, map, table, start, top, bottom, left, mapFrom) {
    if (left === 0 || left === map.width) return false;
    var found = false;
    for(var row = top; row < bottom; row++){
        var index = row * map.width + left;
        var pos = map.map[index];
        if (map.map[index - 1] === pos) {
            found = true;
            var cell = table.nodeAt(pos);
            var cellLeft = map.colCount(pos);
            var updatePos = tr.mapping.slice(mapFrom).map(pos + start);
            tr.setNodeMarkup(updatePos, null, removeColSpan(cell.attrs, left - cellLeft, cell.attrs.colspan - (left - cellLeft)));
            tr.insert(updatePos + cell.nodeSize, cell.type.createAndFill(removeColSpan(cell.attrs, 0, left - cellLeft)));
            row += cell.attrs.rowspan - 1;
        }
    }
    return found;
}
// 解析每个Node的行号列号
function parseRowsSlice(rows, w, h) {
    var map = [];
    for(var i = 0, e = w * h; i < e; i++)map[i] = null;
    for(var i1 = 0; i1 < rows.length; i1++){
        var slice = rows[i1];
        var sliceIndex = 0;
        for(var j = 0; j < w; j++){
            if (map[i1 * w + j]) continue;
            var node = slice.content[sliceIndex];
            for(var ii = 0; ii < node.attrs.rowspan; ii++){
                for(var jj = 0; jj < node.attrs.colspan; jj++){
                    map[(i1 + ii) * w + j + jj] = {
                        i: i1,
                        j: sliceIndex
                    };
                }
            }
            sliceIndex += 1;
        }
    }
    var res = [];
    for(var i2 = 0; i2 < rows.length; i2++)res[i2] = [];
    map.forEach(function(val, idx) {
        if (!res[val.i][val.j]) {
            var row = idx ? Math.floor(idx / w) : 0;
            var col = idx - row * w;
            res[val.i][val.j] = {
                col: col,
                row: row
            };
        }
    });
    return res;
}
// Insert the given set of cells (as returned by `pastedCells`) into a
// table, at the position pointed at by rect.
function insertCells(state, dispatch, tableStart, rect, cells) {
    var _loop = function(row) {
        var slice = cells.rows[row - top];
        var hasNumCell = false;
        // 保留原来的 cellType, diffId，行号
        for(var i = 0; i < slice.content.length; i++){
            var _doc_nodeAt, _doc_nodeAt1, _doc_nodeAt2;
            var node = slice.content[i];
            var _parseRes__i = parseRes[row - top][i], colIdx = _parseRes__i.col, rowIdx = _parseRes__i.row;
            var pos = map.positionAt(rowIdx + top, colIdx + left, table) + tableStart;
            var cellType = ((_doc_nodeAt = doc.nodeAt(pos)) === null || _doc_nodeAt === void 0 ? void 0 : _doc_nodeAt.type) || table_cell;
            var numCell = ((_doc_nodeAt1 = doc.nodeAt(pos)) === null || _doc_nodeAt1 === void 0 ? void 0 : _doc_nodeAt1.attrs.numCell) || false;
            if (!hasNumCell && numCell) {
                hasNumCell = numCell;
            }
            var dataCellDiffId = ((_doc_nodeAt2 = doc.nodeAt(pos)) === null || _doc_nodeAt2 === void 0 ? void 0 : _doc_nodeAt2.attrs.dataCellDiffId) || "ct-cell-diff-id-".concat(v4());
            slice.content[i] = cellType.create(_objectSpreadProps$7(_objectSpread$a({}, node.attrs), {
                dataCellDiffId: dataCellDiffId,
                numCell: numCell
            }), node.content);
        }
        var newNodes = [];
        if (hasNumCell) {
            slice.forEach(function(node, _offset, index) {
                if (index > 0) {
                    newNodes.push(node);
                }
            });
        }
        var from = map.positionAt(row, left + (hasNumCell ? 1 : 0), table);
        var to = map.positionAt(row, right, table);
        tr.replace(tr.mapping.slice(mapFrom).map(from + tableStart), tr.mapping.slice(mapFrom).map(to + tableStart), new Slice(hasNumCell ? Fragment.fromArray(newNodes) : slice, 0, 0));
    };
    var recomp = function recomp() {
        table = tableStart ? tr.doc.nodeAt(tableStart - 1) : tr.doc;
        map = TableMap.get(table);
        mapFrom = tr.mapping.maps.length;
    };
    var table = tableStart ? state.doc.nodeAt(tableStart - 1) : state.doc;
    var map = TableMap.get(table);
    var top = rect.top, left = rect.left;
    var right = left + cells.width;
    var bottom = top + cells.height;
    var tr = state.tr, schema = state.schema;
    var table_cell = schema.nodes.table_cell;
    var mapFrom = 0;
    // Prepare the table to be large enough and not have any cells
    // crossing the boundaries of the rectangle that we want to
    // insert into. If anything about it changes, recompute the table
    // map so that subsequent operations can see the current shape.
    if (growTable(tr, map, table, tableStart, right, bottom, mapFrom)) {
        recomp();
    }
    if (isolateHorizontal(tr, map, table, tableStart, left, right, top, mapFrom)) {
        recomp();
    }
    if (isolateHorizontal(tr, map, table, tableStart, left, right, bottom, mapFrom)) {
        recomp();
    }
    if (isolateVertical(tr, map, table, tableStart, top, bottom, left, mapFrom)) {
        recomp();
    }
    if (isolateVertical(tr, map, table, tableStart, top, bottom, right, mapFrom)) {
        recomp();
    }
    var doc = tr.doc;
    var parseRes = parseRowsSlice(cells.rows, right - left, bottom - top);
    var selPos = map.positionAt(top, left, table) + tableStart;
    for(var row = top; row < bottom; row++)_loop(row);
    recomp();
    tr.setSelection(new TextSelection(tr.doc.resolve(selPos + 1))); // +1之前，位置在<cell>前
    dispatch(tr);
}
// 与 insertCells 逻辑一样，只是返回 tr
function insertCellsTr(state, tr, tableStart, rect, cells) {
    var _loop = function(row) {
        var slice = cells.rows[row - top];
        var hasNumCell = false;
        // 保留原来的 cellType, diffId，行号
        for(var i = 0; i < slice.content.length; i++){
            var _doc_nodeAt, _doc_nodeAt1, _doc_nodeAt2;
            var node = slice.content[i];
            var _parseRes__i = parseRes[row - top][i], colIdx = _parseRes__i.col, rowIdx = _parseRes__i.row;
            var pos = map.positionAt(rowIdx + top, colIdx + left, table) + tableStart;
            var cellType = ((_doc_nodeAt = doc.nodeAt(pos)) === null || _doc_nodeAt === void 0 ? void 0 : _doc_nodeAt.type) || table_cell;
            var numCell = ((_doc_nodeAt1 = doc.nodeAt(pos)) === null || _doc_nodeAt1 === void 0 ? void 0 : _doc_nodeAt1.attrs.numCell) || false;
            if (!hasNumCell && numCell) {
                hasNumCell = numCell;
            }
            var dataCellDiffId = ((_doc_nodeAt2 = doc.nodeAt(pos)) === null || _doc_nodeAt2 === void 0 ? void 0 : _doc_nodeAt2.attrs.dataCellDiffId) || "ct-cell-diff-id-".concat(v4());
            slice.content[i] = cellType.create(_objectSpreadProps$7(_objectSpread$a({}, node.attrs), {
                dataCellDiffId: dataCellDiffId,
                numCell: numCell
            }), node.content);
        }
        var newNodes = [];
        if (hasNumCell) {
            slice.forEach(function(node, _offset, index) {
                if (index > 0) {
                    newNodes.push(node);
                }
            });
        }
        var from = map.positionAt(row, left + (hasNumCell ? 1 : 0), table);
        var to = map.positionAt(row, right, table);
        tr.replace(tr.mapping.slice(mapFrom).map(from + tableStart), tr.mapping.slice(mapFrom).map(to + tableStart), new Slice(hasNumCell ? Fragment.fromArray(newNodes) : slice, 0, 0));
    };
    var recomp = function recomp() {
        table = tableStart ? tr.doc.nodeAt(tableStart - 1) : tr.doc;
        map = TableMap.get(table);
        mapFrom = tr.mapping.maps.length;
    };
    var table = tableStart ? state.doc.nodeAt(tableStart - 1) : state.doc;
    var map = TableMap.get(table);
    var top = rect.top, left = rect.left;
    var right = left + cells.width;
    var bottom = top + cells.height;
    var schema = state.schema;
    var table_cell = schema.nodes.table_cell;
    var mapFrom = 0;
    // Prepare the table to be large enough and not have any cells
    // crossing the boundaries of the rectangle that we want to
    // insert into. If anything about it changes, recompute the table
    // map so that subsequent operations can see the current shape.
    if (growTable(tr, map, table, tableStart, right, bottom, mapFrom)) {
        recomp();
    }
    if (isolateHorizontal(tr, map, table, tableStart, left, right, top, mapFrom)) {
        recomp();
    }
    if (isolateHorizontal(tr, map, table, tableStart, left, right, bottom, mapFrom)) {
        recomp();
    }
    if (isolateVertical(tr, map, table, tableStart, top, bottom, left, mapFrom)) {
        recomp();
    }
    if (isolateVertical(tr, map, table, tableStart, top, bottom, right, mapFrom)) {
        recomp();
    }
    var doc = tr.doc;
    var parseRes = parseRowsSlice(cells.rows, right - left, bottom - top);
    var selPos = map.positionAt(top, left, table) + tableStart;
    for(var row = top; row < bottom; row++)_loop(row);
    recomp();
    tr.setSelection(new TextSelection(tr.doc.resolve(selPos + 1))); // +1之前，位置在<cell>前
    return tr;
}

var css_248z$a = '.table-container{position:relative}.table-container>.mounted-container{height:100%}.table-container>.mounted-container>.ct-table-all-selectbar-layout>div{position:absolute;top:-18px;left:-18px}.table-container>.mounted-container>.ct-table-col-selectbar-layout>div{position:absolute;top:-45px;left:1px}.table-container>.mounted-container>.ct-table-row-selectbar-layout>div{position:absolute;top:0;left:-26px}.ct-table-wrapper{display:block;overflow-x:auto;overflow-y:hidden;margin:24px 0 8px;padding-bottom:1px;padding-right:1px;counter-reset:cellnumber}.ct-table-wrapper table,.ct-table-wrapper tbody,.ct-table-wrapper td,.ct-table-wrapper th{background-clip:padding-box!important}.ct-table-wrapper table{width:auto;border-collapse:collapse;table-layout:fixed;word-break:break-all;position:relative;overflow:auto;margin:0}.ct-table-wrapper td,.ct-table-wrapper th{box-sizing:border-box;width:auto;min-width:-webkit-fit-content;min-width:-moz-fit-content;min-width:fit-content;padding:1px 8px;border:1px solid #ddd;vertical-align:top;background-color:#fff;outline:none}.ct-table-wrapper td.overflow,.ct-table-wrapper td>ol,.ct-table-wrapper td>ul,.ct-table-wrapper th.overflow,.ct-table-wrapper th>ol,.ct-table-wrapper th>ul{overflow:hidden}.ct-table-wrapper th{text-align:left;background-color:#f6f6f6;overflow:hidden}.ct-table-wrapper td{overflow:hidden}.ct-table-wrapper table td>p>span.ct-node-view-dom,.ct-table-wrapper table th>p>span.ct-node-view-dom{margin:0 0 0 -3px}.ct-table-wrapper .num-cell{counter-increment:cellnumber;vertical-align:top;text-align:center;color:#000;padding:8px 0;font-size:12px;-webkit-user-select:none!important;-moz-user-select:none!important;-ms-user-select:none!important;user-select:none!important}.ct-table-wrapper .num-cell:before{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;pointer-events:none;content:counter(cellnumber)}.ct-table-wrapper .num-cell p{display:none}.ct-table-wrapper h1,.ct-table-wrapper h2,.ct-table-wrapper h3,.ct-table-wrapper h4,.ct-table-wrapper h5,.ct-table-wrapper h6,.ct-table-wrapper p{min-width:0;margin-top:8px}.ct-table-wrapper ol,.ct-table-wrapper ul{padding-left:34px}.ct-table-wrapper .ct-node-view-dom .ct-image{font-size:1;min-width:20px;max-width:100%}.ProseMirror .column-resize-handler{position:absolute;top:0;bottom:-1px;width:2px;right:0;z-index:15;cursor:col-resize!important;background-color:#adf;pointer-events:inherit!important}.column-resize-handler.ProseMirror-widget{background:#3d91f2}table .selectedCell{position:relative}table .selectedCell:after{z-index:2;position:absolute;content:"";left:0;right:0;top:0;bottom:0;background-color:rgba(65,194,245,.2);pointer-events:none}table .selectedCell.top:after{border-top:2px solid #1c6cdc}table .selectedCell.bottom:after{border-bottom:2px solid #1c6cdc}table .selectedCell.left:after{border-left:2px solid #1c6cdc}table .selectedCell.right:after{border-right:2px solid #1c6cdc}table[data-borderstyle=dashed]>tbody>tr>td,table[data-borderstyle=dashed]>tbody>tr>th{border-style:dashed}table[data-borderwidth="0"]>tbody>tr>td,table[data-borderwidth="0"]>tbody>tr>th{border:none}table[data-borderwidth="1"] tr:first-child:after{content:""}table[data-borderwidth="7"]>tbody>tr>td,table[data-borderwidth="7"]>tbody>tr>th{border-width:7px}table[data-borderwidth="7"]>tbody>tr .column-resize-handler{width:8px}table[data-borderwidth="7"]>tbody>tr .selectedCell:after{left:6px;right:6px;top:6px;bottom:6px;border-width:8px}table[data-borderwidth="6"]>tbody>tr>td,table[data-borderwidth="6"]>tbody>tr>th{border-width:6px}table[data-borderwidth="6"]>tbody>tr .column-resize-handler{width:7px}table[data-borderwidth="6"]>tbody>tr .selectedCell:after{left:5px;right:5px;top:5px;bottom:5px;border-width:7px}table[data-borderwidth="5"]>tbody>tr>td,table[data-borderwidth="5"]>tbody>tr>th{border-width:5px}table[data-borderwidth="5"]>tbody>tr .column-resize-handler{width:6px}table[data-borderwidth="5"]>tbody>tr .selectedCell:after{left:4px;right:4px;top:4px;bottom:4px;border-width:6px}table[data-borderwidth="4"]>tbody>tr>td,table[data-borderwidth="4"]>tbody>tr>th{border-width:4px}table[data-borderwidth="4"]>tbody>tr .column-resize-handler{width:5px}table[data-borderwidth="4"]>tbody>tr .selectedCell:after{left:3px;right:3px;top:3px;bottom:3px;border-width:5px}table[data-borderwidth="3"]>tbody>tr>td,table[data-borderwidth="3"]>tbody>tr>th{border-width:3px}table[data-borderwidth="3"]>tbody>tr .column-resize-handler{width:4px}table[data-borderwidth="3"]>tbody>tr .selectedCell:after{left:2px;right:2px;top:2px;bottom:2px;border-width:4px}table[data-borderwidth="2"]>tbody>tr>td,table[data-borderwidth="2"]>tbody>tr>th{border-width:2px}table[data-borderwidth="2"]>tbody>tr .column-resize-handler{width:3px}table[data-borderwidth="2"]>tbody>tr .selectedCell:after{left:1px;right:1px;top:1px;bottom:1px;border-width:3px}table[data-borderwidth="1"]>tbody>tr>td,table[data-borderwidth="1"]>tbody>tr>th{border-width:1px}table[data-borderwidth="1"]>tbody>tr .column-resize-handler{width:2px}table[data-borderwidth="1"]>tbody>tr .selectedCell:after{left:0;right:0;top:0;bottom:0;border-width:2px}table[data-bordercolor="#fff"]>tbody>tr>td,table[data-bordercolor="#fff"]>tbody>tr>th{border-color:#fff}table[data-bordercolor="#333"]>tbody>tr>td,table[data-bordercolor="#333"]>tbody>tr>th{border-color:#333}table[data-bordercolor="#f5222d"]>tbody>tr>td,table[data-bordercolor="#f5222d"]>tbody>tr>th{border-color:#f5222d}table[data-bordercolor="#fa541c"]>tbody>tr>td,table[data-bordercolor="#fa541c"]>tbody>tr>th{border-color:#fa541c}table[data-bordercolor="#faad14"]>tbody>tr>td,table[data-bordercolor="#faad14"]>tbody>tr>th{border-color:#faad14}table[data-bordercolor="#fadb14"]>tbody>tr>td,table[data-bordercolor="#fadb14"]>tbody>tr>th{border-color:#fadb14}table[data-bordercolor="#52c41a"]>tbody>tr>td,table[data-bordercolor="#52c41a"]>tbody>tr>th{border-color:#52c41a}table[data-bordercolor="#13c2c2"]>tbody>tr>td,table[data-bordercolor="#13c2c2"]>tbody>tr>th{border-color:#13c2c2}table[data-bordercolor="#1890ff"]>tbody>tr>td,table[data-bordercolor="#1890ff"]>tbody>tr>th{border-color:#1890ff}table[data-bordercolor="#2f54eb"]>tbody>tr>td,table[data-bordercolor="#2f54eb"]>tbody>tr>th{border-color:#2f54eb}table[data-bordercolor="#722ed1"]>tbody>tr>td,table[data-bordercolor="#722ed1"]>tbody>tr>th{border-color:#722ed1}table[data-bordercolor="#eb2f96"]>tbody>tr>td,table[data-bordercolor="#eb2f96"]>tbody>tr>th{border-color:#eb2f96}table[data-bordercolor="#f6f6f6"]>tbody>tr>td,table[data-bordercolor="#f6f6f6"]>tbody>tr>th{border-color:#f6f6f6}table[data-bordercolor="#989898"]>tbody>tr>td,table[data-bordercolor="#989898"]>tbody>tr>th{border-color:#989898}table[data-bordercolor="#ffe8e7"]>tbody>tr>td,table[data-bordercolor="#ffe8e7"]>tbody>tr>th{border-color:#ffe8e7}table[data-bordercolor="#ffece0"]>tbody>tr>td,table[data-bordercolor="#ffece0"]>tbody>tr>th{border-color:#ffece0}table[data-bordercolor="#fff7cb"]>tbody>tr>td,table[data-bordercolor="#fff7cb"]>tbody>tr>th{border-color:#fff7cb}table[data-bordercolor="#fcfcca"]>tbody>tr>td,table[data-bordercolor="#fcfcca"]>tbody>tr>th{border-color:#fcfcca}table[data-bordercolor="#e5f7d3"]>tbody>tr>td,table[data-bordercolor="#e5f7d3"]>tbody>tr>th{border-color:#e5f7d3}table[data-bordercolor="#cffff7"]>tbody>tr>td,table[data-bordercolor="#cffff7"]>tbody>tr>th{border-color:#cffff7}table[data-bordercolor="#d4eefc"]>tbody>tr>td,table[data-bordercolor="#d4eefc"]>tbody>tr>th{border-color:#d4eefc}table[data-bordercolor="#dfe8fc"]>tbody>tr>td,table[data-bordercolor="#dfe8fc"]>tbody>tr>th{border-color:#dfe8fc}table[data-bordercolor="#efe1fa"]>tbody>tr>td,table[data-bordercolor="#efe1fa"]>tbody>tr>th{border-color:#efe1fa}table[data-bordercolor="#fbe1eb"]>tbody>tr>td,table[data-bordercolor="#fbe1eb"]>tbody>tr>th{border-color:#fbe1eb}table[data-bordercolor="#e9e9e9"]>tbody>tr>td,table[data-bordercolor="#e9e9e9"]>tbody>tr>th{border-color:#e9e9e9}table[data-bordercolor="#666"]>tbody>tr>td,table[data-bordercolor="#666"]>tbody>tr>th{border-color:#666}table[data-bordercolor="#ffa39e"]>tbody>tr>td,table[data-bordercolor="#ffa39e"]>tbody>tr>th{border-color:#ffa39e}table[data-bordercolor="#ffbb96"]>tbody>tr>td,table[data-bordercolor="#ffbb96"]>tbody>tr>th{border-color:#ffbb96}table[data-bordercolor="#ffe58f"]>tbody>tr>td,table[data-bordercolor="#ffe58f"]>tbody>tr>th{border-color:#ffe58f}table[data-bordercolor="#fffb8f"]>tbody>tr>td,table[data-bordercolor="#fffb8f"]>tbody>tr>th{border-color:#fffb8f}table[data-bordercolor="#b7eb8f"]>tbody>tr>td,table[data-bordercolor="#b7eb8f"]>tbody>tr>th{border-color:#b7eb8f}table[data-bordercolor="#87e8de"]>tbody>tr>td,table[data-bordercolor="#87e8de"]>tbody>tr>th{border-color:#87e8de}table[data-bordercolor="#91d5ff"]>tbody>tr>td,table[data-bordercolor="#91d5ff"]>tbody>tr>th{border-color:#91d5ff}table[data-bordercolor="#adc6ff"]>tbody>tr>td,table[data-bordercolor="#adc6ff"]>tbody>tr>th{border-color:#adc6ff}table[data-bordercolor="#d3adf7"]>tbody>tr>td,table[data-bordercolor="#d3adf7"]>tbody>tr>th{border-color:#d3adf7}table[data-bordercolor="#ffadd2"]>tbody>tr>td,table[data-bordercolor="#ffadd2"]>tbody>tr>th{border-color:#ffadd2}table[data-bordercolor="#ddd"]>tbody>tr>td,table[data-bordercolor="#ddd"]>tbody>tr>th{border-color:#ddd}table[data-bordercolor="#4b4b4b"]>tbody>tr>td,table[data-bordercolor="#4b4b4b"]>tbody>tr>th{border-color:#4b4b4b}table[data-bordercolor="#ff4d4f"]>tbody>tr>td,table[data-bordercolor="#ff4d4f"]>tbody>tr>th{border-color:#ff4d4f}table[data-bordercolor="#ff7a45"]>tbody>tr>td,table[data-bordercolor="#ff7a45"]>tbody>tr>th{border-color:#ff7a45}table[data-bordercolor="#ffc53d"]>tbody>tr>td,table[data-bordercolor="#ffc53d"]>tbody>tr>th{border-color:#ffc53d}table[data-bordercolor="#ffec3d"]>tbody>tr>td,table[data-bordercolor="#ffec3d"]>tbody>tr>th{border-color:#ffec3d}table[data-bordercolor="#73d13d"]>tbody>tr>td,table[data-bordercolor="#73d13d"]>tbody>tr>th{border-color:#73d13d}table[data-bordercolor="#36cfc9"]>tbody>tr>td,table[data-bordercolor="#36cfc9"]>tbody>tr>th{border-color:#36cfc9}table[data-bordercolor="#40a8ff"]>tbody>tr>td,table[data-bordercolor="#40a8ff"]>tbody>tr>th{border-color:#40a8ff}table[data-bordercolor="#597ef7"]>tbody>tr>td,table[data-bordercolor="#597ef7"]>tbody>tr>th{border-color:#597ef7}table[data-bordercolor="#9254de"]>tbody>tr>td,table[data-bordercolor="#9254de"]>tbody>tr>th{border-color:#9254de}table[data-bordercolor="#f759ab"]>tbody>tr>td,table[data-bordercolor="#f759ab"]>tbody>tr>th{border-color:#f759ab}table[data-bordercolor="#ccc"]>tbody>tr>td,table[data-bordercolor="#ccc"]>tbody>tr>th{border-color:#ccc}table[data-bordercolor="#262626"]>tbody>tr>td,table[data-bordercolor="#262626"]>tbody>tr>th{border-color:#262626}table[data-bordercolor="#cf1322"]>tbody>tr>td,table[data-bordercolor="#cf1322"]>tbody>tr>th{border-color:#cf1322}table[data-bordercolor="#d4380d"]>tbody>tr>td,table[data-bordercolor="#d4380d"]>tbody>tr>th{border-color:#d4380d}table[data-bordercolor="#d48806"]>tbody>tr>td,table[data-bordercolor="#d48806"]>tbody>tr>th{border-color:#d48806}table[data-bordercolor="#d4b106"]>tbody>tr>td,table[data-bordercolor="#d4b106"]>tbody>tr>th{border-color:#d4b106}table[data-bordercolor="#389e0d"]>tbody>tr>td,table[data-bordercolor="#389e0d"]>tbody>tr>th{border-color:#389e0d}table[data-bordercolor="#08979c"]>tbody>tr>td,table[data-bordercolor="#08979c"]>tbody>tr>th{border-color:#08979c}table[data-bordercolor="#096dd9"]>tbody>tr>td,table[data-bordercolor="#096dd9"]>tbody>tr>th{border-color:#096dd9}table[data-bordercolor="#1d39c4"]>tbody>tr>td,table[data-bordercolor="#1d39c4"]>tbody>tr>th{border-color:#1d39c4}table[data-bordercolor="#531dab"]>tbody>tr>td,table[data-bordercolor="#531dab"]>tbody>tr>th{border-color:#531dab}table[data-bordercolor="#c41d7f"]>tbody>tr>td,table[data-bordercolor="#c41d7f"]>tbody>tr>th{border-color:#c41d7f}table[data-bordercolor="#aaa"]>tbody>tr>td,table[data-bordercolor="#aaa"]>tbody>tr>th{border-color:#aaa}table[data-bordercolor="#000"]>tbody>tr>td,table[data-bordercolor="#000"]>tbody>tr>th{border-color:#000}table[data-bordercolor="#820014"]>tbody>tr>td,table[data-bordercolor="#820014"]>tbody>tr>th{border-color:#820014}table[data-bordercolor="#871400"]>tbody>tr>td,table[data-bordercolor="#871400"]>tbody>tr>th{border-color:#871400}table[data-bordercolor="#874d00"]>tbody>tr>td,table[data-bordercolor="#874d00"]>tbody>tr>th{border-color:#874d00}table[data-bordercolor="#876800"]>tbody>tr>td,table[data-bordercolor="#876800"]>tbody>tr>th{border-color:#876800}table[data-bordercolor="#135200"]>tbody>tr>td,table[data-bordercolor="#135200"]>tbody>tr>th{border-color:#135200}table[data-bordercolor="#00474f"]>tbody>tr>td,table[data-bordercolor="#00474f"]>tbody>tr>th{border-color:#00474f}table[data-bordercolor="#003a8c"]>tbody>tr>td,table[data-bordercolor="#003a8c"]>tbody>tr>th{border-color:#003a8c}table[data-bordercolor="#061178"]>tbody>tr>td,table[data-bordercolor="#061178"]>tbody>tr>th{border-color:#061178}table[data-bordercolor="#22075e"]>tbody>tr>td,table[data-bordercolor="#22075e"]>tbody>tr>th{border-color:#22075e}table[data-bordercolor="#780650"]>tbody>tr>td,table[data-bordercolor="#780650"]>tbody>tr>th{border-color:#780650}table.responsive{table-layout:auto}table.responsive>tbody>tr>td,table.responsive>tbody>tr>th{width:auto!important;min-width:52px}table.responsive>tbody>tr>td h1,table.responsive>tbody>tr>td h2,table.responsive>tbody>tr>td h3,table.responsive>tbody>tr>td h4,table.responsive>tbody>tr>td h5,table.responsive>tbody>tr>td h6,table.responsive>tbody>tr>td p,table.responsive>tbody>tr>th h1,table.responsive>tbody>tr>th h2,table.responsive>tbody>tr>th h3,table.responsive>tbody>tr>th h4,table.responsive>tbody>tr>th h5,table.responsive>tbody>tr>th h6,table.responsive>tbody>tr>th p{min-width:0;margin-top:8px;word-break:keep-all}.pm-body-wrapper .ct-table-wrapper{overflow-x:auto}.pm-body-wrapper .ct-table-wrapper table{position:static}.pm-body-wrapper .ct-table-wrapper .num-cell-view:before{content:""}.pm-body-wrapper .ct-table-wrapper .cloneHeader{display:none}.pm-body-wrapper .ct-table-wrapper .cloneHeader>th,.pm-body-wrapper .ct-table-wrapper .originHeader>th{position:relative}.pm-body-wrapper .ct-table-wrapper .cloneHeader>th:after,.pm-body-wrapper .ct-table-wrapper .originHeader>th:after{content:"";width:12px;height:12px;visibility:hidden;position:absolute;right:4px;top:50%;transform:translateY(-50%);background-image:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 standalone%3D%22no%22%3F%3E%3C!DOCTYPE svg PUBLIC %22-%2F%2FW3C%2F%2FDTD SVG 1.1%2F%2FEN%22 %22http%3A%2F%2Fwww.w3.org%2FGraphics%2FSVG%2F1.1%2FDTD%2Fsvg11.dtd%22%3E%3Csvg t%3D%221526442144261%22 class%3D%22icon%22 style%3D%22%22 viewBox%3D%220 0 1024 1024%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 p-id%3D%223424%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22 width%3D%2212%22 height%3D%2212%22%3E%3Cdefs%3E%3Cstyle type%3D%22text%2Fcss%22%3E%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath d%3D%22M228.437333 251.904L475.562667 15.36a51.2 51.2 0 0 1 72.874666 0l247.125334 236.544a52.906667 52.906667 0 0 1 11.178666 57.173333 51.626667 51.626667 0 0 1-47.616 32.256H264.874667a51.541333 51.541333 0 0 1-47.616-32.426666 52.906667 52.906667 0 0 1 11.178666-57.002667z m567.125334 520.192L548.437333 1008.64a51.2 51.2 0 0 1-72.874666 0L228.437333 772.096a52.906667 52.906667 0 0 1-11.178666-57.173333A51.626667 51.626667 0 0 1 264.874667 682.666667h494.250666c20.821333 0 39.594667 12.8 47.616 32.426666a52.906667 52.906667 0 0 1-11.178666 57.002667z%22 fill%3D%22%23B0B0B0%22 p-id%3D%223425%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E");background-color:inherit;background-repeat:no-repeat;background-position:50%;cursor:pointer}.pm-body-wrapper .ct-table-wrapper .cloneHeader:hover>th:after,.pm-body-wrapper .ct-table-wrapper .originHeader:hover>th:after{visibility:visible}.pm-body-wrapper .ct-table-wrapper .cloneHeader>th[data-reverse="-1"]:after,.pm-body-wrapper .ct-table-wrapper .originHeader>th[data-reverse="-1"]:after{background-image:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2212px%22 height%3D%2212px%22 viewBox%3D%220 0 12 12%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E5%8D%87%E5%BA%8F%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22%E7%AC%AC2%E7%89%88%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22web-icon%22 transform%3D%22translate(-357.000000%2C -148.000000)%22%3E            %3Cg id%3D%22%E9%99%8D%E5%BA%8F%22 transform%3D%22translate(357.000000%2C 148.000000)%22%3E                %3Cpath d%3D%22M2.67721068%2C2.95184803 L5.57308098%2C0.17984669 C5.68599316%2C0.0650592945 5.83999069%2C5.32907052e-14 5.99998184%2C0 C6.15997299%2C-5.21804822e-14 6.31339049%2C0.0644696332 6.42688271%2C0.17984669 L9.32309135%2C2.95217191 C9.43842032%2C3.06941622 9.5%2C3.22656095 9.5%2C3.38616261 C9.5%2C3.46537377 9.48491926%2C3.5451746 9.45408109%2C3.6212409 C9.36050318%2C3.85022603 9.14009241%2C4 8.89619049%2C4 L3.10411154%2C3.99967612 C2.86011295%2C3.99967612 2.63970218%2C3.85049182 2.54612427%2C3.62091703 C2.45254636%2C3.39075257 2.50387887%2C3.1279602 2.67721068%2C2.95184803 Z M9.32278932%2C9.04815197 L6.42691902%2C11.8201533 C6.31400684%2C11.9349407 6.16000931%2C12 6.00001816%2C12 C5.84002701%2C12 5.68660951%2C11.9355304 5.57311729%2C11.8201533 L2.67690865%2C9.04782809 C2.56157968%2C8.93058378 2.5%2C8.77343905 2.5%2C8.61383739 C2.5%2C8.53462623 2.51508074%2C8.4548254 2.54591891%2C8.3787591 C2.63949682%2C8.14977397 2.85990759%2C8 3.10380951%2C8 L8.89588846%2C8.00032388 C9.13988705%2C8.00032388 9.36029782%2C8.14950818 9.45387573%2C8.37908297 C9.54745364%2C8.60924743 9.49612113%2C8.8720398 9.32278932%2C9.04815197 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23B0B0B0%22%3E%3C%2Fpath%3E                %3Cg id%3D%22Group-3%22 transform%3D%22translate(2.500000%2C 0.000000)%22%3E                    %3Cpath d%3D%22M0.177210679%2C2.95184803 L3.07308098%2C0.17984669 C3.18599316%2C0.0650592945 3.33999069%2C5.32907052e-14 3.49998184%2C0 C3.65997299%2C-5.21804822e-14 3.81339049%2C0.0644696332 3.92688271%2C0.17984669 L6.82309135%2C2.95217191 C6.93842032%2C3.06941622 7%2C3.22656095 7%2C3.38616261 C7%2C3.46537377 6.98491926%2C3.5451746 6.95408109%2C3.6212409 C6.86050318%2C3.85022603 6.64009241%2C4 6.39619049%2C4 L0.604111544%2C3.99967612 C0.360112952%2C3.99967612 0.139702179%2C3.85049182 0.0461242714%2C3.62091703 C-0.0474536357%2C3.39075257 0.00387887328%2C3.1279602 0.177210679%2C2.95184803 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%236B6B6B%22%3E%3C%2Fpath%3E                    %3Cpath d%3D%22M6.82278932%2C9.04815197 L3.92691902%2C11.8201533 C3.81400684%2C11.9349407 3.66000931%2C12 3.50001816%2C12 C3.34002701%2C12 3.18660951%2C11.9355304 3.07311729%2C11.8201533 L0.176908647%2C9.04782809 C0.0615796765%2C8.93058378 -1.77635684e-15%2C8.77343905 8.8817842e-16%2C8.61383739 C2.13162821e-14%2C8.53462623 0.0150807371%2C8.4548254 0.045918911%2C8.3787591 C0.139496818%2C8.14977397 0.359907591%2C8 0.603809512%2C8 L6.39588846%2C8.00032388 C6.63988705%2C8.00032388 6.86029782%2C8.14950818 6.95387573%2C8.37908297 C7.04745364%2C8.60924743 6.99612113%2C8.8720398 6.82278932%2C9.04815197 Z%22 id%3D%22Fill-1%22 fill%3D%22%23AAAAAA%22%3E%3C%2Fpath%3E                %3C%2Fg%3E            %3C%2Fg%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E")}.pm-body-wrapper .ct-table-wrapper .cloneHeader>th[data-reverse="1"]:after,.pm-body-wrapper .ct-table-wrapper .originHeader>th[data-reverse="1"]:after{background-image:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2212px%22 height%3D%2212px%22 viewBox%3D%220 0 12 12%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E9%99%8D%E5%BA%8F%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22%E7%AC%AC2%E7%89%88%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22web-icon%22 transform%3D%22translate(-357.000000%2C -148.000000)%22%3E            %3Cg id%3D%22%E9%99%8D%E5%BA%8F%22 transform%3D%22translate(357.000000%2C 148.000000)%22%3E                %3Cpath d%3D%22M2.67721068%2C2.95184803 L5.57308098%2C0.17984669 C5.68599316%2C0.0650592945 5.83999069%2C5.32907052e-14 5.99998184%2C0 C6.15997299%2C-5.21804822e-14 6.31339049%2C0.0644696332 6.42688271%2C0.17984669 L9.32309135%2C2.95217191 C9.43842032%2C3.06941622 9.5%2C3.22656095 9.5%2C3.38616261 C9.5%2C3.46537377 9.48491926%2C3.5451746 9.45408109%2C3.6212409 C9.36050318%2C3.85022603 9.14009241%2C4 8.89619049%2C4 L3.10411154%2C3.99967612 C2.86011295%2C3.99967612 2.63970218%2C3.85049182 2.54612427%2C3.62091703 C2.45254636%2C3.39075257 2.50387887%2C3.1279602 2.67721068%2C2.95184803 Z M9.32278932%2C9.04815197 L6.42691902%2C11.8201533 C6.31400684%2C11.9349407 6.16000931%2C12 6.00001816%2C12 C5.84002701%2C12 5.68660951%2C11.9355304 5.57311729%2C11.8201533 L2.67690865%2C9.04782809 C2.56157968%2C8.93058378 2.5%2C8.77343905 2.5%2C8.61383739 C2.5%2C8.53462623 2.51508074%2C8.4548254 2.54591891%2C8.3787591 C2.63949682%2C8.14977397 2.85990759%2C8 3.10380951%2C8 L8.89588846%2C8.00032388 C9.13988705%2C8.00032388 9.36029782%2C8.14950818 9.45387573%2C8.37908297 C9.54745364%2C8.60924743 9.49612113%2C8.8720398 9.32278932%2C9.04815197 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23B0B0B0%22%3E%3C%2Fpath%3E                %3Cg id%3D%22Group-3%22 transform%3D%22translate(2.500000%2C 0.000000)%22%3E                    %3Cpath d%3D%22M0.177210679%2C2.95184803 L3.07308098%2C0.17984669 C3.18599316%2C0.0650592945 3.33999069%2C5.32907052e-14 3.49998184%2C0 C3.65997299%2C-5.21804822e-14 3.81339049%2C0.0644696332 3.92688271%2C0.17984669 L6.82309135%2C2.95217191 C6.93842032%2C3.06941622 7%2C3.22656095 7%2C3.38616261 C7%2C3.46537377 6.98491926%2C3.5451746 6.95408109%2C3.6212409 C6.86050318%2C3.85022603 6.64009241%2C4 6.39619049%2C4 L0.604111544%2C3.99967612 C0.360112952%2C3.99967612 0.139702179%2C3.85049182 0.0461242714%2C3.62091703 C-0.0474536357%2C3.39075257 0.00387887328%2C3.1279602 0.177210679%2C2.95184803 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23AAAAAA%22%3E%3C%2Fpath%3E                    %3Cpath d%3D%22M6.82278932%2C9.04815197 L3.92691902%2C11.8201533 C3.81400684%2C11.9349407 3.66000931%2C12 3.50001816%2C12 C3.34002701%2C12 3.18660951%2C11.9355304 3.07311729%2C11.8201533 L0.176908647%2C9.04782809 C0.0615796765%2C8.93058378 -1.77635684e-15%2C8.77343905 8.8817842e-16%2C8.61383739 C2.13162821e-14%2C8.53462623 0.0150807371%2C8.4548254 0.045918911%2C8.3787591 C0.139496818%2C8.14977397 0.359907591%2C8 0.603809512%2C8 L6.39588846%2C8.00032388 C6.63988705%2C8.00032388 6.86029782%2C8.14950818 6.95387573%2C8.37908297 C7.04745364%2C8.60924743 6.99612113%2C8.8720398 6.82278932%2C9.04815197 Z%22 id%3D%22Fill-1%22 fill%3D%22%236B6B6B%22%3E%3C%2Fpath%3E                %3C%2Fg%3E            %3C%2Fg%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E")}.pm-body-wrapper .ct-table-wrapper .tableHeadFixed{display:table-row;position:absolute;top:0;z-index:99;overflow:hidden}.pm-body-wrapper .ct-table-wrapper .tableHeadFixed td,.pm-body-wrapper .ct-table-wrapper .tableHeadFixed th{overflow:hidden}.pm-body-wrapper .ct-table-wrapper .float-scrollbar{display:none;height:17px;position:absolute;bottom:0;width:100%}.pm-body-wrapper .ct-table-wrapper .float-scroll-filling{border:1px solid #fff;opacity:.01}.pm-body-wrapper .ct-table-wrapper .float-scroll-container{overflow-x:auto;overflow-y:hidden;height:17px}.ProseMirror-selectednode.ct-table-wrapper{outline:none}.ProseMirror-hideselection{caret-color:transparent}';
styleInject(css_248z$a);

function _assertThisInitialized$6(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$d(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _getPrototypeOf$6(o) {
    _getPrototypeOf$6 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$6(o);
}
function _inherits$6(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$6(subClass, superClass);
}
function _instanceof$b(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _possibleConstructorReturn$6(self, call) {
    if (call && (_typeof$6(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$6(self);
}
function _setPrototypeOf$6(o, p) {
    _setPrototypeOf$6 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$6(o, p);
}
var _typeof$6 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$6() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$6(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$6();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$6(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$6(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$6(this, result);
    };
}
var TableExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits$6(TableExtension, NodeExtension);
    var _super = _createSuper$6(TableExtension);
    function TableExtension() {
        _classCallCheck$d(this, TableExtension);
        var _this;
        _this = _super.apply(this, arguments);
        _this.checkCommand = function(command, func) {
            return function(state, dispatch, view) {
                var provider = _assertThisInitialized$6(_this).provider;
                if ((provider === null || provider === void 0 ? void 0 : provider.checkCommand) && !provider.checkCommand(command, {
                    view: view
                })) {
                    return false;
                }
                return func && func(state, dispatch, view);
            };
        };
        return _this;
    }
    var _proto = TableExtension.prototype;
    _proto.plugin = function plugin(param) {
        var providers = param.providers, getView = param.getView, commands = param.commands, editorEventEmitter = param.editorEventEmitter;
        var provider = providers.get(this.name);
        this.provider = provider;
        return [
            columnResizing(provider, getView, {
                editorEventEmitter: editorEventEmitter
            }),
            tableEditing(provider, {
                editorEventEmitter: editorEventEmitter
            }),
            tableLayout(provider, {
                editorEventEmitter: editorEventEmitter
            }),
            stickyHeader(provider, {
                editorEventEmitter: editorEventEmitter
            }),
            scrollBar(provider),
            contextMenu(commands.table, {
                editorEventEmitter: editorEventEmitter
            })
        ];
    };
    _proto.keys = function keys() {
        return {
            Delete: convertCommand(handleDelete()),
            Backspace: convertCommand(handleBackspace()),
            Tab: convertCommand(handleTab(1)),
            "Shift-Tab": convertCommand(handleTab(-1)),
            "Alt-ArrowUp": convertCommand(this.checkCommand("addRowBefore", addRowBefore())),
            "Alt-ArrowDown": convertCommand(this.checkCommand("addRowAfter", addRowAfter())),
            "Alt-ArrowLeft": convertCommand(this.checkCommand("addColumnBefore", addColumnBefore())),
            "Alt-ArrowRight": convertCommand(this.checkCommand("addColumnAfter", addColumnAfter())),
            "Mod-Shift-h": convertCommand(setCellVerticalAlign("top")),
            "Mod-Shift-m": convertCommand(setCellVerticalAlign("middle")),
            "Mod-Shift-d": convertCommand(setCellVerticalAlign("bottom"))
        };
    };
    _proto.commands = function commands() {
        return {
            isInTable: function() {
                return isInTable$1;
            },
            canAddColumnBefore: function() {
                return canAddColumnBefore;
            },
            addColumnBefore: function() {
                return addColumnBefore();
            },
            addColumnAfter: function() {
                return addColumnAfter();
            },
            deleteColumn: function() {
                return deleteColumn();
            },
            canAddRowBefore: function() {
                return canAddRowBefore;
            },
            addRowBefore: function() {
                return addRowBefore();
            },
            addRowAfter: function() {
                return addRowAfter();
            },
            deleteRow: function() {
                return deleteRow();
            },
            canMergeCells: function() {
                return canMergeCells;
            },
            mergeCells: function() {
                return mergeCells();
            },
            canSplitCell: function() {
                return canSplitCell;
            },
            splitCell: function() {
                return splitCell();
            },
            deleteTable: function() {
                return deleteTable$1();
            },
            insertTable: function(args) {
                return insertTable(args);
            },
            hasRowHeader: function() {
                return function(state) {
                    if (!isInTable$1(state)) return false;
                    var table = selectedRect(state).table;
                    return hasRowHeader(table);
                };
            },
            toggleRowHeader: function() {
                return toggleRowHeader();
            },
            canToggleColHeader: function() {
                return canToggleColHeader;
            },
            hasColHeader: function() {
                return function(state) {
                    if (!isInTable$1(state)) return false;
                    var table = selectedRect(state).table;
                    return hasColHeader(table);
                };
            },
            toggleColHeader: function() {
                return toggleColHeader();
            },
            canToggleNumCol: function() {
                return canToggleNumCol;
            },
            hasNumCol: function() {
                return function(state) {
                    if (!isInTable$1(state)) return false;
                    var table = selectedRect(state).table;
                    return hasNumCol(table);
                };
            },
            toggleNumCol: function() {
                return toggleNumCol();
            },
            setCellBgColor: function(color) {
                return setCellBgColor(color);
            },
            isResponsive: function() {
                return isResponsive;
            },
            setResponsive: function() {
                return setResponsive();
            },
            setFixed: function() {
                return setFixed();
            },
            setCellVerticalAlign: function(align) {
                return setCellVerticalAlign(align);
            },
            setTableAttrs: function(attrs) {
                return setTableAttrs(attrs);
            },
            getTableAttrs: function() {
                return getTableAttrs();
            }
        };
    };
    _proto.inputRules = function inputRules(param) {
        var getView = param.getView;
        return [
            new InputRule(new RegExp(regexpTable), function(state, match, start, end) {
                var view = getView();
                if (!match || !(match === null || match === void 0 ? void 0 : match[1])) {
                    return false;
                }
                view.dispatch(view.state.tr.delete(start, end));
                var header = match[1].split("|").map(function(_) {
                    return _.trim();
                });
                insertTable({
                    rowCnt: 2,
                    colCnt: header.length,
                    headerData: header.map(function(i) {
                        return [
                            view.state.schema.nodes.paragraph.create(null, view.state.schema.text(i))
                        ];
                    }),
                    defaultColWidth: 120
                })(view.state, view.dispatch, view);
                // 返回空tr表示已经处理inputRule
                return view.state.tr;
            })
        ];
    };
    _proto.transformPastedHTML = function transformPastedHTML(html) {
        var tableTestReg = /<table[^>]*?data-pm-slice="/;
        var tablePMDataTestReg = /data-pm-slice="[^>]*?\[\]"/;
        if (html.includes('data-pm-slice="')) {
            if (!html.includes('data-pm-slice="none"')) {
                if (tableTestReg.test(html)) {
                    if (!tablePMDataTestReg.test(html)) {
                        html = html.replace(/<table[^>]*?data-pm-slice=.*?<td.*?>/g, "").replace(/<\/td><\/tr><\/tbody><\/table>$/g, "");
                    }
                }
            }
        }
        html = html.replace(/<tr[^>]*?>[^<]*?(<tr)/g, "$1") // 处理异常表格行
        .replace(/<col[^>]*?style="width: 0px;"[^<]*?>/g, "<col>"); // 自适应表格
        // 处理diffId
        var createDom = document.createElement("div");
        createDom.innerHTML = html;
        var cells = createDom.querySelectorAll("[data-cell-diff-id]");
        cells.forEach(function(h) {
            h.setAttribute("data-cell-diff-id", "ct-cell-diff-id-".concat(v4()));
        });
        var rows = createDom.querySelectorAll("[data-row-diff-id]");
        rows.forEach(function(h) {
            h.setAttribute("data-cell-diff-id", "ct-tr-diff-id-".concat(v4()));
        });
        return createDom.innerHTML;
    };
    _proto.transformPasted = function transformPasted(state, node) {
        // 处理从非学城复制来的表格，补上cell里的<p>
        var _state_schema_nodes = state.schema.nodes, table_cell = _state_schema_nodes.table_cell, table_header = _state_schema_nodes.table_header, paragraph = _state_schema_nodes.paragraph;
        if (node.type === table_cell || node.type === table_header) {
            if (node.content.childCount === 0) {
                return node.copy(Fragment.from([
                    paragraph.create()
                ]));
            }
        }
        return node;
    };
    _proto.handlePaste = function handlePaste(param) {
        var view = param.view, slice = param.slice;
        var _view_state = view.state, doc = _view_state.doc, _view_state_schema = _view_state.schema, table_cell = _view_state_schema.nodes.table_cell, _view_state_tr = _view_state.tr, sel = _view_state_tr.selection;
        if (!isInTable$1(view.state)) return false;
        if (!_instanceof$b(sel, CellSelection)) {
            if (!isEmptyCell(cellWrapping(sel.$head))) return false;
        }
        var cells = pastedCells(slice); // cells = null 说明复制内容不是一个单独的表格
        // 复制的是只包含一个单元格的单元格选区时，只相当于只复制单元格里的内容
        if (cells && cells.height === 1 && cells.width === 1) {
            slice.openStart = 3;
            slice.openEnd = 3;
            cells = null;
        }
        // 将复制内容填充到选区的每个单元格内
        if (_instanceof$b(sel, CellSelection) && !cells) {
            var tableNode = sel.$anchorCell.node(-1);
            var start = sel.$anchorCell.start(-1);
            var rect = TableMap.get(tableNode).rectBetween(sel.$anchorCell.pos - start, sel.$headCell.pos - start);
            cells = {
                width: 1,
                height: 1,
                rows: [
                    Fragment.from(fitSlice(table_cell, slice))
                ]
            };
            cells = clipCells(cells, rect.right - rect.left, rect.bottom - rect.top);
            insertCells(view.state, view.dispatch, start, rect, cells);
            return true;
        }
        if (cells) {
            var adjustedSel = sel;
            if (_instanceof$b(sel, CellSelection)) {
                sel.ranges.forEach(function(range) {
                    if (range.$from.pos < adjustedSel.from) {
                        adjustedSel = new Selection(range.$from, range.$to);
                    }
                });
            }
            var foundCell = findTableCell(adjustedSel);
            if (!foundCell) return false;
            var $cell = doc.resolve(foundCell.pos);
            var start1 = $cell.start(-1);
            insertCells(view.state, view.dispatch, start1, TableMap.get($cell.node(-1)).findCell($cell.pos - start1), cells);
            return true;
        }
        return false;
    };
    _proto.handleWashTransaction = function handleWashTransaction(param) {
        var view = param.view, slice = param.slice, tr = param.tr;
        var _view_state = view.state, _view_state_schema = _view_state.schema, table_cell = _view_state_schema.nodes.table_cell;
        var doc = tr.doc, sel = tr.selection;
        if (!isInTable$1(view.state)) return tr;
        if (!_instanceof$b(sel, CellSelection)) {
            if (!isEmptyCell(cellWrapping(sel.$head))) return tr;
        }
        var cells = pastedCells(slice); // cells = null 说明复制内容不是一个单独的表格
        // 复制的是只包含一个单元格的单元格选区时，只相当于只复制单元格里的内容
        if (cells && cells.height === 1 && cells.width === 1) {
            slice.openStart = 3;
            slice.openEnd = 3;
            cells = null;
        }
        // 将复制内容填充到选区的每个单元格内
        if (_instanceof$b(sel, CellSelection) && !cells) {
            var tableNode = sel.$anchorCell.node(-1);
            var start = sel.$anchorCell.start(-1);
            var rect = TableMap.get(tableNode).rectBetween(sel.$anchorCell.pos - start, sel.$headCell.pos - start);
            cells = {
                width: 1,
                height: 1,
                rows: [
                    Fragment.from(fitSlice(table_cell, slice))
                ]
            };
            cells = clipCells(cells, rect.right - rect.left, rect.bottom - rect.top);
            return insertCellsTr(view.state, tr, start, rect, cells);
        }
        if (cells) {
            var adjustedSel = sel;
            if (_instanceof$b(sel, CellSelection)) {
                sel.ranges.forEach(function(range) {
                    if (range.$from.pos < adjustedSel.from) {
                        adjustedSel = new Selection(range.$from, range.$to);
                    }
                });
            }
            var foundCell = findTableCell(adjustedSel);
            if (!foundCell) return tr;
            var $cell = doc.resolve(foundCell.pos);
            var start1 = $cell.start(-1);
            return insertCellsTr(view.state, tr, start1, TableMap.get($cell.node(-1)).findCell($cell.pos - start1), cells);
        }
        return tr;
    };
    _createClass(TableExtension, [
        {
            key: "name",
            get: function get() {
                return "table";
            }
        }
    ]);
    return TableExtension;
}(NodeExtension);

export { TableExtension };
//# sourceMappingURL=index.js.map
